// NomLoop — Full Meal Planning & Grocery Companion
// React JSX · Single-file app
//
// Client is a thin view layer. All data (meals, packages, prices) and all
// proprietary logic (pack rounding, scoring, substitution graph) live behind
// the /api/* endpoints. Do NOT reintroduce inline MEALS / PKG / buildShoppingList
// here — that would reintroduce the reverse-engineering surface we just closed.
import { useState, useMemo, useEffect, useRef, Fragment } from "react";
import * as api from "./api.js";
import { C } from "./src/theme.js";
import "./src/glass.css";
import { ALLERGENS, DIETS, CUISINES, DISLIKE_OPTIONS, CUISINE_DIET_NOTES, CATEGORY_ICONS, DAYS } from "./src/constants.js";
import { useToast } from "./src/hooks/useToast.js";
import { Chip } from "./src/components/Chip.jsx";
import { ProgressDots } from "./src/components/ProgressDots.jsx";
import { OnboardWrap } from "./src/components/OnboardWrap.jsx";
import { VideoReelPlayer } from "./src/components/VideoReelPlayer.jsx";
import { PantryReveal } from "./src/components/PantryReveal.jsx";
import { ListItemRow, leftoverState } from "./src/components/ListItemRow.jsx";
import { CookieBanner } from "./src/components/CookieBanner.jsx";
import { SettingsModal } from "./src/components/SettingsModal.jsx";
import {
  PeoplePicker,
  DietPicker,
  AllergyPicker,
  DislikesPicker,
  CuisinePicker,
  MealCountPicker,
  BudgetPicker,
} from "./src/components/preferences/Pickers.jsx";
import { mealName, ingredientName, retailerLabel, swapSuggestionText, getRecipeSourceDisplayName } from "./src/utils/displayNames.js";
import { ingredientIcon } from "./src/utils/ingredientIcon.js";
import { useCookieConsent } from "./src/hooks/useCookieConsent.js";
import { ImportSheet } from "./src/components/ImportSheet.jsx";
import mealImageMap from "./src/generated/mealImageMap.json";
import * as prefsStore from "./src/utils/prefsStore.js";
import BetaEntry from "./src/components/auth/BetaEntry.jsx";
import BetaCodeGate from "./src/components/auth/BetaCodeGate.jsx";
import TastingBanner from "./src/components/auth/TastingBanner.jsx";


const MEAL_REMOVE_UNDO_MS = 7000;

// Shown when the retailers API hasn't loaded (network failure, budget gate, etc.)
// so users always have a way to switch retailer. IDs match the server's RETAILER_IDS.
const FALLBACK_RETAILERS = [
  { id: "tesco",      name: "Tesco"       },
  { id: "sainsburys", name: "Sainsbury's" },
  { id: "waitrose",   name: "Waitrose"    },
  { id: "ms",         name: "M&S"         },
];
const BRAND_LOGO_SRC = "/icon.png";
const MEAL_IMAGE_ASSETS = import.meta.glob("./assets/meal-images/*.png", {
  eager: true,
  query: "?url",
  import: "default",
});

const isImportedId = id => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

function BrandLogo({ size = 52, radius = 14, style = {} }) {
  return (
    <img
      src={BRAND_LOGO_SRC}
      alt="NomLoop"
      width={size}
      height={size}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        objectFit: "cover",
        display: "block",
        boxShadow: "0 10px 28px rgba(0,0,0,.16)",
        ...style,
      }}
    />
  );
}

function mealImageUrl(mealId) {
  const imagePath = mealImageMap[mealId];
  if (!imagePath) return null;
  return MEAL_IMAGE_ASSETS[`./${imagePath}`] || null;
}

function withMealImage(meal) {
  if (!meal || meal.imageUrl) return meal;
  const imageUrl = mealImageUrl(meal.id);
  return imageUrl ? { ...meal, imageUrl } : meal;
}

function recipeSourceDisplayName(recipeOrMeal) {
  return getRecipeSourceDisplayName(
    recipeOrMeal?.source_url ?? recipeOrMeal?.sourceUrl ?? null,
    recipeOrMeal?.sourceDisplayName ?? recipeOrMeal?.source_domain ?? recipeOrMeal?.sourceDomain ?? null,
  );
}

function importedRecipeImageUrl(recipeOrMeal) {
  return recipeOrMeal?.source_image_url ?? null;
}

const CUISINE_FLAGS = {
  french: "🇫🇷",
  italian: "🇮🇹",
  chinese: "🇨🇳",
  japanese: "🇯🇵",
  cambodian: "🇰🇭",
  thai: "🇹🇭",
  vietnamese: "🇻🇳",
  korean: "🇰🇷",
  indian: "🇮🇳",
  mexican: "🇲🇽",
  spanish: "🇪🇸",
  greek: "🇬🇷",
  middle_eastern: "🇱🇧",
  american: "🇺🇸",
  british: "🇬🇧",
};

function cuisineFlag(cuisine) {
  return CUISINE_FLAGS[cuisine] || "🌍";
}

function cuisineLabel(cuisine) {
  return CUISINES.find(c => c.id === cuisine)?.label?.replace("-inspired", "") || "Imported";
}

const CUISINE_TINTS = {
  french:         { bg:"#EEF0F8", color:"#2C3E7A", accent:"#BBC4DF" },
  italian:        { bg:"#FEF0EE", color:"#A8321E", accent:"#E0B0AA" },
  chinese:        { bg:"#FEF2EC", color:"#A03020", accent:"#E0C0A8" },
  japanese:       { bg:"#FDF0F2", color:"#982030", accent:"#E0B0B8" },
  cambodian:      { bg:"#F0F8EC", color:"#286818", accent:"#A8D4A0" },
  thai:           { bg:"#F0F8EA", color:"#286014", accent:"#A4D09C" },
  vietnamese:     { bg:"#EEF8F4", color:"#1A6840", accent:"#A0D0B8" },
  korean:         { bg:"#FEF0F0", color:"#941828", accent:"#E0B0B4" },
  indian:         { bg:"#FEF5E7", color:"#B05C0A", accent:"#E4C898" },
  mexican:        { bg:"#F0F7EE", color:"#286828", accent:"#A8D4A4" },
  spanish:        { bg:"#FEF2EA", color:"#A04010", accent:"#E0C0A0" },
  greek:          { bg:"#EEF4F8", color:"#1C4E7E", accent:"#A4C4D8" },
  middle_eastern: { bg:"#F5F2EE", color:"#785028", accent:"#CEC0A8" },
  american:       { bg:"#EEF1F8", color:"#2A4888", accent:"#B4C0D8" },
  british:        { bg:"#EEF5EE", color:"#285838", accent:"#A4C8B0" },
};
function cuisineTint(cuisine) {
  return CUISINE_TINTS[cuisine] || { bg:"#F0F0EE", color:"#666660", accent:"#CCCCC8" };
}

function FastTitleTooltip({ label, children }) {
  const anchorRef = useRef(null);
  const timerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const show = (delayMs = 220) => {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      const rect = anchorRef.current?.getBoundingClientRect?.();
      if (!rect) return;
      setTooltip({
        left: rect.left + rect.width / 2,
        top: rect.bottom + 6,
      });
    }, delayMs);
  };

  const hide = () => {
    clearTimer();
    setTooltip(null);
  };

  useEffect(() => () => clearTimer(), []);

  return (
    <span
      ref={anchorRef}
      onMouseEnter={() => show(220)}
      onMouseLeave={hide}
      onFocus={() => show(80)}
      onBlur={hide}
      style={{ display:"inline-flex", minWidth:0, maxWidth:"100%" }}
    >
      {children}
      {tooltip && (
        <span
          role="tooltip"
          style={{
            position:"fixed",
            left:tooltip.left,
            top:tooltip.top,
            transform:"translateX(-50%)",
            zIndex:10000,
            maxWidth:280,
            padding:"6px 10px",
            borderRadius:4,
            background:"rgba(52,52,52,.96)",
            color:C.white,
            fontSize:13,
            fontWeight:700,
            lineHeight:1.25,
            whiteSpace:"normal",
            pointerEvents:"none",
            boxShadow:"0 3px 10px rgba(0,0,0,.18)",
          }}
        >
          {label}
        </span>
      )}
    </span>
  );
}

// Measurement-unit picker — premium glass dropdown shared by the catalog
// and imported-recipe detail modals. Display-only: it just drives the shared
// measureMode string; conversion math + canonical data live elsewhere.
const MEASURE_OPTS = [["original", "Original"], ["imperial", "Imperial"], ["metric", "Metric"]];

function MeasureModePicker({ mode, onChange }) {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  const currentLabel = (MEASURE_OPTS.find(([v]) => v === mode) || MEASURE_OPTS[0])[1];

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect?.();
    if (!r) return;
    // Right-anchored so the menu hugs the heading edge inside the sheet.
    setPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
  };

  const close = () => {
    setClosing(true);
    window.setTimeout(() => { setOpen(false); setClosing(false); }, 120);
  };

  const toggle = () => {
    if (open) { close(); return; }
    place();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      close();
    };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    const reposition = () => place();
    window.addEventListener("mousedown", onDown);
    window.addEventListener("touchstart", onDown, { passive: true });
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("touchstart", onDown);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="measure-trigger glass-control"
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Measurement units: ${currentLabel}`}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "5px 9px 5px 12px", borderRadius: 99,
          border: `1px solid ${C.border}`, cursor: "pointer",
          fontSize: 11, fontWeight: 700, color: C.charcoal, lineHeight: 1,
          boxShadow: open ? C.sh : "none",
        }}
      >
        <span>{currentLabel}</span>
        <svg
          width="9" height="9" viewBox="0 0 10 10" aria-hidden="true"
          style={{ transition: "transform .2s cubic-bezier(.22,1,.36,1)", transform: open ? "rotate(180deg)" : "none" }}
        >
          <path d="M1 3l4 4 4-4" fill="none" stroke={C.grey} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && pos && (
        <div
          ref={popRef}
          role="listbox"
          aria-label="Measurement units"
          className={`glass-sheet ${closing ? "measure-pop-closing" : "measure-pop"}`}
          style={{
            position: "fixed", top: pos.top, right: pos.right, zIndex: 10001,
            minWidth: 150, padding: 5, borderRadius: 14,
            border: "1px solid rgba(255,255,255,0.55)",
            boxShadow: "0 12px 34px rgba(26,94,58,0.16), 0 2px 8px rgba(0,0,0,0.06)",
          }}
        >
          {MEASURE_OPTS.map(([val, lbl]) => {
            const sel = mode === val;
            return (
              <button
                key={val}
                type="button"
                role="option"
                aria-selected={sel}
                className="measure-opt"
                onClick={() => { onChange(val); close(); }}
                onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = C.sand; }}
                onMouseLeave={(e) => { if (!sel) e.currentTarget.style.background = "transparent"; }}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                  width: "100%", padding: "9px 11px", borderRadius: 10, border: "none",
                  cursor: "pointer", textAlign: "left",
                  fontSize: 13, fontWeight: sel ? 700 : 600,
                  background: sel ? C.greenPale : "transparent",
                  color: sel ? C.green : C.charcoal,
                }}
              >
                <span>{lbl}</span>
                {sel && (
                  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                    <path d="M2.5 7.5l3 3 6-7" fill="none" stroke={C.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}

function importedRecipeMatchesTag(recipe, tag) {
  if (!tag) return true;
  const title = (recipe.title || "").toLowerCase();
  const dietary = Array.isArray(recipe.dietary) ? recipe.dietary.join(" ").toLowerCase() : "";
  const totalMinutes = Number(recipe.prep_minutes || 0) + Number(recipe.cook_minutes || 0);

  if (tag === "quick") return totalMinutes > 0 && totalMinutes <= 35;
  if (tag === "vegan") return dietary.includes("vegan");
  if (tag === "vegetarian") return dietary.includes("vegetarian") || dietary.includes("vegan");
  if (tag === "healthy") return dietary.includes("healthy") || dietary.includes("gluten-free") || title.includes("healthy");
  if (tag === "spicy") return /\b(spicy|chilli|chili|hot|sriracha|curry)\b/.test(title);
  if (tag === "comfort") return /\b(comfort|slow cooker|pasta|pie|stew|casserole|lasagne|lasagna|mac)\b/.test(title);
  if (tag === "authentic") return Boolean(recipe.cuisine);
  return true;
}

function normalizeIngredientKey(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\b(?:fresh|dried|ground|boneless|bone in|skinless|skin on|trimmed|chopped|minced|grated|sliced|finely|roughly|large|medium|small)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b(\w+)s\b/g, "$1");
}

function ingredientKeys(ingredient) {
  return [ingredient?.id, ingredient?.name]
    .map(normalizeIngredientKey)
    .filter(key => key.length >= 3);
}

function ingredientOverlapsSelected(ingredient, selectedKeys) {
  const keys = ingredientKeys(ingredient);
  return keys.some(key =>
    selectedKeys.has(key) ||
    [...selectedKeys].some(selectedKey =>
      selectedKey.length >= 4 &&
      key.length >= 4 &&
      (selectedKey.includes(key) || key.includes(selectedKey))
    )
  );
}

// Formats prep/cook minutes into a UI-friendly duration.
// 0-30 → "Less than 30 mins"; 30-60 → "30 mins to 1 hr"; otherwise the value.
function formatDuration(mins) {
  if (mins == null || !Number.isFinite(mins) || mins <= 0) return "—";
  if (mins < 30) return "Less than 30 mins";
  if (mins <= 60) return mins === 60 ? "1 hr" : `${mins} mins`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} mins`;
}

function recipeEmoji(title = "") {
  const t = title.toLowerCase();
  if (/\b(curry|biryani|masala|tagine|dal|dhal)\b/.test(t)) return "🍛";
  if (/\b(pasta|spaghetti|linguine|rigatoni|lasagne|noodle|ramen|udon)\b/.test(t)) return "🍝";
  if (/\b(pizza|flatbread)\b/.test(t)) return "🍕";
  if (/\b(taco|quesadilla|fajita|enchilada)\b/.test(t)) return "🌮";
  if (/\b(burger|sandwich|wrap)\b/.test(t)) return "🍔";
  if (/\b(soup|stew|broth|chowder)\b/.test(t)) return "🥣";
  if (/\b(salad|slaw)\b/.test(t)) return "🥗";
  if (/\b(cake|tart|dessert|pudding|brownie|cookie|biscuit)\b/.test(t)) return "🍰";
  if (/\b(pie|past(y|ies))\b/.test(t)) return "🥧";
  if (/\b(rice|risotto|paella)\b/.test(t)) return "🍚";
  if (/\b(prawn|shrimp|salmon|cod|fish|seafood|hake)\b/.test(t)) return "🐟";
  if (/\b(duck)\b/.test(t)) return "🦆";
  if (/\b(chicken|turkey)\b/.test(t)) return "🍗";
  if (/\b(beef|steak|lamb|pork|bacon|chorizo|meatball)\b/.test(t)) return "🥩";
  if (/\b(tofu|vegan|vegetarian|aubergine|mushroom|lentil|bean)\b/.test(t)) return "🥗";
  return "🍽️";
}

function DrinkPairingCards({ pairs = [], compact = false }) {
  const visible = pairs.slice(0, 3);
  if (!visible.length) return null;
  return (
    <div style={{
      marginBottom: compact ? 14 : 16,
      padding: compact ? "12px" : "12px 0 0",
      borderTop: compact ? "none" : `1px solid ${C.border}`,
      borderRadius: compact ? 14 : 0,
      background: compact ? C.tealPale : "transparent",
    }}>
      <div style={{
        fontSize:11, fontWeight:800, color:C.grey,
        textTransform:"uppercase", letterSpacing:".06em", marginBottom:8,
      }}>🍷 Drink pairings</div>
      <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
        {visible.map((p, i) => (
          <div key={`${p.type}-${p.name}-${i}`} style={{
            background:C.cream, borderRadius:10, padding:"7px 10px",
            border:`1px solid ${C.border}`, flex:"1 1 140px", minWidth:0,
          }}>
            <div style={{ fontSize:12, color:C.charcoal, lineHeight:1.3 }}>
              {p.type} <span style={{ fontWeight:700 }}>{p.name}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:3 }}>
              <span style={{ fontSize:10, color:C.grey }}>{p.region}</span>
              <span style={{ fontSize:11, fontWeight:700, color:C.greenLight }}>{p.match}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Ingredient quantity display helpers ──────────────────────────────────────
// Readable cooking fractions, ascending. Display prefers a unicode glyph where
// one exists (½ ⅓ ¼ ⅙ ⅛ …); 12ths and 16ths have no glyph so show as "n/16".
// Background maths stay decimal — this table only drives the DISPLAY string, so
// a value is always SNAPPED to the nearest entry rather than printed as 0.1875.
const _NICE_FRACTIONS = [
  [1/16, "1/16"],  [1/12, "1/12"], [1/8, "⅛"],   [1/6, "⅙"],   [3/16, "3/16"],
  [1/4,  "¼"],     [5/16, "5/16"], [1/3, "⅓"],   [3/8, "⅜"],   [5/12, "5/12"],
  [7/16, "7/16"],  [1/2,  "½"],    [9/16, "9/16"],[7/12, "7/12"],[5/8, "⅝"],
  [2/3,  "⅔"],     [11/16,"11/16"],[3/4, "¾"],   [13/16,"13/16"],[5/6, "⅚"],
  [7/8,  "⅞"],     [11/12,"11/12"],[15/16,"15/16"],
];

const _DISPLAY_FRACTION_VALUES = new Map([
  ["½", 1/2], ["⅓", 1/3], ["⅔", 2/3],
  ["¼", 1/4], ["¾", 3/4],
  ["⅛", 1/8], ["⅜", 3/8], ["⅝", 5/8], ["⅞", 7/8],
  ["⅙", 1/6], ["⅚", 5/6],
]);

function _parseDisplayQty(value) {
  const text = String(value ?? "").trim();
  if (!text || /\b(to|or)\b|[-–—]/i.test(text)) return null;

  const mixedGlyph = text.match(/^(\d+(?:\.\d+)?)\s*([½⅓⅔¼¾⅛⅜⅝⅞⅙⅚])$/);
  if (mixedGlyph) return Number(mixedGlyph[1]) + _DISPLAY_FRACTION_VALUES.get(mixedGlyph[2]);
  if (_DISPLAY_FRACTION_VALUES.has(text)) return _DISPLAY_FRACTION_VALUES.get(text);

  const fraction = text.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (fraction) {
    const denominator = Number(fraction[2]);
    return denominator ? Number(fraction[1]) / denominator : null;
  }

  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

function _ingredientNumericQty(ing) {
  const direct = ing?.quantityValue ?? ing?.quantityMax ?? ing?.qty;
  if (direct != null && Number.isFinite(Number(direct))) return Number(direct);
  return _parseDisplayQty(ing?.quantityDisplay) ?? _parseDisplayQty(ing?.qtyText);
}

/**
 * Convert a numeric qty to a clean cooking fraction for display.
 * e.g. 0.333 → "⅓", 0.1875 → "3/16", 0.167 → "⅙", 1.5 → "1½".
 * The fractional part snaps to the nearest readable fraction (1/16 resolution),
 * so the UI never shows ugly decimals like 0.188 or 0.563.
 */
export function niceQty(qty) {
  if (qty == null) return "";
  const num = Number(qty);
  if (!Number.isFinite(num)) return String(qty);
  const sign = num < 0 ? "-" : "";
  const abs  = Math.abs(num);
  let whole  = Math.floor(abs);
  const frac = abs - whole;

  // Nearest readable fraction, with the integer endpoints 0 (drop) and 1 (carry).
  let bestDiff = frac;   // distance to 0 → no fraction glyph
  let bestSym  = "";
  for (const [v, s] of _NICE_FRACTIONS) {
    const d = Math.abs(frac - v);
    if (d < bestDiff) { bestDiff = d; bestSym = s; }
  }
  if (1 - frac < bestDiff) { whole += 1; bestSym = ""; }  // rounds up to next whole

  if (bestSym && whole > 0) return `${sign}${whole}${bestSym}`;
  if (bestSym)              return `${sign}${bestSym}`;
  return `${sign}${whole}`;
}

// Teaspoon-family units, for the "pinch" rule on sub-1/16 tsp amounts.
const _TSP_UNITS = new Set(["tsp","tsps","teaspoon","teaspoons","t"]);
// Below this many teaspoons we say "pinch" instead of a tiny fraction; at/above
// it we show "1/16 tsp". Threshold sits between 1/32 (0.031) and 1/16 (0.063).
const _PINCH_MAX_TSP = 0.047;

const _PLURAL_UNITS = {
  teaspoon: "teaspoons", tablespoon: "tablespoons", cup: "cups",
  gram: "grams", pound: "pounds", clove: "cloves", can: "cans",
  tin: "tins", bunch: "bunches", sprig: "sprigs", pinch: "pinches",
  pack: "packs", piece: "pieces", inch: "inches",
};

function _pluralizeUnit(unit, qty) {
  if (!unit || qty == null || qty <= 1) return unit;
  return _PLURAL_UNITS[unit.toLowerCase()] ?? unit;
}

/**
 * Format an ingredient's quantity for display in the recipe detail view.
 * Prefers quantityDisplay (unicode fractions) over raw numeric qty.
 * Pluralizes countable units when qty > 1.
 */
export function displayIngredientQtyUnit(ing) {
  if (!ing) return "";
  const rawUnit = ing.unitDisplay ?? ing.unit ?? "";
  const qtyText = ing.qtyText == null ? "" : String(ing.qtyText).trim();
  if (ing.quantityDisplay == null && qtyText && rawUnit && _qtyTextAlreadyIncludesUnit(qtyText, rawUnit)) {
    return qtyText;
  }
  const qtyStr = ing.quantityDisplay ?? (qtyText || niceQty(ing.qty));
  // Use quantityMax for ranges (highest plausible qty drives plural).
  const displayQty = _ingredientNumericQty(ing);
  const unitStr = _pluralizeUnit(rawUnit, displayQty);
  return [qtyStr, unitStr].filter(Boolean).join(" ");
}

function _qtyTextAlreadyIncludesUnit(qtyText, rawUnit) {
  const text = String(qtyText || "").toLowerCase();
  const unit = String(rawUnit || "").toLowerCase();
  if (!text || !unit) return false;
  const aliases = {
    g: ["g", "gram", "grams"],
    gram: ["g", "gram", "grams"],
    grams: ["g", "gram", "grams"],
    kg: ["kg", "kilogram", "kilograms"],
    ml: ["ml", "millilitre", "milliliter", "millilitres", "milliliters"],
    l: ["l", "litre", "liter", "litres", "liters"],
    tsp: ["tsp", "teaspoon", "teaspoons"],
    teaspoon: ["tsp", "teaspoon", "teaspoons"],
    teaspoons: ["tsp", "teaspoon", "teaspoons"],
    tbsp: ["tbsp", "tablespoon", "tablespoons"],
    tablespoon: ["tbsp", "tablespoon", "tablespoons"],
    tablespoons: ["tbsp", "tablespoon", "tablespoons"],
    clove: ["clove", "cloves"],
    cloves: ["clove", "cloves"],
    cup: ["cup", "cups"],
    cups: ["cup", "cups"],
  };
  const candidates = aliases[unit] || [unit, unit.replace(/s$/, "")].filter(Boolean);
  return candidates.some(candidate => {
    const escaped = candidate.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`(?:^|\\d|\\s)${escaped}\\b`, "i").test(text);
  });
}

// Mass/volume units round to a whole number (e.g. 333g, not 333.33g); countable
// or small units (tsp, tbsp, cloves, "2 eggs") use friendly fractions (½ ⅓ ¼).
const _MASS_VOLUME_UNITS = new Set([
  "g","gram","grams","kg","kilogram","kilograms",
  "ml","millilitre","milliliter","millilitres","milliliters",
  "l","litre","liter","litres","liters","oz","ounce","ounces","lb","lbs","pound","pounds",
]);

function _formatScaledQty(qty, unit) {
  const n = Number(qty);
  if (!Number.isFinite(n)) return "";
  if (_MASS_VOLUME_UNITS.has((unit || "").toLowerCase())) return String(Math.round(n));
  return niceQty(Number(n.toFixed(3)));  // fractions where they're clean, else a short decimal
}

// ── Measurement-mode conversion (DISPLAY ONLY) ───────────────────────────────
// Original/Imperial/Metric toggle for imported recipe ingredient quantities.
// Conservative: converts only common, unambiguous weight/volume units. Subjective
// units (pinch, clove, can…), counts, and ranges keep their original wording.
// NEVER mutates stored ingredients — shopping/matching/pricing read structured
// qty/unit separately and are unaffected.
const _MEASURE_UNIT_ALIAS = {
  g:"g", gram:"g", grams:"g", kg:"kg", kilogram:"kg", kilograms:"kg",
  ml:"ml", millilitre:"ml", milliliter:"ml", millilitres:"ml", milliliters:"ml",
  l:"l", litre:"l", liter:"l", litres:"l", liters:"l",
  oz:"oz", ounce:"oz", ounces:"oz",
  "fl oz":"floz", floz:"floz", "fluid ounce":"floz", "fluid ounces":"floz",
  lb:"lb", lbs:"lb", pound:"lb", pounds:"lb",
  tsp:"tsp", teaspoon:"tsp", teaspoons:"tsp",
  tbsp:"tbsp", tablespoon:"tbsp", tablespoons:"tbsp",
  cup:"cup", cups:"cup", pint:"pint", pints:"pint", quart:"quart", quarts:"quart",
};

function _canonMeasureUnit(unit) {
  return _MEASURE_UNIT_ALIAS[String(unit || "").trim().toLowerCase()] || null;
}

function _isRangeIngredient(ing) {
  if (!ing) return false;
  if (ing.quantityMin != null && ing.quantityMax != null
      && Number(ing.quantityMin) !== Number(ing.quantityMax)) return true;
  const text = String(ing.quantityDisplay ?? ing.qtyText ?? "");
  return /\d/.test(text) && /\bto\b|[-–—]/i.test(text);
}

/**
 * Converts a quantity+unit into the target measurement system for DISPLAY.
 * Returns { qty, unit } when a confident conversion exists, else null (keep
 * original wording). Never guesses density-based volume↔weight conversions.
 */
export function convertMeasureForMode(qty, unit, mode) {
  const n = Number(qty);
  if (!Number.isFinite(n) || n <= 0) return null;
  const u = _canonMeasureUnit(unit);
  if (!u) return null;
  if (mode === "metric") {
    switch (u) {
      case "oz":   { const g = n * 28.3495;  return g  >= 1000 ? { qty: g / 1000,  unit: "kg" } : { qty: g,  unit: "g" }; }
      case "lb":   { const g = n * 453.592;  return g  >= 1000 ? { qty: g / 1000,  unit: "kg" } : { qty: g,  unit: "g" }; }
      case "floz": { const ml = n * 29.5735; return ml >= 1000 ? { qty: ml / 1000, unit: "l" }  : { qty: ml, unit: "ml" }; }
      case "cup":  { const ml = n * 236.588; return ml >= 1000 ? { qty: ml / 1000, unit: "l" }  : { qty: ml, unit: "ml" }; }
      case "pint": { const ml = n * 473.176; return ml >= 1000 ? { qty: ml / 1000, unit: "l" }  : { qty: ml, unit: "ml" }; }
      case "quart":{ const ml = n * 946.353; return ml >= 1000 ? { qty: ml / 1000, unit: "l" }  : { qty: ml, unit: "ml" }; }
      case "tbsp": return { qty: n * 14.7868, unit: "ml" };
      case "tsp":  return { qty: n * 4.92892, unit: "ml" };
      default:     return null; // g/kg/ml/l already metric
    }
  }
  if (mode === "imperial") {
    switch (u) {
      case "g":  { const oz = n / 28.3495;        return oz >= 16 ? { qty: oz / 16, unit: "lb" } : { qty: oz, unit: "oz" }; }
      case "kg": { const oz = (n * 1000) / 28.3495; return oz >= 16 ? { qty: oz / 16, unit: "lb" } : { qty: oz, unit: "oz" }; }
      case "ml": { const floz = n / 29.5735;      return floz >= 8 ? { qty: floz / 8, unit: "cup" } : { qty: floz, unit: "fl oz" }; }
      case "l":  return { qty: (n * 1000) / 236.588, unit: "cup" };
      default:   return null; // oz/lb/fl oz/cup/tsp/tbsp already imperial
    }
  }
  return null;
}

function _trimNum(n, dp) { return String(Number(Number(n).toFixed(dp))); }

function _formatConvertedQty(qty, unit) {
  const n = Number(qty);
  if (!Number.isFinite(n)) return "";
  switch (unit) {
    case "g": case "ml":   return String(Math.round(n));
    case "kg": case "l":   return _trimNum(n, 2);
    case "oz": case "fl oz": return _trimNum(n, 1);
    case "lb":             return _trimNum(n, 2);
    default:               return niceQty(Number(n.toFixed(3))); // cup/tsp/tbsp → fractions
  }
}

function _convertedScaledDisplay(ing, factor, mode) {
  if (_isRangeIngredient(ing)) return null;
  const base = _ingredientNumericQty(ing);
  if (base == null || !Number.isFinite(Number(base))) return null;
  const f = factor > 0 ? factor : 1;
  const scaled = Number(base) * f;
  const conv = convertMeasureForMode(scaled, ing.unitDisplay ?? ing.unit ?? "", mode);
  if (!conv) return null;
  const qtyStr = _formatConvertedQty(conv.qty, conv.unit);
  if (!qtyStr) return null;
  return [qtyStr, _pluralizeUnit(conv.unit, conv.qty)].filter(Boolean).join(" ");
}

/**
 * Imported-recipe quantity scaled to the planned servings, formatted for the UI.
 * Background maths stay decimal; the DISPLAY shows ½ ⅓ ¼ etc for countable units
 * and rounded whole numbers for grams/ml — never an ugly 0.33333.
 * factor === 1 (or unscalable) falls back to the recipe's own display string.
 * `mode` ('original'|'imperial'|'metric') converts common units for display only;
 * unconvertible/ambiguous ingredients keep their original wording.
 */
export function displayScaledImportedQty(ing, factor = 1, mode = "original") {
  if (!ing) return "";
  if (mode === "imperial" || mode === "metric") {
    const converted = _convertedScaledDisplay(ing, factor, mode);
    if (converted) return converted;
  }
  if (!(factor > 0) || factor === 1) return displayIngredientQtyUnit(ing);
  const base = _ingredientNumericQty(ing);
  if (base == null || !Number.isFinite(Number(base))) return displayIngredientQtyUnit(ing);
  const scaled  = Number(base) * factor;
  const rawUnit = ing.unitDisplay ?? ing.unit ?? "";
  // Tiny teaspoon amounts read as "pinch" (no unit) rather than a fiddly fraction.
  if (scaled > 0 && scaled < _PINCH_MAX_TSP && _TSP_UNITS.has(rawUnit.toLowerCase())) {
    return "pinch";
  }
  const qtyStr  = _formatScaledQty(scaled, rawUnit);
  if (!qtyStr) return displayIngredientQtyUnit(ing);
  return [qtyStr, _pluralizeUnit(rawUnit, scaled)].filter(Boolean).join(" ");
}

/**
 * Cart quantity for an imported ingredient.
 * For ranges, uses quantityMax (purchase planning: don't under-buy).
 * For single quantities, uses quantityValue (full precision).
 * Falls back to qty (3dp legacy) for saved ingredients that pre-date the new model.
 */
export function cartQuantityForIngredient(ingredient) {
  if (ingredient?.quantityMax != null) return ingredient.quantityMax;
  if (ingredient?.quantityValue != null) return ingredient.quantityValue;
  const q = ingredient?.qty;
  return q == null ? null : Number(q);
}

export function scaleImportedIngredientForServings(ingredient, factor = 1) {
  const numericQty = cartQuantityForIngredient(ingredient);
  return {
    name: ingredient?.name || "",
    qty:  numericQty != null && Number.isFinite(numericQty)
      ? +(numericQty * factor).toFixed(4)
      : null,
    unit: ingredient?.unit ?? null,
  };
}

function importedRecipeDisplayName(ingredient) {
  const name = String(ingredient?.name || "").trim();
  const notes = String(ingredient?.notes || "").trim();
  if (!notes) return name;
  if (name.toLowerCase().includes(notes.toLowerCase())) return name;
  return `${name}, ${notes}`;
}

export function normalizeImportedMatched(items = []) {
  const byId = new Map();
  for (const item of items) {
    if (!item?.id) continue;
    const originalPkgId = item.originalPkgId
      || (item.original ? item.id : item.alternatives?.find(alt => alt.original)?.pkgId);
    const existing = byId.get(item.id);
    if (!existing) {
      byId.set(item.id, normalizeImportedOriginals({
        ...item,
        originalPkgId,
        origNames: [...new Set(item.origNames || [])],
        alternatives: [...(item.alternatives || [])],
      }));
      continue;
    }

    const mergedOriginalPkgId = existing.originalPkgId || originalPkgId;
    const altById = new Map(
      [...(existing.alternatives || []), ...(item.alternatives || [])]
        .filter(alt => alt?.pkgId && alt.pkgId !== item.id)
        .map(alt => [alt.pkgId, alt]),
    );
    existing.origNames = [...new Set([...(existing.origNames || []), ...(item.origNames || [])])];
    existing.originalPkgId = mergedOriginalPkgId;
    existing.alternatives = [...altById.values()];
    existing.pinned = existing.pinned || item.pinned;
    existing.original = existing.id === mergedOriginalPkgId;
    normalizeImportedOriginals(existing);
  }
  return [...byId.values()];
}

export function normalizeImportedOriginals(item) {
  if (!item) return item;
  const originalPkgId = item.originalPkgId
    || (item.original ? item.id : item.alternatives?.find(alt => alt.original)?.pkgId)
    || null;
  item.originalPkgId = originalPkgId;
  item.original = !!originalPkgId && item.id === originalPkgId;
  item.alternatives = (item.alternatives || []).map(alt => ({
    ...alt,
    original: !!originalPkgId && item.id !== originalPkgId && alt.pkgId === originalPkgId,
  }));
  return item;
}

function formatApproxShoppingAmount(amount, unit = "g") {
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  const suffix = unit === "count" ? "" : unit;
  return `~${Math.round(n)}${suffix}`;
}

function formatExactShoppingAmount(amount, unit = "g") {
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  const suffix = unit === "count" ? "" : unit;
  return `${Math.round(n)}${suffix}`;
}

function displayAlreadyMetric(text) {
  return /\b\d+(?:\.\d+)?\s*(?:g|kg|ml|l|gram|grams|millilitre|milliliter|litre|liter)s?\b/i.test(String(text || ""));
}

function servingLabel(count) {
  const n = Number(count);
  return `${n} serving${n === 1 ? "" : "s"}`;
}

function buyPackLabel(item) {
  const packs = Number(item?.packs || 0);
  const size = formatApproxShoppingAmount(item?.size, item?.unit);
  if (!packs || !size) return null;
  const packWord = packs === 1 ? "pack" : "packs";
  return packs === 1
    ? `Buy: 1 ${packWord} ${size}`
    : `Buy: ${packs} ${packWord} ${size} each`;
}

export function practicalShoppingSubtitle(item) {
  const requirement = item?.recipeRequirement;
  const suggestion = item?.shoppingSuggestion;
  if (!requirement || !suggestion) return null;

  const needs = formatApproxShoppingAmount(requirement.amount, requirement.unit || item.unit);
  const pack = formatApproxShoppingAmount(suggestion.packAmount, suggestion.packUnit || item.unit);
  if (!needs || !pack) return null;

  const qty = Number(suggestion.quantityToBuy || item.packs || 1);
  const packLabel = qty === 1 ? "pack" : "packs";
  const extra = Number(suggestion.extraAmount) > 0
    ? ` · Extra: ${formatApproxShoppingAmount(suggestion.extraAmount, suggestion.packUnit || item.unit)}`
    : "";
  const shortfall = Number(suggestion.shortfallAmount) > 0
    ? ` · Short: ${formatApproxShoppingAmount(suggestion.shortfallAmount, suggestion.packUnit || item.unit)}`
    : "";

  return `Recipe needs: ${needs} · Buy: ${qty} ${packLabel}, ${pack}${extra}${shortfall}`;
}

function importedScalingSubtitle(item, sourceLine) {
  const lines = Array.isArray(item?.importedScaling) ? item.importedScaling : [];
  if (lines.length === 0) return null;
  const buy = buyPackLabel(item);
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
      {sourceLine && <span>{sourceLine}</span>}
      {lines.map((line, index) => {
        const base = formatExactShoppingAmount(line.baseAmount, line.unit || item.unit);
        const planned = formatExactShoppingAmount(line.plannedAmount, line.unit || item.unit);
        const baseApprox = formatApproxShoppingAmount(line.baseAmount, line.unit || item.unit);
        const displayText = typeof line.displayText === "string" ? line.displayText.trim() : "";
        const recipeAmount = displayText
          ? (displayAlreadyMetric(displayText) || !baseApprox ? displayText : `${displayText} (${baseApprox})`)
          : base;
        const recipeLabel = lines.length > 1 ? `Recipe (${line.recipeName})` : "Recipe";
        return (
          <span key={`${line.recipeName}-${index}`}>
            {recipeLabel}: {recipeAmount} for {servingLabel(line.savedServings)} · This plan: {planned} for {servingLabel(line.plannedServings)}
          </span>
        );
      })}
      {buy && <span>{buy}</span>}
    </div>
  );
}

// applyRoundDownChoices — overlay the user's per-item "round down vs buy extra"
// pack choices onto a freshly-built shopping list. The choice itself lives in a
// separate, durable state map (not inside the list), so it survives every
// rebuild — without this, any rebuild (retailer/servings change, imported-cache
// hydration) would silently reset the user back to the server default packs.
export function applyRoundDownChoices(shoppingList = {}, choices = {}) {
  if (!choices || Object.keys(choices).length === 0) return shoppingList;
  let out = null;
  for (const [id, scenario] of Object.entries(choices)) {
    const item = shoppingList[id];
    const opt  = item?.roundDownOption;
    if (!opt || !opt.eligible) continue;            // item gone or no longer eligible
    const pick = scenario === "roundDown" ? opt.roundDown : opt.buyExtra;
    if (!pick || Number(item.packs) === Number(pick.packs)) continue; // already applied
    out = out || { ...shoppingList };
    out[id] = {
      ...item,
      packs:       pick.packs,
      cost:        pick.cost,
      leftover:    pick.leftover,
      roundedDown: scenario === "roundDown",
    };
  }
  return out || shoppingList;
}

// shoppingList is the UNIFIED server list — catalog + imported already merged by
// PKG id, so we no longer concat importedItems.matched here (that would
// double-count a shared ingredient). manualItems are the unmatched imported
// free-text items (priced at £0; only counted for the "owned" tally).
export function computeBasketTotals({ shoppingList = {}, manualItems = [], pantryItems = new Set() } = {}) {
  const pricedItems = Object.values(shoppingList);
  const full = pricedItems.reduce((sum, item) => sum + (Number(item.cost) || 0), 0);
  let payable = 0;
  let ownedCount = 0;

  for (const item of pricedItems) {
    if (pantryItems.has(`pkg:${item.id}`)) {
      ownedCount += 1;
    } else {
      payable += Number(item.cost) || 0;
    }
  }

  for (const item of manualItems) {
    const manualKey = `manual:${item.origName?.toLowerCase?.() ?? item.displayText?.toLowerCase?.() ?? ""}`;
    if (pantryItems.has(manualKey)) ownedCount += 1;
  }

  return {
    fullCost: full.toFixed(2),
    payableCost: payable.toFixed(2),
    ownedCount,
    payableValue: payable,
  };
}

export function dedupeRecipeLibraryById(recipes = []) {
  const out = [];
  const indexById = new Map();
  for (const recipe of recipes || []) {
    const id = String(recipe?.id || "").trim();
    if (!id) {
      out.push(recipe);
      continue;
    }
    if (indexById.has(id)) {
      const idx = indexById.get(id);
      out[idx] = { ...out[idx], ...recipe };
    } else {
      indexById.set(id, out.length);
      out.push(recipe);
    }
  }
  return out;
}

export function retailerOptionsForDisplay(availableRetailers = [], retailerId, fallbackRetailers = FALLBACK_RETAILERS) {
  const options = (availableRetailers && availableRetailers.length > 0)
    ? [...availableRetailers]
    : [...fallbackRetailers];
  const selectedId = String(retailerId || "").trim();
  const normalizedSelectedId = selectedId.toLowerCase();
  if (selectedId && !options.some(r => String(r.id).trim().toLowerCase() === normalizedSelectedId)) {
    const fallback = fallbackRetailers.find(r => String(r.id).trim().toLowerCase() === normalizedSelectedId);
    options.push(fallback || { id: selectedId, name: retailerLabel(selectedId, fallbackRetailers) });
  }
  return options;
}

// Shared-ingredient detection for the basket banner.
//
//   catalog meal A ─┐
//   catalog meal B ─┼─► count by PKG id ─► ids in 2+ sources = "shared"
//   imported (all) ─┘   (imported recipes count as ONE combined source —
//                        per-recipe provenance isn't available client-side,
//                        so two imported recipes sharing an item won't show;
//                        catalog↔imported and catalog↔catalog overlap does)
//
// Each catalog meal counts a given id once; imported PKG ids add one source.
export function computeSharedIngredientIds(loadedMeals = [], importedPkgIds = []) {
  const counts = {};
  for (const meal of loadedMeals) {
    const seen = new Set();
    for (const ing of (meal?.ingredients || [])) {
      if (!ing?.id || seen.has(ing.id)) continue;  // count each id once per meal
      seen.add(ing.id);
      counts[ing.id] = (counts[ing.id] || 0) + 1;
    }
  }
  const importedUnique = [...new Set(importedPkgIds)];
  for (const id of importedUnique) {
    counts[id] = (counts[id] || 0) + 1;
  }
  return Object.entries(counts).filter(([, c]) => c > 1).map(([id]) => id);
}

// Ingredient-specific storage/use tips. Keyed by partial lowercase name.
// Only shown when that ingredient has leftover > 0 in the current shopping list.
export const INGREDIENT_TIPS = {
  "chicken":       "Use leftover chicken in wraps, fried rice, or soup.",
  "fish":          "Use leftover fish in tacos, fishcakes, or rice bowls.",
  "cod":           "Use leftover cod in fishcakes, tacos, or soup.",
  "prawn":         "Use leftover prawns in fried rice, noodles, or salad.",
  "beef":          "Use leftover beef in wraps, fried rice, or sandwiches.",
  "pork":          "Use leftover pork in noodles, tacos, or fried rice.",
  "egg":           "Use extra eggs for fried rice, omelettes, or pancakes.",
  "cream":         "Use leftover cream in pasta sauce, soup, or mashed potatoes.",
  "yogurt":        "Use leftover yogurt in marinades, dips, or dressings.",
  "coconut milk":  "Use leftover coconut milk in curry, oats, or smoothies.",
  "spinach":       "Use extra spinach in soup, pasta, or omelettes.",
  "kale":          "Use extra kale in soup, pasta, or crispy kale.",
  "mushroom":      "Use extra mushrooms in omelettes, pasta, or fried rice.",
  "coriander":     "Use leftover coriander in salsa, curry, or noodle bowls.",
  "parsley":       "Use leftover parsley in salads, sauces, or eggs.",
  "basil":         "Use leftover basil in pasta, pesto, or tomato salad.",
  "mint":          "Use leftover mint in yogurt sauce, salads, or drinks.",
  "lemongrass":    "Freeze leftover lemongrass for curries, soups, or marinades.",
  "ginger":        "Freeze leftover ginger and grate it into stir-fries or tea.",
  "chilli":        "Freeze extra chillies for stir-fries, curries, or salsa.",
  "carrot":        "Use extra carrots in soup or slaw.",
  "onion":         "Use extra onions in soup, sauce, or fried rice.",
  "pepper":        "Use extra peppers in fajitas, pasta, or fried rice.",
  "celery":        "Use extra celery in soup, stew, or stock.",
  "tomato paste":  "Freeze leftover tomato paste in tablespoon portions.",
  "lime":          "Juice leftover lime and freeze it for drinks or marinades.",
  "lemon":         "Juice leftover lemon and freeze it for dressings or tea.",
  // Shelf-stable items get storage advice, not "use within days".
  "rice":          "Use leftover rice for egg fried rice.",
  "pasta":         "Use extra pasta in pasta salad or soup.",
  "lentil":        "Use extra lentils in soup, dal, or salads.",
  "sesame oil":    "Use extra sesame oil in stir-fries, dressings, or marinades.",
  "soy sauce":     "Use extra soy sauce in stir-fries, marinades, or dipping sauces.",
  "fish sauce":    "Use extra fish sauce in stir-fries, curries, or dressings.",
  "olive oil":     "Use extra olive oil in dressings, roasting, or finishing.",
};

// Pure decision function for undo-restore. Operates on a batch — single
// removals pass an array of length 1; bulk "Remove all" passes the full set.
// Returns the entries that fit within mealTarget and a list of skipped names
// for any that couldn't be restored.
export function restoreUndoBatch({ undoMeals, mealsById, selectedMeals, mealTarget }) {
  const restored = [];
  const skipped = [];
  const selectedSet = new Set(selectedMeals);
  let slotsLeft = mealTarget - selectedMeals.length;
  for (const entry of undoMeals) {
    const meal = mealsById.get(entry.id) || entry.meal;
    if (!meal) {
      skipped.push({ name: entry.name, reason: "no longer in current filter" });
      continue;
    }
    if (selectedSet.has(entry.id)) {
      skipped.push({ name: entry.name, reason: "already in your week" });
      continue;
    }
    if (slotsLeft <= 0) {
      skipped.push({ name: entry.name, reason: "week full" });
      continue;
    }
    restored.push(entry);
    selectedSet.add(entry.id);
    slotsLeft -= 1;
  }
  let skippedMessage = "";
  if (skipped.length === 1) {
    skippedMessage = `Couldn't restore ${skipped[0].name} — ${skipped[0].reason}`;
  } else if (skipped.length > 1) {
    skippedMessage = `Couldn't restore ${skipped.length} meals — make room and try again`;
  }
  return { restored, skipped, skippedMessage };
}

export function applyRemoveUndoWeekPlan({ restored, weekPlan }) {
  const next = weekPlan && typeof weekPlan === "object" ? { ...weekPlan } : {};
  const unscheduled = [];
  for (const e of restored || []) {
    if (!e.day) continue;
    if (!next[e.day] || next[e.day] === e.id) {
      next[e.day] = e.id;
    } else {
      unscheduled.push(e);
    }
  }
  return { weekPlan: next, unscheduled };
}

export function makeAutofillUndo({ addedIds, selectedMeals, weekPlan, servingMap }) {
  const ids = Array.isArray(addedIds) ? [...addedIds] : [];
  return {
    ids,
    count: ids.length,
    snapshot: {
      selectedMeals: Array.isArray(selectedMeals) ? [...selectedMeals] : [],
      weekPlan: weekPlan && typeof weekPlan === "object" ? { ...weekPlan } : {},
      servingMap: servingMap && typeof servingMap === "object" ? { ...servingMap } : {},
    },
  };
}

export function applyAutofillUndo({ undo, selectedMeals, weekPlan, servingMap }) {
  const ids = Array.isArray(undo?.ids) ? undo.ids : [];
  const addedSet = new Set(ids);
  if (addedSet.size === 0) {
    return { ok: false, reason: "No autofilled meals to undo" };
  }

  const currentSelected = Array.isArray(selectedMeals) ? selectedMeals : [];
  const removedCount = currentSelected.filter(id => addedSet.has(id)).length;
  if (removedCount === 0) {
    return { ok: false, reason: "Autofill was already undone" };
  }

  const snapshot = undo?.snapshot || {};
  const nextSelected = Array.isArray(snapshot.selectedMeals)
    ? [...snapshot.selectedMeals]
    : currentSelected.filter(id => !addedSet.has(id));
  const nextWeekPlan = snapshot.weekPlan && typeof snapshot.weekPlan === "object"
    ? { ...snapshot.weekPlan }
    : Object.fromEntries(Object.entries(weekPlan || {}).filter(([, id]) => !addedSet.has(id)));
  const nextServingMap = snapshot.servingMap && typeof snapshot.servingMap === "object"
    ? { ...snapshot.servingMap }
    : Object.fromEntries(Object.entries(servingMap || {}).filter(([id]) => !addedSet.has(id)));

  return {
    ok: true,
    removedCount,
    selectedMeals: nextSelected,
    weekPlan: nextWeekPlan,
    servingMap: nextServingMap,
  };
}

function wastePriority(item) {
  const name = String(item?.name || "").toLowerCase();
  const cat = String(item?.cat || "").toLowerCase();
  if (cat === "proteins" || /\b(chicken|beef|pork|lamb|duck|ham|fish|cod|salmon|prawn|shrimp|seafood|chorizo)\b/.test(name)) return 1;
  if (cat === "dairy" || /\b(egg|cream|milk|cheese|yogurt|butter)\b/.test(name)) return 2;
  if (cat === "herbs" || /\b(herb|basil|parsley|coriander|cilantro|mint|spinach|kale|salad|lettuce|rocket|mushroom|berry|berries)\b/.test(name)) return 3;
  if (cat === "produce" || /\b(carrot|celery|onion|pepper|tomato|lemon|lime|ginger|chilli|chili|apple|mango)\b/.test(name)) return 4;
  return 5;
}

function leftoverValue(item) {
  const leftover = Number(item?.leftover) || 0;
  const size = Number(item?.size) || 0;
  const price = Number(item?.price) || 0;
  return size > 0 && price > 0 ? (leftover / size) * price : 0;
}

function leftoverRank(item) {
  const ranks = { large: 3, useful: 2, small: 1, none: 0 };
  return ranks[leftoverState(item).kind] || 0;
}

// Build a Cook-guide step list for an IMPORTED recipe from the user's own saved
// data. Never invents steps — uses exactly the saved instructions. Shape matches
// what the cook-guide modal renders for catalog meals.
export function buildImportedCookSteps(recipe) {
  const ings = Array.isArray(recipe?.ingredients) ? recipe.ingredients : [];
  const items = ings
    .map(i => ({
      section: (i && i.section) || null,
      name:    importedRecipeDisplayName(i),
      // Imported recipe rows must display the saved recipe quantity ("1 lime",
      // "½ onion", "2 cloves"), not the gram estimate used for pack matching.
      amt:     null,
      unit:    null,
      measure: displayIngredientQtyUnit(i),
    }))
    .filter(it => it.name);

  // Group the gather list by ingredient sub-section ("First Marination",
  // "For the sauce") when the saved recipe has them, so prep mirrors the source.
  // Recipes with no sections keep a single "Ingredients" group (unchanged).
  const hasSections = items.some(it => it.section);
  let categories = [];
  if (items.length && !hasSections) {
    categories = [{ key: "all", label: "Ingredients", items }];
  } else if (items.length) {
    let cur = null;
    items.forEach(it => {
      const label = it.section || "Ingredients";
      if (!cur || cur.label !== label) {
        cur = { key: `sec-${categories.length}`, label, items: [] };
        categories.push(cur);
      }
      cur.items.push(it);
    });
  }

  const gather = {
    icon:  "🧺",
    title: "Gather your ingredients",
    categories,
    desc:  items.length ? "" : "No ingredients were saved for this recipe.",
  };

  const steps = Array.isArray(recipe?.instructions)
    ? recipe.instructions.filter(s => typeof s === "string" && s.trim())
    : [];

  if (steps.length === 0) {
    // Requirement 7: friendly fallback, but still show the gather list.
    return [gather, { title: "No cooking steps were saved for this recipe.", desc: "You can still gather everything above and cook from the original source." }];
  }

  return [gather, ...steps.map(text => ({ title: text }))];
}

// ── Cook-guide ingredient highlighting ──────────────────────────────────────
// Display-only helpers. They derive a conservative ingredient vocabulary from
// the cook-guide steps (the gather step's categorised items) and highlight
// those names inside step instruction text. They NEVER touch canonical
// ingredient data, shopping, matching, or pricing — pure presentation.
function _escapeRe(s) { return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }

// Strip a trailing plural so a plural vocab term ("tomatoes", "cashews") can
// match a singular mention in step text ("tomato", "cashew") and vice-versa.
// Conservative: only strips when ≥3 letters remain (keeps "gas", "peas-ish").
function _depluralize(w) {
  const m = /(?:es|s)$/i.exec(w);
  return (m && w.length - m[0].length >= 3) ? w.slice(0, -m[0].length) : w;
}
// Head nouns too generic to match on their own (a step saying "add the sauce"
// must not light up "soy sauce"). The full phrase still matches.
const _GENERIC_HEADS = new Set(["sauce", "paste", "powder", "puree", "purée", "stock", "broth", "water", "mix", "seasoning", "seed", "seeds", "leaf", "leaves", "piece", "pieces"]);

// Match stems for one vocab name: the full pre-notes phrase, plus the head noun
// for adjective-prefixed names ("raw cashews" → "cashew", "canned tomatoes" →
// "tomato") so the bare ingredient still highlights mid-instruction. All
// de-pluralised; the regex re-adds an optional plural when matching.
function _nameStems(name) {
  const head = String(name).split(",")[0].trim().toLowerCase();
  if (!head) return [];
  const stems = new Set();
  const whole = _depluralize(head);
  if (whole.length >= 3) stems.add(whole);
  const words = head.split(/\s+/);
  if (words.length > 1) {
    const lastRaw = words[words.length - 1];
    const last = _depluralize(lastRaw);
    if (last.length >= 3 && !_GENERIC_HEADS.has(lastRaw) && !_GENERIC_HEADS.has(last)) stems.add(last);
  }
  return Array.from(stems);
}

// Highlightable ingredient names pulled from the gather step's categorised
// items. De-duped, lowercased, sorted longest-first so multi-word names match
// before their sub-words ("spring onion" before "onion").
export function buildCookVocab(steps) {
  const names = new Set();
  (Array.isArray(steps) ? steps : []).forEach(s => {
    (s && Array.isArray(s.categories) ? s.categories : []).forEach(g => {
      (g && Array.isArray(g.items) ? g.items : []).forEach(it => {
        const n = (it && it.name ? String(it.name) : "").trim().toLowerCase();
        // Skip very short or non-alpha tokens to avoid noisy highlights.
        if (n.length >= 3 && /[a-z]/.test(n)) names.add(n);
      });
    });
  });
  return Array.from(names).sort((a, b) => b.length - a.length);
}

// Split step text into segments, flagging spans that name a known ingredient.
// Word-boundaried, case-insensitive, plural-tolerant in both directions, and
// adjective-prefix tolerant (see _nameStems). Pure: the component maps flagged
// segments to green (bold when active).
export function highlightSegments(text, vocab) {
  const str = typeof text === "string" ? text : "";
  const names = Array.isArray(vocab) ? vocab.filter(Boolean) : [];
  if (!str || names.length === 0) return [{ text: str, ingredient: false }];
  // Longest stems first so multi-word names win over their head nouns.
  const stems = Array.from(new Set(names.flatMap(_nameStems))).sort((a, b) => b.length - a.length);
  if (stems.length === 0) return [{ text: str, ingredient: false }];
  const re = new RegExp(`\\b(?:${stems.map(_escapeRe).join("|")})(?:es|s)?\\b`, "ig");
  const out = [];
  let last = 0, m;
  while ((m = re.exec(str)) !== null) {
    if (m.index > last) out.push({ text: str.slice(last, m.index), ingredient: false });
    out.push({ text: m[0], ingredient: true });
    last = m.index + m[0].length;
    if (re.lastIndex === m.index) re.lastIndex++; // guard against zero-width loop
  }
  if (last < str.length) out.push({ text: str.slice(last), ingredient: false });
  return out;
}

// Canonical vocab names that appear in a step's text — drives the "Needed now"
// list. A name matches if any of its stems (full phrase or head noun) is found.
// Returns the original vocab names in the vocab's longest-first order.
export function ingredientsInStep(text, vocab) {
  const str = (typeof text === "string" ? text : "").toLowerCase();
  if (!str) return [];
  return (Array.isArray(vocab) ? vocab : []).filter(n =>
    _nameStems(n).some(s => new RegExp(`\\b${_escapeRe(s)}(?:es|s)?\\b`, "i").test(str))
  );
}

// Client-side ingredient-use efficiency — mirrors server calcEfficiency so the
// badge re-flows when the user rounds down / buys an extra pack (changes packs).
// Skips count-unit items (eggs, lemons) which distort gram-based scoring.
export function computeEfficiency(shoppingList) {
  let used = 0, purchased = 0;
  for (const item of Object.values(shoppingList || {})) {
    if (item.unit === "count") continue;
    if (item.pantryStaple) continue; // buy-if-needed staples: needed 0 by design, not waste
    purchased += (Number(item.packs) || 0) * (Number(item.size) || 0);
    used      += Number(item.needed) || 0;
  }
  return purchased > 0 ? Math.round((used / purchased) * 100) : 100;
}

export function computeWasteTips(shoppingList) {
  const items = Object.values(shoppingList)
    .filter(i => i.leftover > 0 && !i.pantried)
    .sort((a, b) => {
      const priorityDelta = wastePriority(a) - wastePriority(b);
      if (priorityDelta !== 0) return priorityDelta;
      return (leftoverValue(b) - leftoverValue(a))
        || (leftoverRank(b) - leftoverRank(a))
        || ((Number(b.leftover) || 0) - (Number(a.leftover) || 0));
    });
  if (items.length === 0) return [];
  const tips = [];
  const seenTips = new Set();

  for (const item of items) {
    const nameLower = item.name.toLowerCase();
    let tip = "";
    for (const [key, candidateTip] of Object.entries(INGREDIENT_TIPS)) {
      if (nameLower.includes(key)) {
        tip = candidateTip;
        break;
      }
    }
    if (!tip) tip = `Use leftover ${item.name.toLowerCase()} soon.`;

    const tipKey = normalizeIngredientKey(tip);
    if (seenTips.has(tipKey)) continue;
    seenTips.add(tipKey);
    tips.push(tip);
    if (tips.length >= 5) break;
  }

  return tips;
}

export function computePerishableSwaps(meals = []) {
  const seen = new Set();
  const swaps = [];
  for (const meal of meals) {
    for (const ing of (meal?.ingredients || [])) {
      const sw = ing?.optional_swap;
      if (!sw?.perishable) continue;
      const key = ing.name.toLowerCase().trim();
      if (seen.has(key)) continue;
      seen.add(key);
      swaps.push({ from: ing.name, to: sw.to });
    }
  }
  return swaps;
}

export function planningStatus({ targetMealsPerWeek, selectedCount, scheduledCount, unscheduledCount }) {
  const target = Math.max(1, Number(targetMealsPerWeek) || 1);
  const selected = Math.max(0, Number(selectedCount) || 0);
  const scheduled = Math.max(0, Number(scheduledCount) || 0);
  const unscheduled = Math.max(0, Number(unscheduledCount) || 0);

  if (selected === 0 && scheduled === 0) {
    return {
      title: "No meals planned yet.",
      detail: "Head to the Meals tab to start building your week.",
      complete: false,
    };
  }
  if (unscheduled > 0) {
    return {
      title: `${unscheduled} meal${unscheduled === 1 ? "" : "s"} still need${unscheduled === 1 ? "s" : ""} a day.`,
      detail: `${scheduled}/${target} meals planned · schedule the rest or review your list any time.`,
      complete: false,
    };
  }
  if (selected >= target && scheduled >= target) {
    return {
      title: "All set — review your shopping list.",
      detail: "Your target meals are planned and every selected meal has a day.",
      complete: true,
    };
  }
  if (scheduled === selected) {
    return {
      title: `${scheduled} meal${scheduled === 1 ? "" : "s"} scheduled · ready to shop for these, or add more meals.`,
      detail: `${selected}/${target} meals planned · add more meals to complete your week.`,
      complete: false,
    };
  }
  return {
    title: `${scheduled}/${target} meals planned · add more meals to complete your week.`,
    detail: `${selected} meal${selected === 1 ? "" : "s"} selected.`,
    complete: false,
  };
}

export function formatMealTime(time) {
  const mins = Number(time);
  if (!Number.isFinite(mins) || mins <= 0) return null;
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const remainder = mins % 60;
  const hourText = `${hours} hr`;
  return remainder > 0 ? `~${hourText} ${remainder} min` : `~${hourText}`;
}

export function mealMetaParts({ cuisineLabel, time, cals, servings }) {
  return [
    cuisineLabel,
    formatMealTime(time),
    Number.isFinite(Number(servings)) && Number(servings) > 0 ? `${Number(servings)} serving${Number(servings) === 1 ? "" : "s"}` : null,
    Number.isFinite(Number(cals)) && Number(cals) > 0 ? `🔥 ~${Number(cals)}` : null,
  ].filter(Boolean);
}

export function plannedServingCount({ id, servingMap = {}, effectiveServings = {}, people, fallback } = {}) {
  const value = (id && servingMap[id] !== undefined)
    ? servingMap[id]
    : (id && effectiveServings[id] !== undefined)
      ? effectiveServings[id]
      : people !== undefined
        ? people
        : fallback;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/* ═══════════════════════════════════════════
   MAIN APP COMPONENT
═══════════════════════════════════════════ */
/**
 * resolveInitialAuth — synchronous auth-scope resolution, run once at mount.
 *
 * Consumes an auth JWT from the URL fragment FIRST, then returns "loading" so
 * the server can confirm the token and beta approval before AppBody reads saved
 * localStorage. Order: JWT → loading; active Tasting session → tasting; else
 * unauthenticated.
 *
 *   #jwt= present ─┐
 *   getJwt()       ├─► "loading"         (no prefs storage yet)
 *   tasting flag   ──► "tasting"         (sessionStorage only)
 *   else           ──► "unauthenticated" (BetaEntry; no app, no storage)
 */
function resolveInitialAuth() {
  api.consumeJwtFromUrl();                 // capture #jwt= synchronously, strip fragment
  api.consumeAuthErrorFromUrl();
  if (api.getJwt()) return "loading";
  if (prefsStore.hasTastingSession()) return "tasting";
  return "unauthenticated";
}

/**
 * App — thin auth shell. Owns authStatus, routes prefsStore to the matching
 * storage BEFORE AppBody renders, and remounts AppBody on every auth transition
 * (key={authStatus}) so a prior user's in-memory state never survives a switch.
 * clearLocal/clearSession wipe disk; this remount wipes RAM.
 */
export default function App() {
  const [authStatus, setAuthStatus] = useState(resolveInitialAuth);
  const [authConfig, setAuthConfig] = useState({ betaMode: true, providers: { google: true, apple: true } });
  // showAuthOverlay lives here (not in AppBody) so the Tasting banner — rendered
  // ABOVE AppBody so it shows over onboarding AND the planner, both of which
  // early-return their own full screens — can flip it.
  const [showAuthOverlay, setShowAuthOverlay] = useState(false);
  prefsStore.setMode(authStatus);          // route storage before AppBody's initializers run

  useEffect(() => {
    const ctrl = new AbortController();
    api.getAuthConfig({ signal: ctrl.signal })
      .then(config => {
        const next = {
          betaMode: config?.betaMode !== false,
          providers: {
            google: config?.providers?.google !== false,
            apple:  config?.providers?.apple !== false,
          },
        };
        setAuthConfig(prev => (
          prev.betaMode === next.betaMode &&
          prev.providers?.google === next.providers.google &&
          prev.providers?.apple === next.providers.apple
            ? prev
            : next
        ));
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    if (authStatus !== "loading") return;
    const ctrl = new AbortController();
    api.getAuthSession({ signal: ctrl.signal })
      .then(session => {
        if (session?.state === "authenticated") {
          prefsStore.endTastingSession();
          setShowAuthOverlay(false);
          setAuthStatus("authenticated");
        } else if (session?.state === "beta_pending") {
          prefsStore.endTastingSession();
          setShowAuthOverlay(false);
          setAuthStatus("beta_pending");
        } else {
          api.clearJwt();
          setAuthStatus(prefsStore.hasTastingSession() ? "tasting" : "unauthenticated");
        }
      })
      .catch(err => {
        if (err?.name === "AbortError") return;   // effect cleanup (StrictMode/remount) — not a rejected session
        api.clearJwt();
        setAuthStatus("unauthenticated");
      });
    return () => ctrl.abort();
  }, [authStatus]);

  const exitTasting = () => {
    prefsStore.endTastingSession();
    setShowAuthOverlay(false);
    setAuthStatus("unauthenticated");
  };

  const logout = () => {
    api.clearJwt();
    prefsStore.clearLocal();
    setShowAuthOverlay(false);
    setAuthStatus("unauthenticated");
  };

  const claimBetaCode = async (code) => {
    const result = await api.claimBetaCode(code);
    if (result?.state !== "authenticated") {
      throw new Error("Beta approval required.");
    }
    prefsStore.endTastingSession();
    setShowAuthOverlay(false);
    setAuthStatus("authenticated");
  };

  if (authStatus === "loading") {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(160deg, ${C.greenDark} 0%, ${C.green} 55%, ${C.greenLight} 100%)`,
        color: C.white,
        fontWeight: 800,
      }}>
        Loading NomLoop...
      </div>
    );
  }

  if (authStatus === "beta_pending") {
    return (
      <BetaCodeGate
        C={C}
        logo={<BrandLogo size={64} radius={18} style={{ marginBottom: 16 }} />}
        onSubmit={claimBetaCode}
        onLogout={logout}
      />
    );
  }

  return (
    <>
      {authStatus === "tasting" && !showAuthOverlay && (
        <TastingBanner onLogin={() => setShowAuthOverlay(true)} onExit={exitTasting} />
      )}
      <AppBody
        key={authStatus}
        authStatus={authStatus}
        setAuthStatus={setAuthStatus}
        authConfig={authConfig}
        showAuthOverlay={showAuthOverlay}
        setShowAuthOverlay={setShowAuthOverlay}
      />
    </>
  );
}

function AppBody({ authStatus, setAuthStatus, authConfig, showAuthOverlay, setShowAuthOverlay }) {
  // Onboarding state. All persisted slots route through prefsStore, which the
  // App shell has already pointed at the correct storage for authStatus.
  const [step, setStep] = useState(() => prefsStore.read("nomloop.step", 0));
  // Hydrate every onboarding pref from the active store so Preferences reflects
  // saved choices after reload.
  const lsRead = (k, fallback) => prefsStore.read(k, fallback);
  const [people, setPeople] = useState(() => lsRead("nomloop.people", 2));
  const [diet, setDiet] = useState(() => lsRead("nomloop.diet", "classic"));
  const [allergies, setAllergies] = useState(() => lsRead("nomloop.allergies", []));
  const [dislikes, setDislikes] = useState(() => lsRead("nomloop.dislikes", []));
  const [selectedCuisines, setSelectedCuisines] = useState(() => lsRead("nomloop.cuisines", []));
  const [mealTarget, setMealTarget] = useState(() => lsRead("nomloop.mealTarget", 5));
  const [budget, setBudget] = useState(() => lsRead("nomloop.budget", 60));

  // App state
  const [activeTab, setActiveTab] = useState("meals");
  const [selectedMeals, setSelectedMeals] = useState(() => lsRead("nomloop.selectedMeals", []));
  const [servingMap, setServingMap] = useState({});
  const [weekPlan, setWeekPlan] = useState(() => lsRead("nomloop.weekPlan", {}));
  const [detailId, setDetailId] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const [detailAttempt, setDetailAttempt] = useState(0);
  const [imagePreview, setImagePreview] = useState(null);
  // Settings modal — separate from the onboarding step machine. Tapping
  // ⚙️ used to call setStep(1), which dropped users back into onboarding.
  // Now ⚙️ flips this flag and SettingsModal handles the edit flow.
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Reassign-modal state for the week tab — tapping a placed meal opens a
  // small chooser to move it to a different day instead of forcing remove +
  // re-pick. Stores the day key being reassigned (null when closed).
  const [reassignDay, setReassignDay] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  // Multi-select tag filter. Each chip toggles independently; filtering
  // applies AND across all active tags. Stored as an array for predictable
  // useMemo deps (Sets compare by reference).
  const [tagFilters, setTagFilters] = useState([]);
  const [copied, setCopied] = useState(false);
  // Onboarding empty-state gate: true while /generate is in-flight, toggled
  // to warn when step 7 "Start planning →" is tapped but allMeals is empty.
  const [mealsLoading, setMealsLoading] = useState(false);
  const [onboardEmpty, setOnboardEmpty] = useState(false);
  // Undo recent meal removal(s). Array shape supports both single removes
  // and bulk removes (conflict-banner "Remove all"). 3.5 s window.
  const [undoMeals, setUndoMeals] = useState(null); // Array<{ id, name, day }> | null
  const undoMealsRef = useRef(null);
  const [justFilledIds, setJustFilledIds] = useState([]); // IDs of meals added by last autofill
  const [autofillUndo, setAutofillUndo] = useState(null); // { ids, count } for autofill undo pill
  const autofillUndoRef = useRef(null);
  const autofillUndoTimer = useRef(null);
  const undoTimerRef = useRef(null);
  const undoExpiresAtRef = useRef(0);
  const undoRemainingMsRef = useRef(MEAL_REMOVE_UNDO_MS);
  const selectedMealsRef = useRef(selectedMeals);
  const weekPlanRef = useRef(weekPlan);
  const servingMapRef = useRef(servingMap);
  // Confirm-before-clear: first click arms, second click executes.
  const [clearConfirm, setClearConfirm] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);

  useEffect(() => { selectedMealsRef.current = selectedMeals; }, [selectedMeals]);
  useEffect(() => { weekPlanRef.current = weekPlan; }, [weekPlan]);
  useEffect(() => { servingMapRef.current = servingMap; }, [servingMap]);
  useEffect(() => { undoMealsRef.current = undoMeals; }, [undoMeals]);
  useEffect(() => { autofillUndoRef.current = autofillUndo; }, [autofillUndo]);

  useEffect(() => {
    if (!imagePreview || typeof window === "undefined") return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setImagePreview(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [imagePreview]);

  // Post-checkout cooking mode. Flips true when the user taps "I've bought
  // these" on the shopping list. When true, a fourth "Cook" tab appears
  // with today's meal surfaced prominently. We reset this any time the
  // meal selection changes — a new week's shop needs a new confirmation.
  const [checkedOut, setCheckedOut] = useState(() => lsRead("nomloop.checkedOut", false));
  // Pre-checkout pack-deficit gate: true when the user tapped "I've bought these"
  // but the list contains rounded-down items. Shows confirmation UI before committing.
  const [showPackDeficit, setShowPackDeficit] = useState(false);

  // Toggle "Used in: Recipe" provenance lines on shopping list items.
  const [showUsedIn, setShowUsedIn] = useState(() => lsRead("nomloop.showUsedIn", true));

  // Lifetime checkout counter — unlocks the cooking-reel video feature after
  // 10 confirmed shops. Persisted to localStorage so it survives reloads.
  // There is no server-side user profile yet; when there is, we'll migrate.
  const VIDEO_UNLOCK_THRESHOLD = 10;
  const [checkoutCount, setCheckoutCount] = useState(() => prefsStore.read("nomloop.checkoutCount", 0));
  const videoUnlocked = checkoutCount >= VIDEO_UNLOCK_THRESHOLD;

  // Recipe import (Phase 2)
  const [importSheetOpen, setImportSheetOpen]   = useState(false);
  const [recipeLibrary, setRecipeLibrary]         = useState([]);
  const [recentlySavedRecipeIds, setRecentlySavedRecipeIds] = useState(() => new Set());
  const [recipeDetailId, setRecipeDetailId]       = useState(null);
  const [recipeDetail, setRecipeDetail]           = useState(null);
  const [recipeDetailLoading, setRecipeDetailLoading] = useState(false);
  // Imported recipe ingredient measurement display mode: 'original'|'imperial'|'metric'.
  // Display-only — never changes stored ingredients, shopping, matching or pricing.
  const [measureMode, setMeasureMode]             = useState("original");
  const recipeDetailIdRef = useRef(null);   // tracks the open recipe so stale fetches don't clobber a newer open
  const [deleteConfirmId, setDeleteConfirmId]     = useState(null);
  // Cache imported recipe ingredient arrays (recipeId → parsed ingredient objects).
  // Populated when user adds an imported recipe to the week (detail sheet open).
  // Also prefetched on mount for any imported IDs already in selectedMeals.
  const [importedIngredientCache, setImportedIngredientCache] = useState(() => new Map());
  const [recipeLibraryIngredientCache, setRecipeLibraryIngredientCache] = useState(() => new Map());
  const importedIngredientCacheRef = useRef(importedIngredientCache);
  useEffect(() => { importedIngredientCacheRef.current = importedIngredientCache; }, [importedIngredientCache]);
  // Results from /api/shopping-list/match-imported
  const [importedItems, setImportedItems] = useState({ matched: [], manualItems: [] });
  // Server-driven state
  // allMeals is the current filtered safe-shape list from /api/plan/generate.
  // mealsCache persists every safe-shape meal we've seen across filter changes
  // so previously-selected meals stay resolvable even if the user narrows filters.
  const [allMeals, setAllMeals] = useState([]);
  // mealsCache hydration from localStorage: meals previously selected are
  // restored on mount so display names ("Sweet & Sour Pork") and metadata
  // (servings target, cuisine) are available before /api/plan/generate returns.
  // Without this, mealName() falls back to TITLE_CASE("chi_sweet_sour_pork")
  // and renders as "Chi Sweet Sour Pork" on reload.
  const [mealsCache, setMealsCache] = useState(() => {
    const arr = lsRead("nomloop.selectedMealsCache", []);
    const entries = Array.isArray(arr)
      ? arr.map(withMealImage).filter(m => m?.id).map(m => [m.id, m])
      : [];
    return new Map(entries);
  });
  const [mealDetails, setMealDetails] = useState(() => new Map()); // id → full meal w/ ingredients
  const [shoppingList, setShoppingList] = useState({});            // server-computed UNIFIED map (catalog + imported merged by PKG id)
  // Per-item pack choice ({ [itemId]: "roundDown" | "buyExtra" }) — durable so it
  // survives every rebuild and is re-applied to each fresh list (see build effect).
  const [roundDownChoices, setRoundDownChoices] = useState(() => lsRead("nomloop.roundDownChoices", {}));
  const roundDownChoicesRef = useRef(roundDownChoices);
  useEffect(() => {
    roundDownChoicesRef.current = roundDownChoices;
    prefsStore.write("nomloop.roundDownChoices", roundDownChoices);
  }, [roundDownChoices]);
  const [buildManualItems, setBuildManualItems] = useState([]);    // imported ingredients with no PKG match, from /build
  const [efficiency, setEfficiency] = useState(100);
  const [swapSuggestions, setSwapSuggestions] = useState([]);
  // Ingredient waste-swap suggestions (up to 2 per build, from server).
  const [ingredientSwapSuggestions, setIngredientSwapSuggestions] = useState([]);
  // Plan-scoped applied swaps overlay — persisted so every rebuild carries them forward.
  const [appliedSwaps, setAppliedSwaps] = useState(() => lsRead("nomloop.appliedSwaps", []));
  const appliedSwapsRef = useRef(appliedSwaps);
  useEffect(() => {
    appliedSwapsRef.current = appliedSwaps;
    prefsStore.write("nomloop.appliedSwaps", appliedSwaps);
  }, [appliedSwaps]);
  // Fingerprints the user dismissed this session — not shown again until plan changes.
  const [dismissedSwapFps, setDismissedSwapFps] = useState(() => lsRead("nomloop.dismissedSwapFps", []));
  useEffect(() => { prefsStore.write("nomloop.dismissedSwapFps", dismissedSwapFps); }, [dismissedSwapFps]);
  // Pantry: Set<itemKey> for optimistic display. Keys: "pkg:<id>" or "manual:<origName>".
  const [pantryItems, setPantryItems] = useState(() => new Set(lsRead("nomloop.pantryItems", [])));
  const [availableRetailers, setAvailableRetailers] = useState([]);
  const [retailerTier, setRetailerTier] = useState(null);
  const [retailerId, setRetailerId] = useState("tesco");
  const retailerOptions = useMemo(
    () => retailerOptionsForDisplay(availableRetailers, retailerId),
    [availableRetailers, retailerId]
  );
  const [organicMode, setOrganicMode] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Cook-guide modal state.
  // The guide itself (6 steps + categorised ingredient groups) is computed
  // server-side by /api/cook-guide. We only hold the display copy here.
  const [cookGuideMealId, setCookGuideMealId] = useState(null);
  const [cookGuideSteps,  setCookGuideSteps]  = useState(null);
  const [cookGuideLoading, setCookGuideLoading] = useState(false);
  const [cookGuideError, setCookGuideError]   = useState(null);
  const [cookStepIdx, setCookStepIdx]         = useState(0);     // active instruction step in the guide navigator
  const [cookSwitchOpen, setCookSwitchOpen]   = useState(false); // "switch meal" menu open
  const cookGuideMealIdRef = useRef(null);   // latest opened cook-guide id; guards stale imported fetches
  const savedPlanHydrated = useRef(false);
  // Profile hydration guard — saveProfile/savePlan stay disarmed until BOTH the
  // profile and the saved plan have loaded, so hydration can't echo just-fetched
  // values back to the server (the hydrate→persist feedback loop).
  const profileHydrated = useRef(false);

  // Auto-fill state. Scoring runs server-side (/api/plan/autofill) so the
  // client just tracks the in-flight request and surfaces errors/toasts.
  const [autofillLoading, setAutofillLoading] = useState(false);
  const [autofillError,   setAutofillError]   = useState(null);

  // Cuisine pair hints (editorial labels like "Classic combo"). Keyed by the
  // first-selected cuisine so we don't re-fetch when the user deselects/re-picks
  // the same anchor. Fetched lazily on step 5 once exactly one cuisine is chosen.
  const [pairHints, setPairHints] = useState({});          // { cuisineId: label }

  // Drink pairings for the detail modal. Keyed by cuisine id so we only fetch
  // each cuisine once per session.
  const [drinkPairings, setDrinkPairings] = useState({});  // { cuisineId: [pairing, ...] }

  const { toasts, show } = useToast();
  const { consent, accept: acceptCookies, reject: rejectCookies, reset: resetCookies } = useCookieConsent();
  const TOTAL_STEPS = 8;

  // Auth view sub-state (authStatus itself comes from the App shell via props).
  //  entryView:       which unauthenticated screen shows — the two-path BetaEntry
  //                   ("entry"), or the email/password/invite form ("auth").
  //  showAuthOverlay: a Tasting user tapped "Log in to save" — render the auth
  //                   form over the app WITHOUT ending the tasting session (they
  //                   may cancel). A successful login remounts into authenticated.
  const [entryView, setEntryView] = useState("entry");        // "entry" | "auth"

  // Sign-in wall state (shown when no JWT).
  const [signinEmail,        setSigninEmail]        = useState("");
  const [signinPassword,     setSigninPassword]     = useState("");
  const [signinCode,         setSigninCode]         = useState("");
  const [signinSent,         setSigninSent]         = useState(false);
  const [signinLoading,      setSigninLoading]      = useState(false);
  const [signinError,        setSigninError]        = useState(null);
  const [authMode,           setAuthMode]           = useState("signin"); // "signin" | "signup"
  const [flippedFromSignin,  setFlippedFromSignin]  = useState(false);    // true when 422 auto-routed to signup
  const [setupToken] = useState(() => api.getSetPasswordTokenFromUrl());
  const [setupPassword, setSetupPassword] = useState("");
  const [setupConfirm, setSetupConfirm] = useState("");
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState(null);

  // "Request a style" CTA state (empty meals state).
  const [styleReqStyle,   setStyleReqStyle]   = useState("");
  const [styleReqEmail,   setStyleReqEmail]   = useState("");
  const [styleReqSent,    setStyleReqSent]    = useState(false);
  const [styleReqLoading, setStyleReqLoading] = useState(false);

  /* ── Diet badge helpers ─── */
  const DIET_LABELS = { vegan:"Vegan", vegetarian:"Vegetarian", pescatarian:"Pescatarian", flexitarian:"Flexitarian", paleo:"Paleo", lowcarb:"Low Carb", keto:"Keto" };
  const DIET_EMOJIS = { vegan:"🌱", vegetarian:"🥗", pescatarian:"🐟", flexitarian:"🌿", paleo:"🥩", lowcarb:"📉", keto:"🥑" };
  const dietBadge = (diets = []) => {
    if (!diets || diets.includes("classic")) return null;
    const d = diets.find(x => DIET_LABELS[x]);
    return d ? { label: DIET_LABELS[d], emoji: DIET_EMOJIS[d] } : null;
  };

  /* ── Derived state (pure, from server data) ─── */
  // Fast id → safe-meal lookup across everything we've seen.
  const mealsById = mealsCache;

  const selectedMealObjs = useMemo(
    () => selectedMeals.map(id => mealsById.get(id)).filter(Boolean),
    [selectedMeals, mealsById],
  );

  // The shopping list builds from EVERY meal in the plan — both meals the user
  // selected AND any meal scheduled in the Week tab (weekPlan). These two sets
  // are normally identical, but a cleared/re-added or cross-device session can
  // leave a meal scheduled in weekPlan that never made it back into
  // selectedMeals. Reading only selectedMeals there left the basket empty even
  // though the Week tab clearly showed scheduled meals. Union keeps them in sync.
  const planMealIds = useMemo(
    () => Array.from(new Set([...selectedMeals, ...Object.values(weekPlan).filter(Boolean)])),
    [selectedMeals, weekPlan],
  );

  // Derive from selectedMeals IDs directly — not selectedMealObjs — so that
  // a fresh page load (mealsCache empty until /api/plan/generate returns)
  // still produces a complete servings map. Without this, the shopping list
  // builds with {} servings and renders £0.00 until the cache hydrates.
  const effectiveServings = useMemo(() => {
    const m = {};
    planMealIds.forEach(id => { m[id] = servingMap[id] || people; });
    return m;
  }, [planMealIds, servingMap, people]);

  function changePeople(nextPeople) {
    const value = Math.max(1, Math.min(12, Number(nextPeople) || people));
    setServingMap(prev => {
      let changed = false;
      const next = { ...prev };
      for (const mealId of selectedMeals) {
        if (next[mealId] === people) {
          next[mealId] = value;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setPeople(value);
    if (selectedMeals.length > 0) setCheckedOut(false);
  }

  const basketTotals = useMemo(
    () => computeBasketTotals({ shoppingList, manualItems: buildManualItems, pantryItems }),
    [shoppingList, buildManualItems, pantryItems],
  );

  // PKG id → imported match (alternatives + origNames) for re-attaching swap
  // chips to the merged rows in the unified list.
  const importedById = useMemo(
    () => new Map((importedItems.matched || []).map(m => [m.id, m])),
    [importedItems],
  );

  // Items where pack-rounding left us slightly under the needed quantity.
  // Each entry: { id, name, packs, size, needed, shortfall, suggestedPacks }
  const roundedDownItems = useMemo(
    () => Object.values(shoppingList)
      .filter(i => i.roundedDown)
      .map(i => ({
        id:            i.id,
        name:          i.name,
        packs:         i.packs,
        size:          i.size,
        unit:          i.unit,
        needed:        i.needed,
        shortfall:     i.needed - i.packs * i.size,
        suggestedPacks: i.packs + 1,
      })),
    [shoppingList],
  );

  // Waste tips — derived from the current shopping list only.
  // Only shows tips for ingredients with leftover > 0; never references
  // imported recipe ingredients or unscheduled meals.
  const wasteTips = useMemo(() => computeWasteTips(shoppingList), [shoppingList]);
  const perishableSwaps = useMemo(() => computePerishableSwaps(selectedMealObjs), [selectedMealObjs]);
  // Recompute efficiency from the live (choice-applied) list so the waste badge
  // updates when the user rounds down / buys an extra pack. Falls back to the
  // server value only when the list is empty.
  const displayEfficiency = useMemo(
    () => Object.keys(shoppingList).length ? computeEfficiency(shoppingList) : efficiency,
    [shoppingList, efficiency],
  );

  const displayCost = basketTotals.ownedCount > 0 ? basketTotals.payableCost : basketTotals.fullCost;
  const displayCostValue = basketTotals.ownedCount > 0 ? basketTotals.payableValue : parseFloat(basketTotals.fullCost || 0);
  const isOverBudget = displayCostValue > budget;

  // Cuisine diet notes
  const cuisineDietNotes = useMemo(() => {
    const notes = [];
    for (const cid of selectedCuisines) {
      const noteMap = CUISINE_DIET_NOTES[cid];
      if (noteMap && noteMap[diet]) notes.push({ cuisine: cid, note: noteMap[diet] });
    }
    return notes;
  }, [selectedCuisines, diet]);

  // Allergen/diet conflicts for already-planned meals.
  // Runs synchronously on every pref change — no extra fetch needed.
  // Imported recipe IDs (UUIDs) are skipped: no allergen metadata available.
  const planConflicts = useMemo(() => {
    if (!allergies.length && (!diet || diet === "classic")) return [];
    const conflicts = [];
    for (const id of selectedMeals) {
      if (isImportedId(id)) continue;
      const meal = mealsCache.get(id);
      if (!meal) continue;
      const reasons = [];
      if (allergies.length > 0) {
        const hits = (meal.allergens || []).filter(a => allergies.includes(a));
        if (hits.length > 0) reasons.push(`contains ${hits.join(", ")}`);
      }
      if (diet && diet !== "classic") {
        const dietOk = diet === "lowcarb"
          ? meal.diets?.includes("lowcarb") || meal.diets?.includes("keto")
          : meal.diets?.includes(diet);
        if (!dietOk) reasons.push(`not ${diet}`);
      }
      if (reasons.length > 0) conflicts.push({ id, name: meal.name, reasons });
    }
    return conflicts;
  }, [selectedMeals, allergies, diet, mealsCache]);

  // Client-only filters the server doesn't know about (tag + free-text search).
  const filteredMeals = useMemo(() => {
    return allMeals.map(withMealImage).filter(meal => {
      // AND logic across all active tag filters.
      if (tagFilters.length > 0 && !tagFilters.every(t => meal.tags.includes(t))) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!meal.name.toLowerCase().includes(q) && !meal.cuisine.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [allMeals, tagFilters, searchQuery]);

  const filteredRecipeLibrary = useMemo(() => {
    return recipeLibrary.filter(recipe => {
      // Cuisine preferences are catalog-only — never hide a user's own imported recipe.
      // Tag filters and search are user-explicit actions so they do apply here.
      if (tagFilters.length > 0 && !tagFilters.every(t => importedRecipeMatchesTag(recipe, t))) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const text = [
          recipe.title,
          recipe.source_domain,
          recipe.cuisine ? cuisineLabel(recipe.cuisine) : "",
        ].filter(Boolean).join(" ").toLowerCase();
        if (!text.includes(q)) return false;
      }
      return true;
    });
  }, [recipeLibrary, tagFilters, searchQuery]);

  const selectedIngredientKeys = useMemo(() => {
    const keys = new Set();
    for (const id of selectedMeals) {
      const ingredients = isImportedId(id)
        ? (importedIngredientCache.get(id) || [])
        : (mealDetails.get(id)?.ingredients || []);
      ingredients.forEach(ingredient => {
        ingredientKeys(ingredient).forEach(key => keys.add(key));
      });
    }
    return keys;
  }, [selectedMeals, importedIngredientCache, mealDetails]);

  const recipeSharedCounts = useMemo(() => {
    const counts = new Map();
    if (selectedIngredientKeys.size === 0) return counts;
    for (const recipe of recipeLibrary) {
      if (selectedMeals.includes(recipe.id)) continue;
      const ingredients = recipeLibraryIngredientCache.get(recipe.id) || [];
      const sharedNames = new Set();
      ingredients.forEach(ingredient => {
        if (!ingredientOverlapsSelected(ingredient, selectedIngredientKeys)) return;
        const key = ingredientKeys(ingredient)[0];
        if (key) sharedNames.add(key);
      });
      if (sharedNames.size > 0) counts.set(recipe.id, sharedNames.size);
    }
    return counts;
  }, [recipeLibrary, recipeLibraryIngredientCache, selectedIngredientKeys, selectedMeals]);

  // Recommendations derive from the server's sharedIngredientCount signal.
  const recommendations = useMemo(() => {
    if (selectedMealObjs.length < 1) return [];
    const excluded = new Set(selectedMeals);
    return filteredMeals
      .filter(m => !excluded.has(m.id))
      .filter(m => typeof m.sharedIngredientCount === "number" && m.sharedIngredientCount > 0)
      .sort((a, b) => (b.sharedIngredientCount || 0) - (a.sharedIngredientCount || 0))
      .slice(0, 4);
  }, [filteredMeals, selectedMeals, selectedMealObjs.length]);

  // ── Persist all onboarding prefs (prefsStore routes to local/session/none) ────
  useEffect(() => { prefsStore.write("nomloop.people", people); }, [people]);
  useEffect(() => { prefsStore.write("nomloop.diet", diet); }, [diet]);
  useEffect(() => { prefsStore.write("nomloop.allergies", allergies); }, [allergies]);
  useEffect(() => { prefsStore.write("nomloop.dislikes", dislikes); }, [dislikes]);
  useEffect(() => { prefsStore.write("nomloop.cuisines", selectedCuisines); }, [selectedCuisines]);
  useEffect(() => { prefsStore.write("nomloop.mealTarget", mealTarget); }, [mealTarget]);
  useEffect(() => { prefsStore.write("nomloop.budget", budget); }, [budget]);
  useEffect(() => { prefsStore.write("nomloop.step", step); }, [step]);
  useEffect(() => { prefsStore.write("nomloop.selectedMeals", selectedMeals); }, [selectedMeals]);
  useEffect(() => { prefsStore.write("nomloop.weekPlan", weekPlan); }, [weekPlan]);
  useEffect(() => { prefsStore.write("nomloop.checkedOut", checkedOut); }, [checkedOut]);
  useEffect(() => { prefsStore.write("nomloop.showUsedIn", showUsedIn); }, [showUsedIn]);
  // Clear stale pack-deficit warning whenever the shopping list updates.
  useEffect(() => { setShowPackDeficit(false); }, [shoppingList]);
  useEffect(() => { prefsStore.write("nomloop.pantryItems", [...pantryItems]); }, [pantryItems]);
  // Persist the subset of mealsCache currently in the plan so reload doesn't
  // strip names/servings/cuisine off scheduled meals. Bounded by selectedMeals
  // (≤ mealTarget), never grows unboundedly.
  useEffect(() => {
    const slice = selectedMeals.map(id => mealsCache.get(id)).filter(Boolean);
    prefsStore.write("nomloop.selectedMealsCache", slice);
  }, [selectedMeals, mealsCache]);

  // (magic-link JWT is captured synchronously in resolveInitialAuth, before mount —
  //  so it can't race the BetaEntry gate or land tasting data in the account.)

  // Stable key for the matched imported-ingredient IDs. matchImported returns a
  // fresh array on every run (even with identical content), so depending on the
  // array ref directly would re-fire generatePlan → mealsCache → mealsById →
  // match-imported in a loop. Keying on the joined IDs only re-runs when the set
  // of biased ingredients actually changes.
  const importedMatchedIds = useMemo(() => importedItems.matched.map(i => i.id), [importedItems.matched]);
  const importedMatchedKey = useMemo(() => importedMatchedIds.join(","), [importedMatchedIds]);

  // ── API: fetch meal list when filters or selection change ────────────────────
  useEffect(() => {
    if (authStatus === "unauthenticated") return;  // entry screen is up — don't fetch
    const ctrl = new AbortController();
    setApiError(null);
    setMealsLoading(true);
    setOnboardEmpty(false);
    api.generatePlan({
      cuisines:              selectedCuisines,
      diet,
      allergens:             allergies,
      dislikes,
      selectedMealIds:       selectedMeals,
      selectedIngredientIds: importedMatchedIds,
      signal:                ctrl.signal,
    })
      .then(({ meals }) => {
        const mealsWithImages = (meals || []).map(withMealImage);
        setAllMeals(mealsWithImages);
        setMealsLoading(false);
        setMealsCache(prev => {
          const next = new Map(prev);
          for (const m of mealsWithImages) next.set(m.id, m);
          return next;
        });
      })
      .catch(err => {
        if (err.name === "AbortError") return;
        setMealsLoading(false);
        setApiError(err.message);
      });
    return () => ctrl.abort();
  // importedMatchedIds is derived from importedMatchedKey; keying on the string
  // avoids re-running on every fresh-but-identical matched array.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCuisines, diet, allergies, dislikes, selectedMeals, importedMatchedKey]);

  // ── API: load authenticated user data after login ──────────────────────────
  useEffect(() => {
    if (authStatus !== "authenticated" || !api.getJwt()) return;  // only real accounts sync
    const ctrl = new AbortController();

    api.listRecipeLibrary({ signal: ctrl.signal })
      .then(data => setRecipeLibrary(dedupeRecipeLibraryById(data.recipes || [])))
      .catch(() => {});         // 404 = feature not enabled for this user; ignore

    api.getPantry({ signal: ctrl.signal })
      .then(({ items }) => setPantryItems(new Set(items.map(i => i.itemKey))))
      .catch(() => {});

    // Profile prefs first, THEN the saved plan — the plan wins on people/retailer
    // because it resolves second. Both guards flip only after their fetch settles,
    // so the saveProfile/savePlan effects stay disarmed through hydration.
    api.getProfile({ signal: ctrl.signal })
      .then(({ profile }) => {
        if (!profile) return;
        if (profile.diet)                     setDiet(profile.diet);
        if (Array.isArray(profile.cuisines))  setSelectedCuisines(profile.cuisines);
        if (Array.isArray(profile.allergens)) setAllergies(profile.allergens);
        if (Array.isArray(profile.dislikes))  setDislikes(profile.dislikes);
        if (Number(profile.people) > 0)       setPeople(Number(profile.people));
        if (Number(profile.mealTarget) > 0)   setMealTarget(Number(profile.mealTarget));
        if (Number(profile.budget) > 0)       setBudget(Number(profile.budget));
        if (profile.retailerId)               setRetailerId(profile.retailerId);
      })
      .catch(() => {})
      .finally(() => { profileHydrated.current = true; })
      .then(() => api.getSavedPlan({ signal: ctrl.signal }))
      .then((res) => {
        const plan = res?.plan;
        if (!plan) return;
        const mealIds = (plan.meals || []).map(meal => meal.id).filter(Boolean);
        if (mealIds.length > 0) {
          const validIds = new Set(mealIds);
          setSelectedMeals(mealIds);
          setMealsCache(prev => {
            const next = new Map(prev);
            for (const meal of plan.meals || []) {
              const mealWithImage = withMealImage(meal);
              if (mealWithImage?.id) next.set(mealWithImage.id, mealWithImage);
            }
            return next;
          });
          if (plan.dayMap && typeof plan.dayMap === "object") {
            setWeekPlan(Object.fromEntries(
              Object.entries(plan.dayMap).filter(([, id]) => validIds.has(id))
            ));
          }
        }
        if (plan.retailerId) setRetailerId(plan.retailerId);
        if (Number(plan.people) > 0) setPeople(Number(plan.people));
        if (typeof plan.checkedOut === "boolean") setCheckedOut(plan.checkedOut);
        if (plan.roundDownChoices && typeof plan.roundDownChoices === "object") {
          setRoundDownChoices(plan.roundDownChoices);
        }
      })
      .catch(() => {})
      .finally(() => { savedPlanHydrated.current = true; });

    return () => ctrl.abort();
  }, [authStatus]);

  // Persist profile prefs to the backend (authenticated only, debounced 500ms,
  // and only AFTER hydration so we never echo just-loaded values back). Mirrors
  // the savePlan autosave. allergens=allergies maps client→server naming.
  useEffect(() => {
    if (authStatus !== "authenticated" || !api.getJwt()) return;
    if (!profileHydrated.current || !savedPlanHydrated.current) return;
    const timer = setTimeout(() => {
      api.saveProfile({
        diet,
        cuisines:  selectedCuisines,
        allergens: allergies,
        dislikes,
        people,
        mealTarget,
        budget,
      }).catch(err => console.warn("save-profile:", err.message));
    }, 500);
    return () => clearTimeout(timer);
  }, [authStatus, diet, selectedCuisines, allergies, dislikes, people, mealTarget, budget]);

  useEffect(() => {
    if (authStatus !== "authenticated" || !api.getJwt() || !savedPlanHydrated.current || selectedMeals.length === 0) return;
    const timer = setTimeout(() => {
      api.savePlan({
        mealIds: selectedMeals,
        dayMap: weekPlan,
        retailerId,
        people,
        totalCost: Number.isFinite(displayCostValue) ? displayCostValue : undefined,
        checkedOut,
        roundDownChoices,
      }).catch(err => console.warn("save-plan:", err.message));
    }, 500);
    return () => clearTimeout(timer);
  }, [authStatus, selectedMeals, weekPlan, retailerId, people, displayCostValue, checkedOut, roundDownChoices]);

  useEffect(() => {
    if (recipeLibrary.length === 0) return;
    const missing = recipeLibrary
      .map(recipe => recipe.id)
      .filter(id => !recipeLibraryIngredientCache.has(id));
    if (missing.length === 0) return;

    let cancelled = false;
    Promise.all(missing.map(id => api.getRecipe(id).catch(() => null)))
      .then(results => {
        if (cancelled) return;
        setRecipeLibraryIngredientCache(prev => {
          const next = new Map(prev);
          results.forEach((recipe, index) => {
            if (recipe) next.set(missing[index], recipe.ingredients || []);
          });
          return next;
        });
      });
    return () => { cancelled = true; };
  }, [recipeLibrary, recipeLibraryIngredientCache]);

  // ── Recipe detail sheet ───────────────────────────────────────────────────
  // Build a best-effort recipe detail from data already in client state so the
  // modal shows the saved recipe instantly — even if the fetch is slow or
  // briefly rate-limited. Fresh server data overwrites this when it arrives.
  function cachedRecipeDetail(id) {
    const lib  = recipeLibrary.find(r => r.id === id);
    const ings = importedIngredientCache.get(id);
    if (!lib && !(ings && ings.length)) return null;
    return {
      id,
      title:         lib?.title ?? null,
      cuisine:       lib?.cuisine ?? null,
      servings:      lib?.servings ?? null,
      serves:        lib?.servings ?? null,
      chef:          lib?.chef ?? null,
      publisher:     lib?.publisher ?? null,
      source_url:    lib?.sourceUrl ?? lib?.source_url ?? null,
      source_domain: lib?.sourceDomain ?? lib?.source_domain ?? null,
      prep_minutes:  lib?.prepMinutes ?? lib?.prep_minutes ?? null,
      cook_minutes:  lib?.cookMinutes ?? lib?.cook_minutes ?? null,
      dietary:       Array.isArray(lib?.dietary) ? lib.dietary : [],
      ingredients:   ings || lib?.ingredients || [],
      instructions:  Array.isArray(lib?.instructions) ? lib.instructions : [],
      _cached:       true,
    };
  }

  // Retry transient failures (429 / 5xx / network) before giving up. Only show
  // the scary "couldn't load" state when there's no cached fallback to display.
  function fetchRecipeDetail(id, cached, attempt) {
    api.getRecipe(id)
      .then(data => {
        if (recipeDetailIdRef.current !== id) return;   // user navigated away
        setRecipeDetail(data);
        setRecipeDetailLoading(false);
      })
      .catch(err => {
        if (recipeDetailIdRef.current !== id) return;
        const transient = err.status === 429 || err.status >= 500 || err.status == null;
        if (transient && attempt < 2) {
          const wait = err.retryAfter ? Math.min(Number(err.retryAfter) * 1000, 4000) : 500 * (attempt + 1);
          setTimeout(() => fetchRecipeDetail(id, cached, attempt + 1), wait);
          return;   // keep cached content / spinner during backoff
        }
        setRecipeDetailLoading(false);
        if (!cached) setRecipeDetail(null);   // nothing to show → surface the error
      });
  }

  function openRecipeDetail(id) {
    recipeDetailIdRef.current = id;
    setRecipeDetailId(id);
    const cached = cachedRecipeDetail(id);
    setRecipeDetail(cached);            // instant content when available
    setRecipeDetailLoading(!cached);   // spinner only when nothing cached
    fetchRecipeDetail(id, cached, 0);
  }
  function closeRecipeDetail() { recipeDetailIdRef.current = null; setRecipeDetailId(null); setRecipeDetail(null); }

  // ── API: fetch full meal detail on demand (detail modal) ────────────────────
  // Deps: [detailId, detailAttempt] — NOT mealDetails. Including mealDetails
  // caused the prefetch effect to abort and restart this call every time any
  // meal was fetched, creating a race that kept the modal stuck on "Loading
  // ingredients…". The guard still reads mealDetails from the current render
  // closure; that's correct because React captures the latest value at effect
  // run time, which is the same render that triggered the effect.
  useEffect(() => {
    setDetailError(null);
    if (!detailId || mealDetails.has(detailId)) return;
    const ctrl = new AbortController();
    api.getMeal(detailId, { signal: ctrl.signal })
      .then(full => setMealDetails(prev => {
        const meal = withMealImage(full);
        const next = new Map(prev); next.set(meal.id, meal); return next;
      }))
      .catch(err => {
        if (err.name !== "AbortError") {
          setDetailError("Couldn't load ingredients. Try again.");
        }
      });
    return () => ctrl.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailId, detailAttempt]);

  // ── API: prefetch full ingredient lists for every selected meal ─────────────
  // The shared-ingredients banner needs full ingredient arrays. Without this
  // prefetch the banner stays "None yet" until the user opens each meal modal.
  useEffect(() => {
    const missing = selectedMeals.filter(id => !mealDetails.has(id));
    if (missing.length === 0) return;
    const ctrl = new AbortController();
    Promise.all(
      missing.map(id => api.getMeal(id, { signal: ctrl.signal }).catch(() => null))
    ).then(results => {
      const fetched = results.filter(Boolean).map(withMealImage);
      setMealDetails(prev => {
        const next = new Map(prev);
        fetched.forEach(full => next.set(full.id, full));
        return next;
      });
      if (fetched.length > 0) {
        setMealsCache(prev => {
          const next = new Map(prev);
          fetched.forEach(full => next.set(full.id, full));
          return next;
        });
      }
    });
    return () => ctrl.abort();
  }, [selectedMeals]);

  // ── API: fetch cook-guide steps when user opens the cook modal ──────────────
  // Pass shoppingList so the server can honour threshold-rounded-down amounts
  // (step 1 shows grams that sum to what was actually bought, not the recipe
  // target). Servings defaults to the user's per-meal adjustment, or `people`.
  // cookGuideAttempt bumps when the user taps Retry → refires the effect.
  const [cookGuideAttempt, setCookGuideAttempt] = useState(0);
  useEffect(() => {
    if (!cookGuideMealId) return;
    cookGuideMealIdRef.current = cookGuideMealId;
    const id = cookGuideMealId;
    setCookGuideLoading(true);
    setCookGuideError(null);
    setCookGuideSteps(null);

    // ── Imported recipes — build the guide from the user's OWN saved recipe.
    // Never call the catalog cook-guide generator (it would invent steps).
    if (isImportedId(id)) {
      // Seed instantly from client cache, then refresh from the server.
      const lib  = recipeLibrary.find(r => r.id === id);
      const cachedIngs = importedIngredientCache.get(id);
      const cachedRecipe = (lib || (cachedIngs && cachedIngs.length))
        ? { title: lib?.title, servings: lib?.servings, ingredients: cachedIngs || [], instructions: lib?.instructions || [] }
        : null;
      if (cachedRecipe && (cachedRecipe.ingredients.length || cachedRecipe.instructions.length)) {
        setCookGuideSteps(buildImportedCookSteps(cachedRecipe));
        setCookGuideLoading(false);
      }
      api.getRecipe(id)
        .then(recipe => {
          if (cookGuideMealIdRef.current !== id) return;   // user opened a different meal
          setCookGuideSteps(buildImportedCookSteps(recipe));
          setCookGuideLoading(false);
        })
        .catch(() => {
          if (cookGuideMealIdRef.current !== id) return;
          // Keep cached steps if shown; otherwise build from any cached ingredients
          // with the no-steps fallback — never a scary error, never AI steps.
          if (!cachedRecipe) {
            setCookGuideSteps(buildImportedCookSteps({ ingredients: importedIngredientCache.get(id) || [], instructions: [] }));
          }
          setCookGuideLoading(false);
        });
      return;
    }

    const ctrl = new AbortController();
    const sl = shoppingListRef.current;
    api.getCookingSteps({
      mealId:       cookGuideMealId,
      servings:     effectiveServings[cookGuideMealId] || servingMap[cookGuideMealId] || people,
      shoppingList: Object.keys(sl).length ? sl : undefined,
      appliedSwaps: appliedSwapsRef.current,
      signal:       ctrl.signal,
    })
      .then(({ steps }) => setCookGuideSteps(steps))
      .catch(err => {
        if (err.name === "AbortError") return;
        // Map server errors to user-friendly copy. Never expose /api/... paths.
        if (err.status === 429) {
          setCookGuideError({ kind: "rate_limit", retryAfter: err.retryAfter });
        } else if (err.status === 503 || err.status === 502) {
          setCookGuideError({ kind: "unavailable" });
        } else {
          setCookGuideError({ kind: "generic" });
        }
      })
      .finally(() => setCookGuideLoading(false));
    return () => ctrl.abort();
  }, [cookGuideMealId, effectiveServings, servingMap, people, cookGuideAttempt]);

  // Opening a different meal resets the step navigator and closes the switch menu.
  useEffect(() => { setCookStepIdx(0); setCookSwitchOpen(false); }, [cookGuideMealId]);

  // ── API: fetch cuisine pair hints when the user picks their first cuisine ──
  // These are editorial labels ("Classic combo", "Perfect match") used to
  // decorate the *other* cuisine cards — no numbers, no formula exposure.
  useEffect(() => {
    if (selectedCuisines.length !== 1) {
      setPairHints({});
      return;
    }
    const first = selectedCuisines[0];
    const ctrl = new AbortController();
    api.getCuisinePairHints({ first, signal: ctrl.signal })
      .then(({ hints }) => {
        const map = {};
        for (const h of hints) map[h.cuisine] = h.label;
        setPairHints(map);
      })
      .catch(err => { if (err.name !== "AbortError") setPairHints({}); });
    return () => ctrl.abort();
  }, [selectedCuisines]);

  // ── API: fetch drink pairings for the meal in the detail modal ─────────────
  // Cached by cuisine so each cuisine is fetched at most once per session.
  useEffect(() => {
    if (!detailId) return;
    const meal = mealsById.get(detailId);
    if (!meal?.cuisine || drinkPairings[meal.cuisine]) return;
    const ctrl = new AbortController();
    api.getCuisineDrinkPairings({ first: meal.cuisine, signal: ctrl.signal })
      .then(({ pairings }) => setDrinkPairings(prev => ({ ...prev, [meal.cuisine]: pairings })))
      .catch(err => { if (err.name !== "AbortError") { /* swallow — detail modal stays useful without drinks */ } });
    return () => ctrl.abort();
  }, [detailId, mealsById, drinkPairings]);

  // ── API: fetch drink pairings for the meal in the cook guide ───────────────
  useEffect(() => {
    if (!cookGuideMealId) return;
    const meal = mealsById.get(cookGuideMealId);
    if (!meal?.cuisine || drinkPairings[meal.cuisine]) return;
    const ctrl = new AbortController();
    api.getCuisineDrinkPairings({ first: meal.cuisine, signal: ctrl.signal })
      .then(({ pairings }) => setDrinkPairings(prev => ({ ...prev, [meal.cuisine]: pairings })))
      .catch(err => { if (err.name !== "AbortError") { /* cook guide can render without drinks */ } });
    return () => ctrl.abort();
  }, [cookGuideMealId, mealsById, drinkPairings]);

  // ── API: retailer catalogue (budget-gated) ──────────────────────────────────
  useEffect(() => {
    const ctrl = new AbortController();
    api.listRetailers({ budget, signal: ctrl.signal })
      .then(({ tier, retailers }) => {
        setRetailerTier(tier);
        setAvailableRetailers(retailers || []);
      })
      .catch(err => { if (err.name !== "AbortError") setApiError(err.message); });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budget]);

  // ── API: build shopping list whenever selection, servings or retailer change
  // Race guard: AbortController + monotonic sequence number. If two retailer
  // changes fire in quick succession, only the LATEST response is applied —
  // an earlier response that resolves AFTER the abort would otherwise overwrite
  // newer state and leave the UI showing stale prices.
  const shoppingListRef = useRef(shoppingList);
  useEffect(() => { shoppingListRef.current = shoppingList; }, [shoppingList]);

  const shoppingListSeq = useRef(0);
  useEffect(() => {
    const builtInMealIds = planMealIds.filter(id => !isImportedId(id));
    const importedIds     = planMealIds.filter(isImportedId);

    // Build the imported-recipe payload from the in-memory ingredient cache.
    // Raw ingredients at the recipe's SAVED servings; the server scales by
    // plannedServings/savedServings and merges grams into the same list as
    // catalog meals (one line per PKG id, packed once).
    const importedRecipes = importedIds
      .map(id => {
        const ings = importedIngredientCache.get(id) || [];
        const meal = mealsById.get(id);
        return {
	          ingredients: ings
	            .map(i => ({
	              name: i.name,
	              qty: cartQuantityForIngredient(i),
	              unit: i.unit ?? null,
	              displayQty: displayIngredientQtyUnit(i),
	              displayName: i.name,
	            }))
	            .filter(i => i.name),
          // Recipe name for source-meal attribution on merged shopping list lines.
          title:           meal?.name || meal?.title || mealName(id, mealsById) || "Imported recipe",
          savedServings:   Math.max(1, Math.min(12, Number(meal?.serves) || 2)),
          plannedServings: Math.max(1, Math.min(12, Number(effectiveServings[id] ?? people) || 2)),
        };
      })
      .filter(r => r.ingredients.length > 0);

    if (builtInMealIds.length === 0 && importedRecipes.length === 0) {
      shoppingListSeq.current++;
      setShoppingList({}); setBuildManualItems([]); setEfficiency(100); setSwapSuggestions([]); setIngredientSwapSuggestions([]);
      return;
    }
    const ctrl  = new AbortController();
    const mySeq = ++shoppingListSeq.current;
    api.buildShoppingList({
      mealIds:      builtInMealIds,
      servings:     Object.fromEntries(builtInMealIds.map(id => [id, effectiveServings[id]])),
      retailerId,
      organicMode,
      diet,
      allergens:    allergies,
      dislikes,
      importedRecipes,
      appliedSwaps: appliedSwapsRef.current,
      signal:       ctrl.signal,
    })
      .then(res => {
        if (mySeq !== shoppingListSeq.current) return;  // stale response — drop
        // Server returns items as an array; UI indexes them as a map by id.
        const asMap = Array.isArray(res.items)
          ? Object.fromEntries(res.items.map(it => [it.id, it]))
          : (res.items || {});
        // Re-overlay the user's pack choices so a rebuild never resets them.
        setShoppingList(applyRoundDownChoices(asMap, roundDownChoicesRef.current));
        setBuildManualItems(res.manualItems || []);
        setEfficiency(res.efficiency ?? 100);
        setSwapSuggestions(res.swapSuggestions || []);
        setIngredientSwapSuggestions(res.ingredientSwapSuggestions || []);
        // Reconcile validated swaps: server may drop invalid entries (e.g. meal removed).
        // Compare by ID fingerprints only — server strips display names (fromName/toName/mealName)
        // before they reach validation, so JSON.stringify would always differ and clobber local state.
        const validated = res.appliedSwaps || [];
        const current = appliedSwapsRef.current;
        const fp = a => `${a.mealId}:${a.fromId}:${a.toId}`;
        const currentFps = current.map(fp).join(",");
        const validatedFps = validated.map(fp).join(",");
        if (currentFps !== validatedFps) {
          // Server trimmed some entries — re-merge display names from local state.
          const nameMap = new Map(current.map(a => [fp(a), a]));
          setAppliedSwaps(validated.map(sv => ({ ...nameMap.get(fp(sv)), ...sv })));
        }
      })
      .catch(err => { if (err.name !== "AbortError") setApiError(err.message); });
    return () => ctrl.abort();
  // diet/allergies/dislikes change swap-suggestion candidates and server-side conflict detection;
  // importedIngredientCache/mealsById/people feed the imported-recipe payload;
  // appliedSwaps triggers a rebuild when user accepts/undoes a swap.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planMealIds, effectiveServings, retailerId, organicMode, diet, allergies, dislikes, importedIngredientCache, mealsById, people, appliedSwaps]);

  // ── API: prefetch imported recipe data on mount / when selectedMeals changes ─
  // importedIngredientCache is in-memory only; on page reload it is empty while
  // selectedMeals (localStorage) may still contain imported UUIDs. This effect
  // re-fetches missing entries so the List tab works without requiring the user
  // to re-open each recipe detail.
  useEffect(() => {
    const importedIds = planMealIds.filter(isImportedId);
    // Re-fetch when the entry is absent OR empty. Removing an imported meal
    // deletes its cache entry, and some re-add paths seed it with an empty
    // array (the library list endpoint strips ingredients). Treating empty as
    // "missing" means re-adding always restores the real ingredients + cost,
    // instead of leaving an empty entry that silently drops the meal from the
    // basket. Saved imported recipes always have ≥1 ingredient, so empty here
    // unambiguously means "not loaded yet".
    const missing     = importedIds.filter(id => {
      const cached = importedIngredientCache.get(id);
      return !cached || cached.length === 0;
    });
    if (missing.length === 0) return;
    Promise.all(missing.map(id => api.getRecipe(id).catch(() => null)))
      .then(results => {
        const ingrUpd  = new Map();
        const cacheUpd = new Map();
        results.forEach((r, i) => {
          if (!r) return;
          ingrUpd.set(missing[i], r.ingredients || []);
          if (!mealsCache.has(missing[i])) {
            cacheUpd.set(missing[i], {
              id: missing[i], name: r.title ?? "Imported recipe",
              emoji: recipeEmoji(r.title), time: null, cals: null,
              serves: r.servings ?? 2, isImported: true,
              cuisine: r.cuisine ?? null,
              sourceUrl: r.source_url ?? null, sourceDomain: r.source_domain ?? null, sourceDisplayName: recipeSourceDisplayName(r),
            });
          }
        });
        if (ingrUpd.size > 0)
          setImportedIngredientCache(prev => { const n = new Map(prev); ingrUpd.forEach((v, k) => n.set(k, v)); return n; });
        if (cacheUpd.size > 0)
          setMealsCache(prev => { const n = new Map(prev); cacheUpd.forEach((v, k) => n.set(k, v)); return n; });
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planMealIds]);

  // Prune applied swaps for meals that are no longer in the plan.
  useEffect(() => {
    setAppliedSwaps(prev => {
      const pruned = prev.filter(s => planMealIds.includes(s.mealId));
      return pruned.length === prev.length ? prev : pruned;
    });
    setDismissedSwapFps([]);  // fresh plan → show suggestions again
  }, [planMealIds]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── API: fuzzy-match imported recipe ingredients against PKG catalog ──────────
  // Runs whenever imported meals in the week plan change or the retailer changes.
  // Same sequence-number race guard as the build-list useEffect above.
  const importedItemsSeq = useRef(0);
  useEffect(() => {
    const importedIds = selectedMeals.filter(isImportedId);
    if (importedIds.length === 0) {
      importedItemsSeq.current++;
      // Return prev when already empty so we don't emit a fresh {matched:[]}
      // object every run — that new array ref churns the generatePlan effect
      // (which feeds mealsCache → mealsById → this effect) into an infinite loop.
      setImportedItems(prev =>
        prev.matched.length === 0 && prev.manualItems.length === 0 ? prev : { matched: [], manualItems: [] });
      return;
    }
    const allIngredients = [];
    for (const id of importedIds) {
      const ings = importedIngredientCache.get(id) || [];
      const meal = mealsById.get(id);
      const baseServings = Number(meal?.serves);
      const targetServings = Number(effectiveServings[id] ?? people);
      const factor = baseServings > 0 && targetServings > 0 ? targetServings / baseServings : 1;
      ings.forEach(i => allIngredients.push(scaleImportedIngredientForServings(i, factor)));
    }
    if (allIngredients.length === 0) {
      importedItemsSeq.current++;
      setImportedItems(prev =>
        prev.matched.length === 0 && prev.manualItems.length === 0 ? prev : { matched: [], manualItems: [] });
      return;
    }
    const ctrl  = new AbortController();
    const mySeq = ++importedItemsSeq.current;
    api.matchImportedIngredients({ ingredients: allIngredients, retailerId, signal: ctrl.signal })
      .then(res => {
        if (mySeq !== importedItemsSeq.current) return;  // stale response — drop
        setImportedItems({
          ...(res || {}),
          matched: normalizeImportedMatched(res?.matched || []),
          manualItems: res?.manualItems || [],
        });
      })
      .catch(err => { if (err.name !== "AbortError") console.warn("match-imported:", err.message); });
    return () => ctrl.abort();
  }, [selectedMeals, importedIngredientCache, retailerId, effectiveServings, mealsById, people]);

  // Toggle an item's Own state. Guests keep it locally; signed-in users also
  // sync to the server so the pantry survives across devices.
  function handleOwn(itemKey) {
    const jwt = api.getJwt();
    // Read current owned state from the live closure value, not from inside the
    // functional updater, so we can use it for the API call and toast below.
    const wasOwned = pantryItems.has(itemKey);

    // Use prev.has() inside the updater so concurrent calls always operate on
    // the latest state, not on a stale closure snapshot.
    setPantryItems(prev => {
      const next = new Set(prev);
      if (prev.has(itemKey)) next.delete(itemKey); else next.add(itemKey);
      return next;
    });

    if (!jwt) {
      show(wasOwned ? "Removed from owned items" : "Marked as owned for this device", "info");
      return;
    }

    // Keep the optimistic UI update regardless of API outcome — the user's
    // intent is clear. A toast warns on failure without reverting the toggle.
    if (wasOwned) {
      api.removePantryItem(itemKey).catch(() => {
        show("Couldn't sync owned status — will reset on reload", "warn");
      });
    } else {
      api.addPantryItem(itemKey).catch(() => {
        show("Couldn't sync owned status — will reset on reload", "warn");
      });
    }
  }

  // Resolve a small-shortfall pack choice. Rewrites the item's packs/cost/leftover
  // in the live shopping list between the two pre-computed scenarios (from the
  // server's roundDownOption). Because every total (basket, efficiency, waste
  // tips, banners) derives from shoppingList, they all re-flow automatically.
  function handleRoundDownChoice(itemId, scenario) {
    // Record the durable choice (survives rebuilds), then overlay it on the
    // live list for an immediate update. Update the ref SYNCHRONOUSLY (not just
    // via the [roundDownChoices] effect, which runs a render later) so a build
    // response landing right after this click re-applies the new choice instead
    // of the server default — otherwise the toggle intermittently reverts.
    const next = { ...roundDownChoicesRef.current, [itemId]: scenario };
    roundDownChoicesRef.current = next;
    setRoundDownChoices(next);
    setShoppingList(prev => applyRoundDownChoices(prev, { [itemId]: scenario }));
    show(
      scenario === "roundDown"
        ? "Rounded down — using what you have, less waste"
        : "Added the extra pack for the full amount",
      "info",
    );
  }

  // Swap an imported ingredient to a different PKG item. The chosen item moves
  // into the row and the previous row item drops back into the swap chips.
  function handleSwap(rowId, origName, pkgId) {
    const jwt = api.getJwt();

    // Optimistic: swap the matched item in-place.
    setImportedItems(prev => {
      const next = { ...prev };
      next.matched = prev.matched.map(item => {
        if (item.id !== rowId) return item;
        // Find the alternative entry for the chosen pkgId.
        const alt = item.alternatives?.find(a => a.pkgId === pkgId);
        if (!alt) return item;
        const originalPkgId = item.originalPkgId
          || (item.original ? item.id : item.alternatives?.find(a => a.original)?.pkgId)
          || null;
        const previous = {
          pkgId:          item.id,
          pkgName:        item.name,
          original:       !!originalPkgId && item.id === originalPkgId,
          cat:            item.cat,
          unit:           item.unit,
          needed:         item.needed,
          packs:          item.packs,
          size:           item.size,
          leftover:       item.leftover,
          costAtRetailer: item.cost,
        };
        const alternatives = [previous, ...item.alternatives.filter(a => a.pkgId !== pkgId)]
          .filter((a, index, arr) => arr.findIndex(x => x.pkgId === a.pkgId) === index);

        return normalizeImportedOriginals({
          ...item,
          id:           pkgId,
          name:         alt.pkgName,
          cat:          alt.cat ?? item.cat,
          unit:         alt.unit ?? item.unit,
          needed:       alt.needed ?? item.needed,
          packs:        alt.packs ?? item.packs,
          size:         alt.size ?? item.size,
          leftover:     alt.leftover ?? item.leftover,
          cost:         alt.costAtRetailer,
          pinned:       !!jwt,
          originalPkgId,
          original:     !!originalPkgId && pkgId === originalPkgId,
          alternatives,
        });
      });
      next.matched = normalizeImportedMatched(next.matched);
      return next;
    });

    if (!jwt) return;
    api.pinSwap({ origName, pkgId })
      .catch(() => show("Couldn't save swap — try again", "warn"));
  }

  // Ingredient overlap set, derived from any meal-detail responses we have.
  // This only lights up once the user has opened the detail modal for at least
  // one selected meal. It's a UI nicety — the authoritative shared count comes
  // from the server's sharedIngredientCount field on each meal card.
  const ingSet = useMemo(() => {
    const s = new Set();
    for (const id of selectedMeals) {
      const full = mealDetails.get(id);
      if (full) full.ingredients.forEach(i => s.add(i.id));
    }
    return s;
  }, [selectedMeals, mealDetails]);

  /* ── Actions ─── */
  // batchUndo: when true, multiple toggleMeal calls in the same tick should
  // accumulate into a single undoMeals batch rather than overwriting it.
  // Used by the conflict-banner "Remove all" path.
  const batchUndoRef = useRef(false);

  const clearUndoDismissTimer = () => {
    clearTimeout(undoTimerRef.current);
    undoTimerRef.current = null;
  };

  const startUndoDismissTimer = (durationMs = MEAL_REMOVE_UNDO_MS) => {
    clearUndoDismissTimer();
    undoRemainingMsRef.current = durationMs;
    undoExpiresAtRef.current = Date.now() + durationMs;
    undoTimerRef.current = setTimeout(() => setUndoMeals(null), durationMs);
  };

  const pauseUndoDismissTimer = () => {
    if (!undoTimerRef.current) return;
    undoRemainingMsRef.current = Math.max(1000, undoExpiresAtRef.current - Date.now());
    clearUndoDismissTimer();
  };

  const resumeUndoDismissTimer = () => {
    if (!undoMeals || undoMeals.length === 0 || undoTimerRef.current) return;
    startUndoDismissTimer(undoRemainingMsRef.current || MEAL_REMOVE_UNDO_MS);
  };

  const clearAutofillUndoState = () => {
    clearTimeout(autofillUndoTimer.current);
    autofillUndoTimer.current = null;
    autofillUndoRef.current = null;
    setAutofillUndo(null);
  };

  const performAutofillUndo = () => {
    const undo = autofillUndoRef.current;
    const result = applyAutofillUndo({
      undo,
      selectedMeals: selectedMealsRef.current,
      weekPlan: weekPlanRef.current,
      servingMap: servingMapRef.current,
    });
    if (!result.ok) {
      show(result.reason || "Couldn't undo autofill", "warn");
      return false;
    }
    setSelectedMeals(result.selectedMeals);
    setWeekPlan(result.weekPlan);
    setServingMap(result.servingMap);
    setJustFilledIds([]);
    setCheckedOut(false);
    clearAutofillUndoState();
    show(`Autofill undone — restored ${result.selectedMeals.length}/${mealTarget} meals`);
    return true;
  };

  const removeMealWithUndo = (mealId, { silent = false } = {}) => {
    const meal = mealsById.get(mealId);
    const latestSelected = selectedMealsRef.current;
    const latestWeekPlan = weekPlanRef.current;
    if (!meal || !latestSelected.includes(mealId)) return;
    clearAutofillUndoState();
    const removedDay = Object.keys(latestWeekPlan).find(d => latestWeekPlan[d] === mealId) || null;
    const importedIngredients = isImportedId(mealId)
      ? (importedIngredientCacheRef.current.get(mealId) || recipeLibraryIngredientCache.get(mealId) || null)
      : null;
    const entry = {
      id: mealId,
      type: isImportedId(mealId) ? "imported" : "catalog",
      name: meal.name,
      title: meal.title || meal.name,
      day: removedDay,
      serving: servingMapRef.current[mealId] ?? effectiveServings[mealId] ?? people,
      meal,
      ingredients: importedIngredients,
      snapshot: {
        selectedMeals: [...latestSelected],
        weekPlan: { ...latestWeekPlan },
        servingMap: { ...servingMapRef.current },
        count: latestSelected.length,
      },
    };
    setSelectedMeals(s => s.filter(id => id !== mealId));
    setWeekPlan(p => {
      const n = { ...p };
      Object.keys(n).forEach(d => { if (n[d] === mealId) delete n[d]; });
      return n;
    });
    if (isImportedId(mealId)) {
      setImportedIngredientCache(prev => { const n = new Map(prev); n.delete(mealId); return n; });
    }
    setCheckedOut(false);
    setClearConfirm(false);
    clearUndoDismissTimer();
    setUndoMeals(prev => (batchUndoRef.current && prev ? [...prev, entry] : [entry]));
    startUndoDismissTimer(MEAL_REMOVE_UNDO_MS);
    if (!silent && !batchUndoRef.current) show(`${meal.name} removed`, "remove");
  };

  const toggleMeal = (mealId) => {
    const meal = mealsById.get(mealId);
    if (!meal) return;
    if (selectedMeals.includes(mealId)) {
      removeMealWithUndo(mealId);
    } else if (selectedMeals.length < mealTarget) {
      clearAutofillUndoState();
      setSelectedMeals(s => [...s, mealId]);
      setCheckedOut(false);
      clearUndoDismissTimer();
      setUndoMeals(null); // adding a meal cancels any pending undo
      // Two-step day assignment was the #1 discoverability gap in beta:
      // users tapped "Add" and assumed the meal was scheduled, never
      // discovering the Week tab. Toast now explicitly points the way and
      // the persistent "unassigned-meals" banner on the Meals tab provides
      // a tappable shortcut for users who miss the toast.
      show(`${meal.name} added · Pick a day in the Week tab →`);
    } else {
      show(`Week full — remove a meal first`, "warn");
    }
  };

  const toggleAllergen = a => setAllergies(prev =>
    prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]
  );

  const toggleDislike = d => setDislikes(prev =>
    prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]
  );

  const toggleCuisine = c => {
    setSelectedCuisines(prev => {
      if (prev.includes(c)) return prev.filter(x => x !== c);
      if (prev.length >= 2) {
        show("Max 2 cuisines — deselect one first", "warn");
        return prev;
      }
      return [...prev, c];
    });
  };

  // Auto-fill: ask the server to pick the N highest-overlap meals until we
  // reach mealTarget, then splice them into selectedMeals + mealsCache.
  // Scoring (shared ingredients ×2, new ×0.5) lives on the server — the
  // client never reads the scoring formula.
  const runAutofill = async () => {
    if (autofillLoading) return;
    clearAutofillUndoState();
    const autofillBase = {
      selectedMeals: [...selectedMealsRef.current],
      weekPlan: { ...weekPlanRef.current },
      servingMap: { ...servingMapRef.current },
    };
    const slotsLeft = mealTarget - autofillBase.selectedMeals.length;
    if (slotsLeft <= 0) {
      show("Week already full — nothing to fill", "warn");
      return;
    }
    setAutofillLoading(true);
    setAutofillError(null);
    try {
      const { meals: filled } = await api.autofillPlan({
        selectedMealIds:       autofillBase.selectedMeals,
        cuisines:              selectedCuisines,
        diet,
        allergens:             allergies,
        dislikes,
        lovedMealIds:          [],
        targetCount:           mealTarget,
        selectedIngredientIds: importedItems.matched.map(i => i.id),
        retailerId,
      });
      if (!filled || filled.length === 0) {
        show("No more matching meals to add", "warn");
        return;
      }
      // Merge into cache so the UI can render them without waiting for the
      // next /generate refresh.
      setMealsCache(prev => {
        const next = new Map(prev);
        for (const m of filled.map(withMealImage)) next.set(m.id, m);
        return next;
      });
      const already = new Set(autofillBase.selectedMeals);
      const newIds = filled.map(m => m.id).filter(id => !already.has(id));
      if (newIds.length === 0) {
        show("No new matching meals to add", "warn");
        return;
      }
      setSelectedMeals(s => {
        const alreadyFresh = new Set(s);
        const add = filled.map(m => m.id).filter(id => !alreadyFresh.has(id));
        return [...s, ...add].slice(0, mealTarget);
      });
      // Just-filled badge: show for 4s then fade
      setJustFilledIds(newIds);
      setTimeout(() => setJustFilledIds([]), 4000);
      const undo = makeAutofillUndo({
        addedIds: newIds,
        selectedMeals: autofillBase.selectedMeals,
        weekPlan: autofillBase.weekPlan,
        servingMap: autofillBase.servingMap,
      });
      autofillUndoRef.current = undo;
      setAutofillUndo(undo);
      if (newIds.length < slotsLeft) {
        show(`Added ${newIds.length} matching meal${newIds.length === 1 ? "" : "s"} — no more valid meals fit your filters`, "warn");
      } else {
        show(`Added ${newIds.length} meal${newIds.length === 1 ? "" : "s"} — ♻️ optimised for less waste`);
      }
    } catch (err) {
      setAutofillError(err.message);
      show("Auto-fill failed — try again", "warn");
    } finally {
      setAutofillLoading(false);
    }
  };

  const assignDay = (day, mealId) => {
    clearAutofillUndoState();
    setWeekPlan(p => {
      const n = { ...p };
      Object.keys(n).forEach(d => { if (n[d] === mealId) delete n[d]; });
      if (mealId) n[day] = mealId; else delete n[day];
      return n;
    });
  };

  const copyList = () => {
    const retailerName = retailerLabel(retailerId, retailerOptions);
    const lines = [`🛒 ${retailerName} Shopping List — ${selectedMealObjs.length} meals\n`];
    ["Proteins","Produce","Dairy","Pantry","Herbs"].forEach(cat => {
      const items = Object.values(shoppingList).filter(i => i.cat === cat);
      if (!items.length) return;
      lines.push(`\n${CATEGORY_ICONS[cat]} ${cat}`);
      items.forEach(i => lines.push(`  • ${i.packs}× ${i.name}  £${i.cost.toFixed(2)}`));
    });
    // Imported recipe ingredients that couldn't be matched to the catalog.
    if (buildManualItems.length) {
      lines.push(`\n📋 From your imported recipes`);
      lines.push(`  🛒 Add manually`);
      buildManualItems.forEach(i => lines.push(`    • ${i.displayText || i.origName}`));
    }
    lines.push(`\n💰 Estimated total: £${displayCost}`);
    lines.push("Prices are cached estimates and may differ from live retailer prices, discounts, or current offers.");
    navigator.clipboard.writeText(lines.join("\n")).then(() => {
      setCopied(true); show("List copied 📋");
      setTimeout(() => setCopied(false), 2200);
    });
  };

  const isSetPasswordRoute = typeof window !== "undefined" && window.location.pathname === "/set-password";
  if (isSetPasswordRoute) {
    const handleSetPassword = async e => {
      e.preventDefault();
      setSetupError(null);
      if (!setupToken) {
        setSetupError("This setup link is missing or has expired. Please request a new signup link.");
        return;
      }
      if (setupPassword.length < 8) {
        setSetupError("Password must be at least 8 characters.");
        return;
      }
      if (setupPassword !== setupConfirm) {
        setSetupError("Passwords do not match.");
        return;
      }

      setSetupLoading(true);
      try {
        const result = await api.setPassword(setupToken, setupPassword);
        prefsStore.endTastingSession();          // never carry a guest session into the account
        history.replaceState(null, "", "/app");  // pathname must be /app before the remount
        setAuthStatus(result?.state === "beta_pending" ? "beta_pending" : "authenticated");
      } catch (err) {
        setSetupError(err.message || "Could not save password. Please request a fresh signup link.");
      } finally {
        setSetupLoading(false);
      }
    };

    const cardStyle = {
      background: "rgba(255,255,255,.12)", borderRadius: 20,
      padding: "32px 28px", maxWidth: 400, width: "100%",
      display: "flex", flexDirection: "column", gap: 14,
    };
    const inputStyle = {
      padding: "14px 16px", borderRadius: 12, border: "none",
      fontSize: 16, fontFamily: "inherit", outline: "none",
      background: "rgba(255,255,255,.9)", color: C.charcoal,
    };

    return (
      <div style={{
        minHeight: "100vh",
        background: `linear-gradient(160deg, ${C.greenDark} 0%, ${C.green} 55%, ${C.greenLight} 100%)`,
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", padding: "40px 24px", textAlign: "center",
      }}>
        <BrandLogo size={64} radius={18} style={{ marginBottom: 16 }} />
        <h1 style={{
          fontFamily: "Lora, Georgia, serif", fontSize: 32, fontWeight: 700,
          color: C.white, marginBottom: 8,
        }}>Set your password</h1>
        <p style={{ color: "rgba(255,255,255,.75)", fontSize: 15, marginBottom: 28, maxWidth: 420 }}>
          Create a password for NomLoop. After this, you can log in directly without waiting for email.
        </p>
        <form onSubmit={handleSetPassword} style={cardStyle}>
          <input
            type="password"
            placeholder="Password"
            value={setupPassword}
            onChange={e => setSetupPassword(e.target.value)}
            minLength={8}
            required
            autoFocus
            style={inputStyle}
          />
          <input
            type="password"
            placeholder="Confirm password"
            value={setupConfirm}
            onChange={e => setSetupConfirm(e.target.value)}
            minLength={8}
            required
            style={inputStyle}
          />
          {setupError && (
            <p style={{ color: "#ffcdd2", fontSize: 13, margin: 0 }}>{setupError}</p>
          )}
          <button
            type="submit"
            disabled={setupLoading || !setupPassword || !setupConfirm}
            style={{
              padding: "15px", borderRadius: 12, border: "none",
              background: setupLoading ? "rgba(255,255,255,.4)" : C.white,
              color: C.greenDark, fontWeight: 800, fontSize: 16,
              fontFamily: "inherit", cursor: setupLoading ? "default" : "pointer",
            }}
          >
            {setupLoading ? "Saving…" : "Save password →"}
          </button>
        </form>
        <a
          href="/"
          style={{ marginTop: 24, color: "rgba(255,255,255,.65)", fontSize: 13, textDecoration: "underline" }}
        >
          Back to homepage
        </a>
        <CookieBanner consent={consent} onAccept={acceptCookies} onReject={rejectCookies} onReset={resetCookies} />
      </div>
    );
  }

  /* ═══════════════════════════════════════════
     BETA ACCESS — entry screen, then sign-in / sign-up
  ═══════════════════════════════════════════ */
  // Two-path front door for fresh visitors. "Log in" flips to the auth form
  // below; "Tasting" starts a session-only guest run and remounts into the app.
  if (authStatus === "unauthenticated" && entryView === "entry") {
    return (
      <BetaEntry
        C={C}
        logo={<BrandLogo size={64} radius={18} style={{ marginBottom: 16 }} />}
        onGoogle={() => api.startOAuth("google")}
        onApple={() => api.startOAuth("apple")}
        onEmail={() => { setAuthMode("signin"); setEntryView("auth"); }}
        onTasting={() => { prefsStore.beginTastingSession(); setAuthStatus("tasting"); }}
      />
    );
  }

  // The email / password / invite-code wall: shown to an unauthenticated visitor
  // who chose "Log in / Create account", or a Tasting user who tapped "Log in to
  // save". A successful login remounts into the authenticated app.
  if (authStatus === "unauthenticated" || (authStatus === "tasting" && showAuthOverlay)) {
    const finishAuth = (result) => {
      prefsStore.endTastingSession();   // a tasting→login must not carry guest data forward
      setShowAuthOverlay(false);
      setAuthStatus(result?.state === "beta_pending" ? "beta_pending" : "authenticated");
    };

    const handleAuth = async e => {
      e.preventDefault();
      setSigninError(null);
      setSigninLoading(true);
      try {
        if (authMode === "signup") {
          const result = await api.signup(
            signinEmail.trim(),
            signinPassword,
            authConfig?.betaMode ? signinCode.trim() : undefined,
          );
          finishAuth(result);
        } else {
          const result = await api.login(signinEmail.trim(), signinPassword);
          finishAuth(result);
        }
      } catch (err) {
        if (authMode === "signin" && err.status === 401) {
          setSigninError("Email or password is incorrect.");
        } else if (err.status === 409) {
          setSigninError("Account already exists. Sign in instead.");
        } else {
          setSigninError(err.message || "Something went wrong");
        }
      } finally {
        setSigninLoading(false);
      }
    };

    const switchToSignup = () => { setAuthMode("signup"); setFlippedFromSignin(false); setSigninError(null); };
    const switchToSignin = () => { setAuthMode("signin"); setFlippedFromSignin(false); setSigninError(null); setSigninCode(""); };

    const isSignup = authMode === "signup";

    // Form-card and input styles shared across both modes — single source of truth.
    const cardStyle = {
      background: "rgba(255,255,255,.12)", borderRadius: 20,
      padding: "32px 28px", maxWidth: 380, width: "100%",
      display: "flex", flexDirection: "column", gap: 14,
    };
    const inputStyle = {
      padding: "14px 16px", borderRadius: 12, border: "none",
      fontSize: 16, fontFamily: "inherit", outline: "none",
      background: "rgba(255,255,255,.9)", color: C.charcoal,
    };

    return (
      <div style={{
        minHeight: "100vh",
        background: `linear-gradient(160deg, ${C.greenDark} 0%, ${C.green} 55%, ${C.greenLight} 100%)`,
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", padding: "40px 24px", textAlign: "center",
      }}>
        <BrandLogo size={64} radius={18} style={{ marginBottom: 16 }} />
        <h1 style={{
          fontFamily: "Lora, Georgia, serif", fontSize: 32, fontWeight: 700,
          color: C.white, marginBottom: 8,
        }}>NomLoop</h1>
        <p style={{ color: "rgba(255,255,255,.75)", fontSize: 15, marginBottom: 36 }}>
          {isSignup ? "Create your account with email and password." : "Sign in with your email and password."}
        </p>

        {signinSent ? (
          <div style={{
            background: "rgba(255,255,255,.12)", borderRadius: 20, padding: "32px 28px",
            maxWidth: 380, width: "100%",
          }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>📬</div>
            <p style={{ color: C.white, fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
              Check your inbox
            </p>
            <p style={{ color: "rgba(255,255,255,.75)", fontSize: 14, lineHeight: 1.6 }}>
              Magic link sent to <strong>{signinEmail}</strong>.<br />
              Click it to set your password and finish creating your account — link expires in 15 minutes.
            </p>
            <button
              onClick={() => { setSigninSent(false); setSigninError(null); setFlippedFromSignin(false); }}
              style={{
                marginTop: 24, background: "none", border: "none",
                color: "rgba(255,255,255,.6)", fontSize: 13,
                cursor: "pointer", textDecoration: "underline",
              }}
            >
              Use a different email
            </button>
          </div>
        ) : (
          <form onSubmit={handleAuth} style={cardStyle}>
            {isSignup && flippedFromSignin && (
              <div style={{ padding: "12px 14px", borderRadius: 10, background: "rgba(255,255,255,.18)", textAlign: "left" }}>
                <p style={{ color: C.white, fontWeight: 700, fontSize: 14, margin: "0 0 4px" }}>No account yet for this email</p>
                <p style={{ color: "rgba(255,255,255,.8)", fontSize: 13, margin: 0, lineHeight: 1.5 }}>
                  Enter your invite code below to join. Future sign-ins won't need it.
                </p>
              </div>
            )}
            <input
              type="email"
              placeholder="your@email.com"
              value={signinEmail}
              onChange={e => { setSigninEmail(e.target.value); setFlippedFromSignin(false); setSigninError(null); }}
              required
              style={inputStyle}
            />
            {!isSignup && (
              <input
                type="password"
                placeholder="Password"
                value={signinPassword}
                onChange={e => { setSigninPassword(e.target.value); setSigninError(null); }}
                minLength={8}
                required
                style={inputStyle}
              />
            )}
            {isSignup && (
              <input
                type="password"
                placeholder="Password"
                value={signinPassword}
                onChange={e => { setSigninPassword(e.target.value); setSigninError(null); }}
                minLength={8}
                required
                style={inputStyle}
              />
            )}
            {isSignup && authConfig?.betaMode && (
              <input
                type="text"
                placeholder="Beta code"
                value={signinCode}
                onChange={e => setSigninCode(e.target.value)}
                autoCapitalize="characters"
                autoFocus={flippedFromSignin}
                required
                style={{ ...inputStyle, fontSize: 15, letterSpacing: 0 }}
              />
            )}
            {signinError && (
              <p style={{ color: "#ffcdd2", fontSize: 13, margin: 0 }}>{signinError}</p>
            )}
            <button
              type="submit"
              disabled={signinLoading || !signinEmail || !signinPassword || (isSignup && authConfig?.betaMode && !signinCode)}
              style={{
                padding: "15px", borderRadius: 12, border: "none",
                background: signinLoading ? "rgba(255,255,255,.4)" : C.white,
                color: C.greenDark, fontWeight: 800, fontSize: 16,
                fontFamily: "inherit", cursor: signinLoading ? "default" : "pointer",
              }}
            >
              {signinLoading ? (isSignup ? "Creating..." : "Signing in...") : isSignup ? "Create account" : "Sign in"}
            </button>
            <p style={{ color: "rgba(255,255,255,.5)", fontSize: 12, margin: 0 }}>
              {isSignup && authConfig?.betaMode
                ? "Beta code is checked on the server before saved access."
                : isSignup
                  ? "Beta code is not required while beta mode is off."
                  : "New users can create an account with email and password."}
            </p>
          </form>
        )}

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginTop: 28 }}>
          {!signinSent && (
            <button
              onClick={isSignup ? switchToSignin : switchToSignup}
              style={{
                background: "none", border: "none",
                color: "rgba(255,255,255,.85)", fontSize: 13, fontWeight: 600,
                cursor: "pointer", textDecoration: "underline",
              }}
            >
              {isSignup ? "Back to sign in" : "Create account"}
            </button>
          )}
          <button
            onClick={() => { if (authStatus === "tasting") setShowAuthOverlay(false); else setEntryView("entry"); }}
            style={{
              background: "none", border: "none",
              color: "rgba(255,255,255,.6)", fontSize: 13,
              cursor: "pointer", textDecoration: "underline",
            }}
          >
            ← Back
          </button>
          <a
            href="/"
            style={{ color: "rgba(255,255,255,.4)", fontSize: 12, textDecoration: "none" }}
          >
            ← Back to homepage
          </a>
        </div>

        <CookieBanner consent={consent} onAccept={acceptCookies} onReject={rejectCookies} onReset={resetCookies} />
      </div>
    );
  }

  /* ═══════════════════════════════════════════
     ONBOARDING SCREENS
  ═══════════════════════════════════════════ */
  if (step < TOTAL_STEPS) {

    // 0 – Welcome
    if (step === 0) return (
      <div style={{
        minHeight: "100vh", background: `linear-gradient(160deg, ${C.greenDark} 0%, ${C.green} 55%, ${C.greenLight} 100%)`,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: "40px 24px", textAlign: "center",
      }}>
        <BrandLogo size={78} radius={22} style={{ marginBottom: 20 }} />
        <h1 style={{
          fontFamily: "Lora, Georgia, serif", fontSize: 36, fontWeight: 700,
          color: C.white, marginBottom: 12,
        }}>
          NomLoop
        </h1>
        <p style={{ color: "rgba(255,255,255,.75)", fontSize: 17, maxWidth: 320, lineHeight: 1.6, marginBottom: 40 }}>
          Plan your week around shared ingredients and build your grocery basket — automatically.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%", maxWidth: 340 }}>
          {[
            { emoji: "🥗", text: "Smart weekly meal planning" },
            { emoji: "♻️", text: "Meals that share ingredients" },
            { emoji: "🛍️", text: "Auto-builds your grocery basket" },
          ].map((f, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
              background: "rgba(255,255,255,.1)", borderRadius: 14,
              color: C.white, fontSize: 15, fontWeight: 600,
            }}>
              <span style={{ fontSize: 22 }}>{f.emoji}</span> {f.text}
            </div>
          ))}
        </div>
        <button
          onClick={() => setStep(1)}
          style={{
            marginTop: 40, padding: "17px 48px", borderRadius: 99, border: "none",
            background: C.white, color: C.greenDark, fontFamily: "inherit",
            fontWeight: 800, fontSize: 18, cursor: "pointer", boxShadow: C.shLg,
          }}
        >
          Get started
        </button>
        <CookieBanner consent={consent} onAccept={acceptCookies} onReject={rejectCookies} onReset={resetCookies} />
      </div>
    );

    // 1 – People
    if (step === 1) return (
      <OnboardWrap step={step} total={TOTAL_STEPS} title="Who's eating?" subtitle="How many people are you planning meals for?" onBack={() => setStep(0)} onNext={() => setStep(2)}>
        <PeoplePicker value={people} onChange={changePeople} />
      </OnboardWrap>
    );

    // 2 – Diet
    if (step === 2) return (
      <OnboardWrap step={step} total={TOTAL_STEPS} title="Pick your diet" subtitle="We'll tailor every meal and recipe to match." onBack={() => setStep(1)} onNext={() => setStep(3)}>
        <DietPicker value={diet} onChange={setDiet} />
      </OnboardWrap>
    );

    // 3 – Allergies
    if (step === 3) return (
      <OnboardWrap step={step} total={TOTAL_STEPS} title="Any allergies?" subtitle="We'll remove any meals that could be a problem. Skip if none apply." onBack={() => setStep(2)} onNext={() => setStep(4)} nextLabel={allergies.length > 0 ? `Continue (${allergies.length} selected)` : "Skip"}>
        <AllergyPicker value={allergies} onChange={setAllergies} />
      </OnboardWrap>
    );

    // 4 – Dislikes
    if (step === 4) return (
      <OnboardWrap
        step={step} total={TOTAL_STEPS}
        title="Foods to avoid"
        subtitle="Tell us what you'd rather not eat. Search or tap — we'll skip meals featuring these."
        onBack={() => setStep(3)}
        onNext={() => setStep(5)}
        nextLabel={dislikes.length > 0 ? `Continue (${dislikes.length} avoided)` : "Skip"}
      >
        <DislikesPicker value={dislikes} onChange={setDislikes} />
      </OnboardWrap>
    );

    // 5 – Cuisines
    if (step === 5) return (
      <OnboardWrap
        step={step} total={TOTAL_STEPS}
        title="Pick your cooking styles"
        subtitle="Choose up to 2. We'll optimise ingredient overlap across your chosen styles to cut waste."
        onBack={() => setStep(4)}
        onNext={() => setStep(6)}
        nextLabel={selectedCuisines.length > 0 ? "Continue" : "Skip (show all)"}
      >
        <CuisinePicker value={selectedCuisines} onChange={setSelectedCuisines} pairHints={pairHints} />
      </OnboardWrap>
    );

    // 6 – Meal target
    if (step === 6) return (
      <OnboardWrap step={step} total={TOTAL_STEPS} title="How many meals this week?" subtitle="We'll recommend the best combination to minimise leftover ingredients." onBack={() => setStep(5)} onNext={() => setStep(7)}>
        <MealCountPicker value={mealTarget} onChange={setMealTarget} />
      </OnboardWrap>
    );

    // 7 – Budget
    if (step === 7) return (
      <OnboardWrap
        step={step} total={TOTAL_STEPS}
        title="Set your budget"
        subtitle="We'll flag when your basket goes over. Estimate includes all ingredients."
        onBack={() => setStep(6)}
        nextLabel={mealsLoading ? "Checking…" : "Start planning →"}
        nextDisabled={mealsLoading}
        onNext={() => {
          if (mealsLoading) return;
          if (allMeals.length === 0) {
            setOnboardEmpty(true);
            return;
          }
          setStep(TOTAL_STEPS);
        }}
      >
        <BudgetPicker value={budget} onChange={setBudget} people={people} mealTarget={mealTarget} />

        {/* Empty-results warning — shown after CTA when server returned 0 meals.
            Lists exactly which prefs are active so the user can see the conflict,
            then offers targeted recovery actions for each restrictive pref. */}
        {onboardEmpty && (
          <div style={{
            marginTop: 24, padding: "18px 20px", borderRadius: 16,
            background: "#FFF8E1", border: "1.5px solid #F9A825",
            color: "#5D4037",
          }}>
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 6 }}>
              ⚠️ Your preferences are too restrictive
            </div>
            <p style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 12 }}>
              No meals match all your current choices. Active filters:
            </p>
            <ul style={{ fontSize: 13, lineHeight: 1.7, marginBottom: 14, paddingLeft: 18 }}>
              {diet && diet !== "classic" && <li><strong>Diet:</strong> {diet}</li>}
              {allergies.length > 0 && <li><strong>Allergies:</strong> {allergies.join(", ")}</li>}
              {dislikes.length > 0 && <li><strong>Avoid:</strong> {dislikes.slice(0,3).join(", ")}{dislikes.length>3?` +${dislikes.length-3} more`:""}</li>}
              {selectedCuisines.length > 0 && <li><strong>Cuisines:</strong> {selectedCuisines.map(c=>CUISINES.find(x=>x.id===c)?.label||c).join(" + ")}</li>}
            </ul>
            <p style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 14 }}>
              Pick one to relax (your saved preferences stay until you change them):
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {selectedCuisines.length > 0 && (
                <button
                  onClick={() => { setSelectedCuisines([]); setOnboardEmpty(false); }}
                  style={{
                    padding: "11px 16px", borderRadius: 10,
                    border: "1.5px solid #F9A825", background: C.white,
                    fontWeight: 700, fontSize: 13, cursor: "pointer", textAlign: "left",
                  }}
                >
                  🌍 Show all cuisines (clears your cuisine choice)
                </button>
              )}
              {allergies.length > 0 && (
                <button
                  onClick={() => setStep(3)}
                  style={{
                    padding: "11px 16px", borderRadius: 10,
                    border: "1.5px solid #F9A825", background: C.white,
                    fontWeight: 700, fontSize: 13, cursor: "pointer", textAlign: "left",
                  }}
                >
                  🚫 Edit allergies ({allergies.length} selected)
                </button>
              )}
              {dislikes.length > 0 && (
                <button
                  onClick={() => setStep(4)}
                  style={{
                    padding: "11px 16px", borderRadius: 10,
                    border: "1.5px solid #F9A825", background: C.white,
                    fontWeight: 700, fontSize: 13, cursor: "pointer", textAlign: "left",
                  }}
                >
                  🥦 Edit foods to avoid ({dislikes.length})
                </button>
              )}
              <button
                onClick={() => setStep(2)}
                style={{
                  padding: "11px 16px", borderRadius: 10,
                  border: "1.5px solid #F9A825", background: C.white,
                  fontWeight: 700, fontSize: 13, cursor: "pointer", textAlign: "left",
                }}
              >
                🥗 Change diet ({diet})
              </button>
            </div>
          </div>
        )}
      </OnboardWrap>
    );
  }

  /* ═══════════════════════════════════════════
     MAIN APP
  ═══════════════════════════════════════════ */
  // detailMealSafe = safe-shape (no ingredients) — always available if the meal
  // has ever appeared in a /generate response. detailMealFull = full-shape with
  // ingredients, populated async by the /api/meals/:id effect.
  const detailMealSafe = detailId ? (mealsById.get(detailId) || null) : null;
  const detailMealFull = detailId ? (mealDetails.get(detailId) || null) : null;
  const detailMeal = detailMealSafe; // keep legacy name for the non-ingredients fields
  const allTags = [...new Set(allMeals.flatMap(m => m.tags || []))];

  return (
    <div style={{
      minHeight: "100vh", background: C.cream,
      // 80px reserves space for the bottom nav. var(--nl-bottom-inset) is
      // published by CookieBanner while visible so its height is added —
      // prevents the banner from covering scrolled-into-view CTAs. Falls
      // back to 0 when no banner is mounted.
      paddingBottom: "calc(80px + var(--nl-bottom-inset, 0px))",
    }}>

      {/* Global CSS */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:wght@600;700&family=Nunito:wght@500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; font-family: Nunito, sans-serif; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes slideUp { from { transform:translateY(100%); } to { transform:translateY(0); } }
        @keyframes cookGuideIn { from { opacity:0; transform:translateY(14px) scale(.98); } to { opacity:1; transform:translateY(0) scale(1); } }
        @keyframes imagePreviewIn { from { opacity:0; transform:scale(.96); } to { opacity:1; transform:scale(1); } }
        @keyframes popIn { from { transform:scale(0.85); opacity:0; } to { transform:scale(1); opacity:1; } }
        @keyframes fillFadeOut { 0%,85% { opacity:1; } 100% { opacity:0; } }
        /* Full-screen cook guide — flex column: glass top bar, scroll body,
           glass bottom controls. Instruction text sits on a solid card for
           contrast (clarity over decoration); glass stays on the chrome bars. */
        .cook-guide-overlay {
          position: fixed;
          inset: 0;
          z-index: 600;
        }
        .cook-fs-panel {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          background: ${C.cream};
          animation: cookGuideIn .24s ease-out;
        }
        .cook-fs-top {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 14px;
          padding-top: max(12px, env(safe-area-inset-top));
          border-bottom: 1px solid ${C.border};
          box-shadow: ${C.sh};
          z-index: 2;
        }
        .cook-fs-close {
          width: 36px; height: 36px; border-radius: 50%;
          border: 1.5px solid ${C.border};
          color: ${C.charcoal}; cursor: pointer;
          font-size: 18px; line-height: 1;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .cook-fs-body {
          flex: 1;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          padding: 18px 16px 26px;
          width: 100%;
          max-width: 680px;
          margin: 0 auto;
        }
        .cook-fs-bottom {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 14px;
          padding-bottom: max(12px, env(safe-area-inset-bottom));
          border-top: 1px solid ${C.border};
          box-shadow: 0 -2px 12px rgba(26,94,58,.06);
          z-index: 2;
        }
        .cook-fs-all summary { list-style: none; }
        .cook-fs-all summary::-webkit-details-marker { display: none; }
        @media (max-width: 400px) {
          .cook-fs-switch-label { display: none; }
        }
        @media (min-width: 720px) {
          .cook-fs-body { padding: 28px 24px 34px; }
        }
        input[type=range] { -webkit-appearance:none; height:8px; border-radius:99px; background:${C.greenMid}; outline:none; }
        input[type=range]::-webkit-slider-thumb { -webkit-appearance:none; width:22px; height:22px; border-radius:50%; background:${C.green}; cursor:pointer; box-shadow:0 2px 8px rgba(26,94,58,.3); }
      `}</style>

      {/* COOKIE BANNER */}
      <CookieBanner consent={consent} onAccept={acceptCookies} onReject={rejectCookies} onReset={resetCookies} />

      {/* CLEAR ALL MODAL */}
      {showClearModal && (
        <div onClick={() => setShowClearModal(false)} style={{ position:"fixed", inset:0, zIndex:600, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,.45)", backdropFilter:"blur(4px)" }} />
          <div onClick={e => e.stopPropagation()} style={{ position:"relative", background:C.white, borderRadius:20, padding:"28px 24px", width:"min(340px,90vw)", boxShadow:"0 8px 40px rgba(0,0,0,.18)", zIndex:1 }}>
            <div style={{ fontSize:32, textAlign:"center", marginBottom:12 }}>🗑️</div>
            <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:18, textAlign:"center", marginBottom:8, color:C.charcoal }}>Clear all meals?</div>
            <div style={{ fontSize:14, color:C.grey, textAlign:"center", lineHeight:1.6, marginBottom:24 }}>
              This will remove all {selectedMeals.length} meal{selectedMeals.length!==1?"s":""} and clear your week plan. This can't be undone.
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button
                onClick={() => setShowClearModal(false)}
                style={{ flex:1, padding:"12px", borderRadius:10, border:`1.5px solid ${C.border}`, background:C.white, fontWeight:700, fontSize:14, cursor:"pointer", color:C.charcoal }}
              >Cancel</button>
              <button
                onClick={() => {
                  clearAutofillUndoState();
                  setSelectedMeals([]);
                  setWeekPlan({});
                  setCheckedOut(false);
                  setUndoMeals(null);
                  setClearConfirm(false);
                  setShowClearModal(false);
                }}
                style={{ flex:1, padding:"12px", borderRadius:10, border:"none", background:"#C0392B", fontWeight:700, fontSize:14, cursor:"pointer", color:C.white }}
              >Clear all</button>
            </div>
          </div>
        </div>
      )}

      {/* SETTINGS MODAL — preference editor (replaces the old setStep(1) hack
          that dropped users back into onboarding). */}
      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onLogout={authStatus === "authenticated" ? (() => { api.clearJwt(); prefsStore.clearLocal(); setSettingsOpen(false); setAuthStatus("unauthenticated"); }) : null}
        people={people} setPeople={changePeople}
        diet={diet} setDiet={setDiet}
        allergies={allergies} setAllergies={setAllergies}
        dislikes={dislikes} setDislikes={setDislikes}
        selectedCuisines={selectedCuisines} setSelectedCuisines={setSelectedCuisines}
        mealTarget={mealTarget} setMealTarget={setMealTarget}
        budget={budget} setBudget={setBudget}
        pairHints={pairHints}
      />

      {/* TOASTS + UNDO */}
      <div style={{ position:"fixed", top:68, right:12, zIndex:900, display:"flex", flexDirection:"column", gap:7, pointerEvents:"none" }}>
        {toasts.map(t => (
          <div key={t.id} style={{
            padding:"9px 16px", borderRadius:12, fontSize:13, fontWeight:700, boxShadow:C.shMd,
            background: t.type==="warn" ? C.goldPale : t.type==="remove" ? "#FEE2E2" : C.green,
            color: t.type==="warn" ? C.amber : t.type==="remove" ? "#C0392B" : C.white,
            animation:"fadeUp .25s ease", whiteSpace:"nowrap",
          }}>{t.msg}</div>
        ))}
        {/* Undo pill — restores one or many meals in a single click */}
        {undoMeals && undoMeals.length > 0 && (
          <button
            onClick={() => {
              clearUndoDismissTimer();
              const pendingUndoMeals = undoMealsRef.current || undoMeals;
              const result = restoreUndoBatch({
                undoMeals: pendingUndoMeals,
                mealsById,
                selectedMeals: selectedMealsRef.current,
                mealTarget,
              });
              if (result.restored.length === 0) {
                show(result.skippedMessage || "Couldn't restore meal — make room and try again", "warn");
                startUndoDismissTimer(MEAL_REMOVE_UNDO_MS);
                return;
              }
              setMealsCache(prev => {
                const next = new Map(prev);
                for (const e of result.restored) if (e.meal) next.set(e.id, withMealImage(e.meal));
                return next;
              });
              setSelectedMeals(s => {
                const ids = new Set(s);
                for (const e of result.restored) ids.add(e.id);
                return [...ids];
              });
              const restorePlan = applyRemoveUndoWeekPlan({
                restored: result.restored,
                weekPlan: weekPlanRef.current,
              });
              setWeekPlan(restorePlan.weekPlan);
              setServingMap(prev => {
                const next = { ...prev };
                let changed = false;
                for (const e of result.restored) {
                  if (e.serving && next[e.id] == null) {
                    next[e.id] = e.serving;
                    changed = true;
                  }
                }
                return changed ? next : prev;
              });
              setImportedIngredientCache(prev => {
                let changed = false;
                const next = new Map(prev);
                for (const e of result.restored) {
                  if (e.ingredients && isImportedId(e.id)) {
                    next.set(e.id, e.ingredients);
                    changed = true;
                  }
                }
                return changed ? next : prev;
              });
              setCheckedOut(false);
              if (restorePlan.unscheduled.length > 0) {
                show("Meal restored but not scheduled because that day is now occupied.", "warn");
              } else if (result.skipped.length > 0) {
                show(result.skippedMessage, "warn");
              }
              setUndoMeals(null);
            }}
            onMouseEnter={pauseUndoDismissTimer}
            onMouseLeave={resumeUndoDismissTimer}
            onFocus={pauseUndoDismissTimer}
            onBlur={resumeUndoDismissTimer}
            style={{
              padding:"9px 16px", borderRadius:12, fontSize:13, fontWeight:700,
              boxShadow:C.shMd, background:"#1A1A2E", color:C.white,
              border:"none", cursor:"pointer", animation:"fadeUp .25s ease",
              whiteSpace:"nowrap", pointerEvents:"auto",
            }}
          >
            {undoMeals.length === 1 ? `${undoMeals[0].name} removed` : `${undoMeals.length} meals removed`} · Undo
          </button>
        )}
      </div>

      {/* DETAIL MODAL */}
      {detailMeal && (
        <div onClick={() => setDetailId(null)} style={{ position:"fixed", inset:0, zIndex:500, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,.45)", backdropFilter:"blur(4px)" }} />
          <div onClick={e => e.stopPropagation()} style={{
            position:"relative", background:C.white, borderRadius:"24px 24px 0 0",
            width:"100%", maxWidth:500, padding:"26px 22px 36px",
            boxShadow:"0 -16px 60px rgba(0,0,0,.2)", animation:"slideUp .3s ease",
            maxHeight:"88vh", overflowY:"auto", overscrollBehavior:"contain",
          }}>
            <div style={{ width:36, height:4, background:C.border, borderRadius:99, margin:"0 auto 20px" }} />
            {/* Explicit close affordance — beta users couldn't always tell
                that backdrop tap dismisses, especially on desktop. */}
            <button
              onClick={() => setDetailId(null)}
              aria-label="Close meal details"
              className="glass-control"
              style={{
                position:"absolute", top:14, right:14,
                width:34, height:34, borderRadius:"50%",
                border:`1.5px solid ${C.border}`,
                color:C.charcoal, cursor:"pointer",
                fontSize:18, lineHeight:1,
                display:"flex", alignItems:"center", justifyContent:"center",
              }}
            >✕</button>
            <div style={{ display:"flex", gap:14, marginBottom:18 }}>
              {detailMeal.imageUrl ? (
                <button
                  type="button"
                  onClick={() => setImagePreview({ src: detailMeal.imageUrl, name: detailMeal.name, cuisine: detailMeal.cuisine })}
                  aria-label={`View larger image of ${detailMeal.name}`}
                  title="View larger image"
                  style={{
                    width:72, height:72, borderRadius:20, background:`${C.greenPale}`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    flexShrink:0, position:"relative", overflow:"hidden",
                    border:"none", padding:0, cursor:"zoom-in", boxShadow:C.sh,
                  }}
                >
                  <img
                    src={detailMeal.imageUrl}
                    alt=""
                    loading="lazy"
                    style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}
                  />
                  <div style={{ position:"absolute", inset:0, background:"linear-gradient(180deg,rgba(0,0,0,0),rgba(0,0,0,.22))", pointerEvents:"none" }} />
                  <span aria-hidden="true" style={{ position:"absolute", right:6, bottom:5, width:22, height:22, borderRadius:"50%", background:"rgba(0,0,0,.58)", color:C.white, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:800 }}>↗</span>
                </button>
              ) : (
                <div style={{ width:72, height:72, borderRadius:20, background:`${C.greenPale}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:40, flexShrink:0, position:"relative", overflow:"hidden" }}>
                  {detailMeal.emoji}
                </div>
              )}
              <div>
                <div style={{ fontFamily:"Lora,serif", fontSize:20, fontWeight:700, color:C.charcoal, lineHeight:1.2 }}>{detailMeal.name}</div>
                <div style={{ fontSize:13, color:C.grey, marginTop:4 }}>
                  {mealMetaParts({
                    cuisineLabel: CUISINES.find(c=>c.id===detailMeal.cuisine)?.label,
                    time: detailMeal.time,
                    cals: detailMeal.cals,
                  }).join(" · ")}
                </div>
                {typeof detailMeal.sharedIngredientCount === "number" && detailMeal.sharedIngredientCount > 0 && (
                  <div style={{ fontSize:13, color:C.green, fontWeight:700, marginTop:4 }}>
                    ♻️ {detailMeal.sharedIngredientCount} shared ingredients
                  </div>
                )}
              </div>
            </div>
            {detailMeal.subtitle && (
              <p style={{ fontSize:14, color:C.grey, marginBottom:16, lineHeight:1.55 }}>{detailMeal.subtitle}</p>
            )}

            {/* Serving control — default is household size (people), not the
                recipe's own serves count, so it's pre-scaled to the user's
                table size. servingMap[detailId] stores explicit adjustments. */}
            {(() => {
              const currentServings = servingMap[detailId] ?? effectiveServings[detailId] ?? people;
              return (
                <div style={{ background:C.greenPale, borderRadius:14, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                  <span style={{ fontWeight:700, fontSize:14 }}>Servings</span>
                  <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                    {["-","+"].map((lbl,i) => (
                      <button key={lbl} onClick={() => setServingMap(p=>({...p, [detailId]: Math.max(1, Math.min(12, currentServings+(i===0?-1:1)))}))}
                        style={{ width:32, height:32, borderRadius:"50%", border:`2px solid ${C.green}`, background:C.white, color:C.green, fontWeight:700, fontSize:18, cursor:"pointer" }}>
                        {lbl}
                      </button>
                    ))}
                    <span style={{ fontWeight:800, fontSize:20, color:C.green, minWidth:24, textAlign:"center" }}>
                      {currentServings}
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* Ingredients — fetched on demand */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, flexWrap:"wrap", marginBottom:8 }}>
              <div style={{ fontSize:11, fontWeight:800, color:C.grey, textTransform:"uppercase", letterSpacing:".06em" }}>Ingredients</div>
              {/* Measurement display picker — converts qty for display only.
                  Shares measureMode with the imported-recipe modal; never
                  mutates canonical ingredient data, shopping, or matching. */}
              <MeasureModePicker mode={measureMode} onChange={setMeasureMode} />
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:7, marginBottom:20 }}>
              {!detailMealFull && !detailError && (
                <div style={{ padding:"14px 12px", background:C.sand, borderRadius:10, color:C.grey, fontSize:13, textAlign:"center" }}>
                  Loading ingredients…
                </div>
              )}
              {detailError && (
                <div style={{ padding:"14px 12px", background:"#FEF2F2", borderRadius:10, color:"#C0392B", fontSize:13, textAlign:"center" }}>
                  {detailError}
                  <button
                    onClick={() => {
                      setMealDetails(prev => { const n = new Map(prev); n.delete(detailId); return n; });
                      setDetailAttempt(n => n + 1);
                    }}
                    style={{ display:"block", margin:"8px auto 0", padding:"4px 12px", borderRadius:8, border:"1.5px solid #C0392B", background:"transparent", color:"#C0392B", fontWeight:700, fontSize:12, cursor:"pointer" }}
                  >
                    Retry
                  </button>
                </div>
              )}
              {detailMealFull && detailMealFull.ingredients.map((ing, ingIdx) => {
                const displayServings = servingMap[detailId] ?? effectiveServings[detailId] ?? people;
                const factor = displayServings / detailMeal.serves;
                const fromList = shoppingList[ing.id];
                // Centralised resolver — falls back to title-cased slug if
                // the shopping list hasn't named this ingredient yet.
                const label = ing.name || ingredientName(ing.id, shoppingList);
                const unit = fromList?.unit || ing.unit || "g";
                // Defensive: the safe-shape from /api/meals/:id returns `amt`
                // (alias of meals.json `amount`) but if either is ever 0/null
                // we'd render "0g chicken" which beta users have flagged.
                // Coalesce to either field, then guard against zero/NaN.
                const rawAmt = ing.amt ?? ing.amount ?? 0;
                const scaled = rawAmt * factor;
                // Display-only unit conversion. Original mode and unconvertible
                // units (count/clove/etc.) keep the canonical wording untouched;
                // conversion never feeds shopping/matching/pricing.
                let display;
                if (!Number.isFinite(scaled) || scaled <= 0) {
                  display = "—";
                } else {
                  const conv = unit === "count" ? null : convertMeasureForMode(scaled, unit, measureMode);
                  display = conv
                    ? `${_formatConvertedQty(conv.qty, conv.unit)} ${conv.unit}`
                    : `${Math.round(scaled)}${unit === "count" ? "" : unit}`;
                }
                // Dev-only sanity check: flag implausibly small protein/
                // produce amounts so we can fix the underlying data before
                // they reach a user. Never blocks render.
                if (typeof window !== "undefined" && scaled > 0 && scaled < 10 && unit === "g" && fromList?.cat === "Proteins") {
                  // eslint-disable-next-line no-console
                  console.warn(`[NomLoop] Implausible quantity for protein "${ing.id}" in meal "${detailMeal.id}": ${scaled}${unit} (raw=${rawAmt}, factor=${factor})`);
                }
                const subs = ing.subs || [];
                return (
                  <div key={ing.id != null ? ing.id : `unresolved-${ingIdx}`} style={{ display:"flex", flexDirection:"column", gap:5, padding:"9px 12px", background:C.sand, borderRadius:10 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{ flex:1, display:"flex", alignItems:"center", gap:8, minWidth:0, fontSize:13, fontWeight:600, textTransform:"capitalize" }}>
                        <span aria-hidden="true" style={{ fontSize:15, lineHeight:1, flexShrink:0 }}>{ingredientIcon(label)}</span>
                        <span style={{ minWidth:0 }}>{label}</span>
                      </span>
                      <span style={{ fontSize:12, color:C.grey }}>{display}</span>
                      {ingSet.has(ing.id) && (
                        <span style={{ fontSize:11, background:C.greenPale, color:C.green, borderRadius:99, padding:"1px 7px", fontWeight:700 }}>shared</span>
                      )}
                    </div>
                    {subs.length > 0 && (
                      <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                        {subs.map(s => (
                          <span
                            key={s.id}
                            title={`${s.note} (${s.qualityLoss} quality change)`}
                            style={{ fontSize:11, background:C.white, border:`1px solid ${C.border}`, color:C.grey, borderRadius:99, padding:"1px 8px", cursor:"default" }}
                          >
                            ↔ {s.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Allergen / diet tags */}
            {(() => { const b = dietBadge(detailMeal.diets); return b ? (
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:800, color:C.grey, textTransform:"uppercase", letterSpacing:".06em", marginBottom:6 }}>Dietary</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  <span style={{ padding:"3px 10px", borderRadius:99, background:C.tealPale, color:C.teal, fontSize:12, fontWeight:700 }}>{b.emoji} {b.label}</span>
                </div>
              </div>
            ) : null; })()}
            {detailMeal.allergens.length > 0 && (
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:800, color:C.grey, textTransform:"uppercase", letterSpacing:".06em", marginBottom:6 }}>Contains</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {detailMeal.allergens.map(a => (
                    <span key={a} style={{ padding:"3px 10px", borderRadius:99, background:C.goldPale, color:C.amber, fontSize:12, fontWeight:700 }}>{a}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Drink pairings — fetched per cuisine, up to 3 entries.
                Server endpoint: GET /api/cuisines/drink-pairings?first=<cuisine>.
                The effect at the top of this component populates drinkPairings[cuisine]
                when the detail modal opens. Silent-fail: if the fetch errors the modal
                stays useful without the strip. */}
            {(() => {
              const pairs = (drinkPairings[detailMeal.cuisine] || []).slice(0, 3);
              return <DrinkPairingCards pairs={pairs} />;
            })()}

            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <button
                onClick={() => {
                  setDetailId(null);
                  setCookGuideMealId(detailId);
                }}
                style={{
                  width:"100%", padding:13, borderRadius:99, border:`2px solid ${C.green}`,
                  background:C.white, color:C.green,
                  fontWeight:800, fontSize:14, cursor:"pointer",
                }}
              >
                🍳 Cook this
              </button>
              <button
                onClick={() => { toggleMeal(detailId); setDetailId(null); }}
                style={{
                  width:"100%", padding:14, borderRadius:99, border:"none",
                  background: selectedMeals.includes(detailId) ? "#FEE2E2" : C.green,
                  color: selectedMeals.includes(detailId) ? "#C0392B" : C.white,
                  fontWeight:800, fontSize:15, cursor:"pointer",
                }}
              >
                {selectedMeals.includes(detailId) ? "✕ Remove from week" : "+ Add to this week"}
              </button>
            </div>
          </div>
        </div>
      )}

      {imagePreview && (
        <div
          onClick={() => setImagePreview(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`Image preview for ${imagePreview.name}`}
          style={{ position:"fixed", inset:0, zIndex:650, display:"flex", alignItems:"center", justifyContent:"center", padding:18 }}
        >
          <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,.72)", backdropFilter:"blur(8px)" }} />
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position:"relative", width:"min(980px, 94vw)", maxHeight:"90vh",
              display:"flex", flexDirection:"column", gap:12,
              animation:"imagePreviewIn .18s ease both",
            }}
          >
            <button
              onClick={() => setImagePreview(null)}
              aria-label="Close image preview"
              style={{
                position:"absolute", top:12, right:12, zIndex:2,
                width:42, height:42, borderRadius:"50%",
                border:"1.5px solid rgba(255,255,255,.55)", background:"rgba(0,0,0,.48)",
                color:C.white, cursor:"pointer", fontSize:24, lineHeight:1,
                display:"flex", alignItems:"center", justifyContent:"center",
              }}
            >✕</button>
            <div style={{ borderRadius:22, overflow:"hidden", background:"#111", boxShadow:"0 28px 90px rgba(0,0,0,.45)", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <img
                src={imagePreview.src}
                alt={imagePreview.name}
                style={{ width:"100%", maxHeight:"82vh", objectFit:"contain", display:"block" }}
              />
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:10, color:C.white, fontWeight:800, textShadow:"0 1px 8px rgba(0,0,0,.5)" }}>
              <span>{cuisineFlag(imagePreview.cuisine)}</span>
              <span>{imagePreview.name}</span>
            </div>
          </div>
        </div>
      )}

      {/* COOK GUIDE MODAL
          All step content (titles, descriptions, categorised gram amounts)
          comes from /api/cook-guide. This view only groups + paints. */}
      {cookGuideMealId && (() => {
        const cookMeal = mealsById.get(cookGuideMealId);
        const cookServings = effectiveServings[cookGuideMealId] || servingMap[cookGuideMealId] || (cookMeal?.serves ?? people);
        // Which planned day is this meal cooked for? Reverse-lookup the week
        // plan; may be null when cooking a meal that isn't on the plan.
        const cookDay = DAYS.find(d => weekPlan[d] === cookGuideMealId) || null;
        // Device-local weekday (Mon-indexed) — "Tonight" when cooking today's meal.
        const cookTodayName = DAYS[(new Date().getDay() + 6) % 7];
        // Other planned meals this week — targets for "switch meal" (cook
        // target only; never mutates the saved week plan).
        const switchTargets = DAYS
          .map(d => ({ day: d, id: weekPlan[d] }))
          .filter(x => x.id && x.id !== cookGuideMealId);
        // Conservative ingredient vocabulary for in-step green highlighting.
        const allSteps  = Array.isArray(cookGuideSteps) ? cookGuideSteps : [];
        const cookVocab = buildCookVocab(allSteps);
        // The gather step (categorised ingredient list) leads; the remaining
        // steps are the navigable instruction sequence.
        const hasGather  = allSteps[0] && Array.isArray(allSteps[0].categories) && allSteps[0].categories.length > 0;
        const gatherStep = hasGather ? allSteps[0] : null;
        const instrSteps = hasGather ? allSteps.slice(1) : allSteps;
        const stepCount  = instrSteps.length;
        const curIdx     = Math.min(Math.max(0, cookStepIdx), Math.max(0, stepCount - 1));
        const curStep    = instrSteps[curIdx] || null;
        // Ingredient names used in the active step — bold them in the gather
        // list and surface them as chips. Set for O(1) membership checks.
        const curStepUsed = new Set(ingredientsInStep(curStep ? (curStep.desc || curStep.title || "") : "", cookVocab));
        // "Needed now" — the gather items whose names appear in the active step,
        // so the user sees what to grab (with quantities) next to the instruction.
        const gatherItems = [];
        (gatherStep && Array.isArray(gatherStep.categories) ? gatherStep.categories : [])
          .forEach(g => (Array.isArray(g.items) ? g.items : []).forEach(it => gatherItems.push(it)));
        const neededNow = gatherItems.filter(it => curStepUsed.has(String(it.name || "").toLowerCase()));
        const fmtQty = (it) => (Number.isFinite(it.amt) && it.amt > 0)
          ? `${Math.round(it.amt)}${it.unit === "count" || it.unit === "each" ? "" : (it.unit || "g")}`
          : (it.measure || "");
        return (
          <div className="cook-guide-overlay cook-fs" onClick={() => setCookGuideMealId(null)}>
            <div className="cook-fs-panel" onClick={e => e.stopPropagation()}>

              {/* TOP GLASS BAR — close · day + title · step progress */}
              <div className="cook-fs-top glass-nav">
                <button
                  onClick={() => setCookGuideMealId(null)}
                  aria-label="Close cook guide"
                  className="glass-control cook-fs-close"
                >✕</button>
                <div style={{ flex:1, minWidth:0, textAlign:"center", padding:"0 8px" }}>
                  <div style={{ fontSize:11, fontWeight:800, letterSpacing:".07em", textTransform:"uppercase", color:C.green }}>
                    {cookDay
                      ? (cookDay === cookTodayName ? `Tonight · ${cookDay}` : `Cooking for ${cookDay}`)
                      : "Cook guide"}
                  </div>
                  <div style={{ fontFamily:"Lora,serif", fontSize:16, fontWeight:700, color:C.charcoal, lineHeight:1.2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {cookMeal?.emoji ? `${cookMeal.emoji} ` : ""}{cookMeal?.name || "Cooking"}
                  </div>
                </div>
                <div style={{ flexShrink:0, minWidth:38, textAlign:"right" }}>
                  {stepCount > 0 && (
                    <span style={{ fontSize:12, fontWeight:800, color:C.grey, fontVariantNumeric:"tabular-nums" }}>{curIdx + 1}/{stepCount}</span>
                  )}
                </div>
              </div>

              {/* SCROLLABLE BODY — meta, warnings, instruction, needed-now */}
              <div className="cook-fs-body">
                <div style={{ fontSize:12, color:C.grey, marginBottom:14, textAlign:"center" }}>
                  {mealMetaParts({ time: cookMeal?.time, cals: cookMeal?.cals, servings: cookServings }).join(" · ")}
                </div>

              {/* Allergen/diet safety warning for this specific meal */}
              {cookGuideMealId && (() => {
                const conflict = planConflicts.find(c => c.id === cookGuideMealId);
                if (!conflict) return null;
                return (
                  <div style={{ marginBottom:16, padding:"10px 14px", borderRadius:12, background:"#FEF3C7", border:"1px solid #F59E0B", display:"flex", gap:8, alignItems:"flex-start" }}>
                    <span style={{ fontSize:16, flexShrink:0 }}>⚠️</span>
                    <div style={{ fontSize:12, color:"#92400E", fontWeight:600 }}>
                      This meal may not match your restrictions: {conflict.reasons.join(", ")}
                    </div>
                  </div>
                );
              })()}

              {cookMeal?.cuisine && (
                <DrinkPairingCards pairs={drinkPairings[cookMeal.cuisine] || []} compact />
              )}

              {/* Loading / error */}
              {cookGuideLoading && (
                <div style={{ padding:"20px 14px", background:C.sand, borderRadius:12, color:C.grey, fontSize:13, textAlign:"center" }}>
                  Building your cook guide…
                </div>
              )}
              {cookGuideError && !cookGuideLoading && (() => {
                const friendly = cookGuideError.kind === "rate_limit"
                  ? (cookGuideError.retryAfter
                      ? `The cook guide is busy right now. Please try again in ${cookGuideError.retryAfter} seconds.`
                      : "The cook guide is busy right now. Please try again in a moment.")
                  : cookGuideError.kind === "unavailable"
                    ? "The cook guide is temporarily unavailable. Please try again shortly."
                    : "We couldn't load the cook guide right now. Please try again.";
                return (
                  <div style={{ padding:"14px", background:C.goldPale, color:C.amber, borderRadius:12, fontSize:13, fontWeight:600, display:"flex", flexDirection:"column", gap:10 }}>
                    <div>{friendly}</div>
                    <button
                      onClick={() => setCookGuideAttempt(a => a + 1)}
                      style={{ alignSelf:"flex-start", padding:"7px 14px", borderRadius:99, border:`1.5px solid ${C.amber}`, background:C.white, color:C.amber, fontWeight:700, fontSize:12, cursor:"pointer" }}
                    >↻ Retry</button>
                  </div>
                );
              })()}

              {/* Cook guide content — dominant instruction, then what to grab
                  now. Ingredient names in the active step render green. */}
              {cookGuideSteps && !cookGuideLoading && !cookGuideError && (
                <>
                  {/* INSTRUCTION CARD — the focus of the screen (solid for contrast) */}
                  {stepCount > 0 && curStep ? (() => {
                    const bodyText = curStep.desc || curStep.title || "";
                    const heading  = curStep.desc ? curStep.title : null;
                    const segs     = highlightSegments(bodyText, cookVocab);
                    return (
                      <div style={{ background:C.white, borderRadius:20, padding:"22px 20px", border:`1px solid ${C.border}`, boxShadow:C.sh, marginBottom:14 }}>
                        {/* progress — segmented bar, current step elongated */}
                        <div style={{ display:"flex", gap:5, marginBottom:18 }} aria-hidden="true">
                          {instrSteps.map((_, i) => (
                            <span key={i} style={{ flex: i === curIdx ? "0 0 28px" : "1 1 8px", height:7, borderRadius:99, background: i === curIdx ? C.green : (i < curIdx ? C.greenMid : C.sand), transition:"flex-basis .22s ease, background .22s ease" }} />
                          ))}
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
                          <div style={{ width:40, height:40, borderRadius:"50%", background:C.green, color:C.white, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:18, flexShrink:0 }}>{curIdx + 1}</div>
                          <div style={{ fontSize:11, fontWeight:800, color:C.green, textTransform:"uppercase", letterSpacing:".08em" }}>
                            Step {curIdx + 1} of {stepCount}
                          </div>
                        </div>
                        {heading && (
                          <div style={{ fontFamily:"Lora,serif", fontSize:18, fontWeight:700, color:C.charcoal, marginBottom:8 }}>
                            {curStep.icon ? `${curStep.icon} ` : ""}{heading}
                          </div>
                        )}
                        <p key={curIdx} className="cook-step-body" style={{ fontSize:18, color:C.charcoal, lineHeight:1.65, margin:0, fontWeight:500 }}>
                          {segs.map((seg, i) => seg.ingredient
                            ? <span key={i} style={{ color:C.green, fontWeight:800 }}>{seg.text}</span>
                            : <Fragment key={i}>{seg.text}</Fragment>)}
                        </p>
                        {curStep.waste && (
                          <div style={{ marginTop:14, padding:"9px 13px", background:C.goldPale, color:C.amber, borderRadius:12, fontSize:13, fontWeight:600 }}>💡 {curStep.waste}</div>
                        )}
                      </div>
                    );
                  })() : stepCount === 0 ? (
                    <div style={{ background:C.white, borderRadius:20, padding:"22px 20px", border:`1px solid ${C.border}`, boxShadow:C.sh, marginBottom:14, fontSize:15, color:C.grey, textAlign:"center" }}>
                      No cooking steps were saved for this meal. Gather everything below and cook from your source.
                    </div>
                  ) : null}

                  {/* NEEDED NOW — glanceable tiles: big icon for instant
                      recognition (helps non-native readers), name, bold amount.
                      Goal: know ingredient + quantity in under a second. */}
                  {neededNow.length > 0 && (
                    <div className="glass-sheet" style={{ borderRadius:16, padding:"14px 16px", border:`1px solid ${C.border}`, marginBottom:14 }}>
                      <div style={{ fontSize:11, fontWeight:800, color:C.green, textTransform:"uppercase", letterSpacing:".07em", marginBottom:10 }}>Needed now</div>
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(150px, 1fr))", gap:8 }}>
                        {neededNow.map((it, i) => {
                          const qty = fmtQty(it);
                          return (
                            <div key={i} style={{ display:"flex", alignItems:"center", gap:11, padding:"10px 12px", borderRadius:12, background:C.greenPale }}>
                              <span aria-hidden="true" style={{ fontSize:26, lineHeight:1, flexShrink:0 }}>{ingredientIcon(it.name)}</span>
                              <div style={{ minWidth:0, display:"flex", flexDirection:"column", gap:1 }}>
                                <span style={{ fontSize:12, fontWeight:700, color:C.green, textTransform:"capitalize", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{it.name}</span>
                                {qty && <span style={{ fontSize:17, fontWeight:800, color:C.green, fontVariantNumeric:"tabular-nums", lineHeight:1.1 }}>{qty}</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* ALL INGREDIENTS — quieter reference, collapsed by default */}
                  {gatherStep && Array.isArray(gatherStep.categories) && gatherStep.categories.length > 0 && (
                    <details className="cook-fs-all" style={{ marginBottom:8 }}>
                      <summary style={{ cursor:"pointer", fontSize:12, fontWeight:800, color:C.grey, textTransform:"uppercase", letterSpacing:".06em", padding:"8px 2px" }}>
                        🧺 All ingredients
                      </summary>
                      <div className="glass-sheet" style={{ borderRadius:16, padding:"14px 16px", border:`1px solid ${C.border}`, marginTop:6, display:"flex", flexDirection:"column", gap:10 }}>
                        {gatherStep.categories.map(group => (
                          <div key={group.key}>
                            <div style={{ fontSize:11, fontWeight:800, color:C.grey, textTransform:"uppercase", letterSpacing:".06em", marginBottom:6 }}>{group.label}</div>
                            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                              {group.items.map((it, i) => {
                                const usedNow = curStepUsed.has(String(it.name || "").toLowerCase());
                                return (
                                  <div key={`${group.key}-${i}`} style={{ display:"flex", justifyContent:"space-between", fontSize:13 }}>
                                    <span style={{ color: usedNow ? C.green : C.charcoal, fontWeight: usedNow ? 800 : 500, textTransform:"capitalize" }}>{it.name}</span>
                                    <span style={{ color:C.grey, fontVariantNumeric:"tabular-nums" }}>{fmtQty(it)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </>
              )}
              </div>{/* /cook-fs-body */}

              {/* BOTTOM GLASS CONTROLS — switch meal · back · next/done */}
              <div className="cook-fs-bottom glass-nav">
                {switchTargets.length > 0 && (
                  <div style={{ position:"relative", flexShrink:0 }}>
                    <button
                      type="button"
                      className="measure-trigger glass-control"
                      onClick={() => setCookSwitchOpen(o => !o)}
                      aria-haspopup="listbox"
                      aria-expanded={cookSwitchOpen}
                      aria-label="Switch meal"
                      style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"12px 14px", borderRadius:99, border:`1px solid ${C.border}`, cursor:"pointer", fontSize:14, fontWeight:700, color:C.charcoal, lineHeight:1 }}
                    >
                      <span>🔁</span>
                      <span className="cook-fs-switch-label">Switch</span>
                    </button>
                    {cookSwitchOpen && (
                      <div
                        role="listbox"
                        aria-label="Switch to another planned meal"
                        className="glass-sheet measure-pop"
                        style={{ position:"absolute", bottom:"calc(100% + 8px)", left:0, zIndex:5, minWidth:240, padding:5, borderRadius:14, border:"1px solid rgba(255,255,255,0.55)", boxShadow:"0 -8px 34px rgba(26,94,58,0.16), 0 2px 8px rgba(0,0,0,0.06)" }}
                      >
                        {switchTargets.map(t => {
                          const m = mealsById.get(t.id);
                          return (
                            <button
                              key={t.day}
                              type="button"
                              role="option"
                              className="measure-opt"
                              onClick={() => { setCookGuideMealId(t.id); setCookSwitchOpen(false); }}
                              onMouseEnter={e => { e.currentTarget.style.background = C.sand; }}
                              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                              style={{ display:"flex", alignItems:"center", gap:10, width:"100%", padding:"9px 11px", borderRadius:10, border:"none", cursor:"pointer", textAlign:"left", background:"transparent" }}
                            >
                              <span style={{ fontSize:18, flexShrink:0 }}>{m?.emoji || "📋"}</span>
                              <span style={{ minWidth:0, flex:1 }}>
                                <span style={{ display:"block", fontSize:11, fontWeight:800, color:C.green, letterSpacing:".04em" }}>{t.day}</span>
                                <span style={{ display:"block", fontSize:13, fontWeight:600, color:C.charcoal, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m?.name || "Planned meal"}</span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setCookStepIdx(i => Math.max(0, i - 1))}
                  disabled={stepCount === 0 || curIdx === 0}
                  style={{ flex:1, padding:"13px", borderRadius:99, border:`1.5px solid ${C.border}`, background:C.white, color: (stepCount === 0 || curIdx === 0) ? C.greyLight : C.charcoal, fontWeight:700, fontSize:14, cursor: (stepCount === 0 || curIdx === 0) ? "default" : "pointer", opacity: (stepCount === 0 || curIdx === 0) ? .5 : 1 }}
                >← Back</button>
                {stepCount > 0 && curIdx < stepCount - 1 ? (
                  <button
                    type="button"
                    onClick={() => { setCookStepIdx(i => Math.min(stepCount - 1, i + 1)); document.querySelector(".cook-fs-body")?.scrollTo({ top:0, behavior:"smooth" }); }}
                    style={{ flex:2, padding:"13px", borderRadius:99, border:"none", background:C.green, color:C.white, fontWeight:800, fontSize:14, cursor:"pointer" }}
                  >Next step →</button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setCookGuideMealId(null)}
                    style={{ flex:2, padding:"13px", borderRadius:99, border:"none", background:C.green, color:C.white, fontWeight:800, fontSize:14, cursor:"pointer" }}
                  >✓ Done cooking</button>
                )}
              </div>

            </div>
          </div>
        );
      })()}

      {/* TOP BAR */}
      <div className="glass-nav" style={{ borderBottom:`1px solid ${C.border}`, position:"sticky", top:0, zIndex:200, boxShadow:C.sh }}>
        <div style={{ maxWidth:1100, margin:"0 auto", padding:"0 14px", height:58, display:"flex", alignItems:"center", gap:10 }}>
          <BrandLogo size={34} radius={10} style={{ flexShrink:0, boxShadow:"none" }} />
          <span style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:16, color:C.charcoal, flex:1 }}>NomLoop</span>
          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
            {selectedMeals.length > 0 && <>
              <div style={{ padding:"5px 11px", borderRadius:99, background:C.greenPale, color:C.green, fontWeight:700, fontSize:13 }}>🍽️ {selectedMeals.length}/{mealTarget}</div>
              {/* Unassigned-meals nudge — surfaces the two-step day-assign
                  flow that beta users missed. Tappable shortcut to Week. */}
              {(() => {
                const selectedSet = new Set(selectedMeals);
                const assigned = new Set(Object.values(weekPlan).filter(id => selectedSet.has(id)));
                const pending = selectedMeals.filter((id) => !assigned.has(id)).length;
                if (activeTab === "week") return null;
                if (pending === 0 && selectedMeals.length === mealTarget) {
                  return (
                    <div style={{ padding:"5px 11px", borderRadius:99, background:C.greenPale, color:C.green, fontWeight:700, fontSize:13, whiteSpace:"nowrap" }}>
                      ✅ All meals scheduled
                    </div>
                  );
                }
                if (pending === 0) return null;
                return (
                  <button
                    onClick={() => setActiveTab("week")}
                    title={`${pending} meal${pending===1?"":"s"} need${pending===1?"s":""} a day. Open Week tab to schedule.`}
                    aria-label={`${pending} meals unassigned, open Week tab`}
                    style={{
                      padding:"5px 11px", borderRadius:99,
                      background:C.tealPale, color:C.teal,
                      fontWeight:700, fontSize:13,
                      border:"none", cursor:"pointer",
                      whiteSpace:"nowrap",
                    }}
                  >📌 {pending} meal{pending!==1?"s":""} unscheduled</button>
                );
              })()}
              <button
                onClick={() => setShowClearModal(true)}
                title="Clear all meals"
                style={{
                  padding:"5px 8px", borderRadius:99,
                  background:"#FEE2E2", color:"#C0392B",
                  fontWeight:700, fontSize:12, border:"none", cursor:"pointer",
                }}
              >✕ Clear</button>
              <div title={`£${displayCost} spent of £${budget} weekly budget`} style={{ padding:"5px 11px", borderRadius:99, background:isOverBudget?"#FEE2E2":C.greenPale, color:isOverBudget?"#C0392B":C.green, fontWeight:700, fontSize:13, whiteSpace:"nowrap" }}>£{displayCost} <span style={{ opacity:.7, fontWeight:600 }}>of £{budget}</span></div>
              <div title="Higher = less food waste. We pack-round so leftover ingredients are reused across meals." style={{ padding:"5px 11px", borderRadius:99, background:displayEfficiency>=80?C.greenPale:C.goldPale, color:displayEfficiency>=80?C.green:C.amber, fontWeight:700, fontSize:13, cursor:"help" }}>♻️ {displayEfficiency}%</div>
            </>}
            <button onClick={() => setSettingsOpen(true)} title="Preferences" aria-label="Open preferences" style={{ width:34, height:34, border:`1.5px solid ${C.border}`, borderRadius:9, background:C.white, cursor:"pointer", fontSize:15, display:"flex", alignItems:"center", justifyContent:"center" }}>⚙️</button>
          </div>
        </div>
      </div>

      {/* BOTTOM NAV — "Cook" appears only after the user confirms checkout.
          `bottom` reads the CSS var published by CookieBanner so the nav
          stacks above the banner instead of being hidden under it.
          z-index 380 > banner (350) and < modals (500) so the nav stays
          visible while a banner is up but doesn't intrude on modal CTAs. */}
      <div className="glass-nav" style={{ position:"fixed", bottom:"var(--nl-bottom-inset, 0px)", left:0, right:0, borderTop:`1px solid ${C.border}`, zIndex:380, boxShadow:"0 -4px 16px rgba(0,0,0,.06)" }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"flex" }}>
        {[
          {id:"meals",icon:"🍽️",label:"Meals"},
          {id:"week", icon:"📅",label:"Week"},
          {id:"list", icon:"🛒",label:"List"},
          ...(checkedOut ? [{id:"cook",icon:"🍳",label:"Cook"}] : []),
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            flex:1, padding:"9px 4px 7px", border:"none", background:"none", cursor:"pointer",
            display:"flex", flexDirection:"column", alignItems:"center", gap:2,
            color:activeTab===tab.id ? C.green : C.grey,
            fontWeight:700, fontSize:11,
            borderTop:`2.5px solid ${activeTab===tab.id ? C.green : "transparent"}`,
            transition:"all .18s", position:"relative",
          }}>
            <span style={{ fontSize:20 }}>{tab.icon}</span>
            {tab.label}
            {tab.id==="list" && selectedMeals.length>0 && (
              <span style={{ position:"absolute", top:6, right:"calc(50% - 14px)", width:7, height:7, background:C.green, borderRadius:"50%", border:"2px solid white" }} />
            )}
          </button>
        ))}
        </div>
      </div>

      <div style={{ maxWidth:1100, margin:"0 auto", padding:"0 14px" }}>

        {/* ══ MEALS TAB ══ */}
        {activeTab === "meals" && (
          <div>
            <div style={{ padding:"16px 0 12px" }}>

              {/* First-run flow banner — explains NomLoop's core loop so a new
                  user "gets it" in ~5s. Collapses to a compact progress strip once
                  at least one meal is chosen, so it never pushes the grid far down.
                  Pure display: reads selectedMeals/weekPlan/shoppingList state and
                  setActiveTab; changes no selection or shopping-list logic. */}
              {selectedMeals.length === 0 ? (
                <div style={{
                  background:`linear-gradient(135deg, ${C.greenPale} 0%, ${C.tealPale} 100%)`,
                  border:`1px solid ${C.greenMid}`, borderRadius:16,
                  padding:"14px 16px", marginBottom:14,
                }}>
                  <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:17, color:C.green, marginBottom:4 }}>
                    Plan your week with less waste.
                  </div>
                  <div style={{ fontSize:13, color:C.charcoal, opacity:.85, marginBottom:11, lineHeight:1.45 }}>
                    Choose meals, NomLoop reuses shared ingredients, then builds your UK shopping list.
                  </div>
                  <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:6 }}>
                    {["Choose meals","Plan week","Review list","Cook"].map((step, i) => (
                      <div key={step} style={{ display:"flex", alignItems:"center", gap:6 }}>
                        {i > 0 && <span style={{ color:C.greyLight, fontWeight:700 }}>→</span>}
                        <span style={{
                          padding:"4px 10px", borderRadius:99, fontSize:12, fontWeight:700,
                          background: i === 0 ? C.green : C.white,
                          color:      i === 0 ? C.white : C.grey,
                          border:`1px solid ${i === 0 ? C.green : C.border}`,
                          whiteSpace:"nowrap",
                        }}>{step}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (() => {
                const steps = [
                  { label:"Meals selected", done: selectedMeals.length > 0 },
                  { label:"Week planned",   done: Object.keys(weekPlan).length > 0 },
                  { label:"List ready",     done: Object.keys(shoppingList).length > 0 },
                  { label:"Cook unlocked",  done: Object.keys(shoppingList).length > 0 },
                ];
                return (
                  <div style={{
                    background:C.white, border:`1px solid ${C.border}`, borderRadius:12,
                    padding:"8px 12px", marginBottom:12,
                    display:"flex", flexWrap:"wrap", alignItems:"center", gap:6,
                  }}>
                    {steps.map((s, i) => (
                      <div key={s.label} style={{ display:"flex", alignItems:"center", gap:6 }}>
                        {i > 0 && <span style={{ color:C.greyLight, fontWeight:700 }}>→</span>}
                        <span style={{
                          fontSize:12, fontWeight:700, whiteSpace:"nowrap",
                          color: s.done ? C.green : C.grey,
                        }}>
                          {s.done ? "✓ " : ""}{s.label}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Cuisine + diet notes */}
              {cuisineDietNotes.map(({ cuisine, note }) => (
                <div key={cuisine} style={{
                  padding:"12px 16px", borderRadius:14, background:C.tealPale,
                  color:C.teal, fontSize:13, fontWeight:600, marginBottom:10,
                  display:"flex", gap:10, alignItems:"flex-start",
                }}>
                  <span style={{ fontSize:18, flexShrink:0 }}>💡</span>
                  <span>{note}</span>
                </div>
              ))}

              {/* Search */}
              <div style={{ position:"relative", marginBottom:12 }}>
                <span style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", fontSize:14 }}>🔍</span>
                <input
                  placeholder="Search meals…"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{
                    width:"100%", padding:"10px 36px 10px 40px", borderRadius:12,
                    border:`1.5px solid ${C.border}`, background:C.white,
                    fontSize:14, outline:"none", color:C.charcoal, boxShadow:C.sh,
                  }}
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery("")} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", fontSize:15, color:C.grey }}>✕</button>
                )}
              </div>

              {/* Tag filters — multi-select; chips toggle independently, AND across active tags */}
              <div
                role="group"
                aria-label="Filter meals by tag — combine multiple"
                style={{ display:"flex", gap:8, overflowX:"auto", paddingBottom:6, alignItems:"center" }}
              >
                {["vegan","vegetarian","healthy","quick","comfort","spicy","authentic"].map(tag => {
                  const isOn = tagFilters.includes(tag);
                  return (
                    <Chip
                      key={tag}
                      label={tag}
                      selected={isOn}
                      aria-pressed={isOn}
                      onClick={() => setTagFilters(prev => isOn ? prev.filter(t => t !== tag) : [...prev, tag])}
                    />
                  );
                })}
                {tagFilters.length > 0 && (
                  <button
                    onClick={() => setTagFilters([])}
                    aria-label="Clear all tag filters"
                    style={{
                      flexShrink:0, padding:"7px 12px", borderRadius:99,
                      border:`1.5px dashed ${C.border}`, background:"transparent",
                      color:C.grey, fontWeight:600, fontSize:12, cursor:"pointer",
                      whiteSpace:"nowrap",
                    }}
                  >Clear ({tagFilters.length})</button>
                )}
              </div>
            </div>

            {/* Allergen/diet conflict banner — shown when planned meals conflict with saved restrictions */}
            {planConflicts.length > 0 && (
              <div style={{ margin:"4px 0 12px", padding:"12px 14px", borderRadius:14, background:"#FEF3C7", border:"1px solid #F59E0B", display:"flex", gap:10, alignItems:"flex-start" }}>
                <span style={{ fontSize:18, flexShrink:0 }}>⚠️</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:"#92400E" }}>
                    {planConflicts.length} planned meal{planConflicts.length !== 1 ? "s" : ""} may conflict with your restrictions
                  </div>
                  <div style={{ fontSize:12, color:"#B45309", marginTop:3, lineHeight:1.5 }}>
                    {planConflicts.map(c => `${c.name} (${c.reasons.join(", ")})`).join(" · ")}
                  </div>
                  <button
                    onClick={() => {
                      const n = planConflicts.length;
                      if (!window.confirm(
                        `Remove ${n} conflicting meal${n === 1 ? "" : "s"} from your plan?\n\n` +
                        planConflicts.map(c => `• ${c.name}`).join("\n")
                      )) return;
                      // Accumulate all removals into a single undo batch so the
                      // toast can restore every removed meal in one click.
                      batchUndoRef.current = true;
                      setUndoMeals(null); // start a fresh batch
                      try {
                        planConflicts.forEach(c => toggleMeal(c.id));
                      } finally {
                        batchUndoRef.current = false;
                      }
                      show(`${n} conflicting meal${n === 1 ? "" : "s"} removed`, "remove");
                    }}
                    style={{ marginTop:8, fontSize:11, fontWeight:700, padding:"4px 12px", border:"1px solid #F59E0B", borderRadius:99, background:"transparent", color:"#92400E", cursor:"pointer" }}
                  >
                    Remove all
                  </button>
                </div>
              </div>
            )}

            {/* Filtered meals count + auto-fill CTA */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, marginBottom:12, flexWrap:"wrap" }}>
              <div style={{ fontSize:13, color:C.grey, fontWeight:600 }}>
                {filteredMeals.length} meal{filteredMeals.length!==1?"s":""} match your preferences
                {selectedCuisines.length > 0 && ` · ${selectedCuisines.map(c=>CUISINES.find(x=>x.id===c)?.label).join(" & ")}`}
              </div>
              {autofillUndo ? (
                <div
                  style={{
                    padding:"7px 8px 7px 12px", borderRadius:99,
                    border:`1.5px solid ${C.green}`,
                    background:C.greenPale, color:C.green,
                    fontWeight:800, fontSize:13,
                    display:"flex", alignItems:"center", gap:8,
                    boxShadow:"0 2px 8px rgba(26,94,58,.08)",
                  }}
                >
                  <span>✓ {autofillUndo.count} meal{autofillUndo.count === 1 ? "" : "s"} added</span>
                  <button
                    onClick={performAutofillUndo}
                    style={{
                      border:"none", borderRadius:99, background:C.white,
                      color:C.green, fontWeight:800, fontSize:13,
                      padding:"5px 10px", cursor:"pointer",
                      boxShadow:"0 1px 4px rgba(26,94,58,.14)",
                    }}
                  >Undo</button>
                </div>
              ) : selectedMeals.length > 0 && selectedMeals.length < mealTarget && (
                <button
                  onClick={runAutofill}
                  disabled={autofillLoading}
                  style={{
                    padding:"8px 14px", borderRadius:99,
                    border:`1.5px solid ${C.green}`,
                    background: autofillLoading ? C.greenPale : C.white,
                    color: C.green, fontWeight:700, fontSize:13,
                    cursor: autofillLoading ? "progress" : "pointer",
                    display:"flex", alignItems:"center", gap:6,
                    transition:"all .15s",
                  }}
                  title="Picks meals that use up your existing ingredients with the least new waste."
                >
                  {autofillLoading ? "Filling…" : `♻️ Fill remaining ${mealTarget - selectedMeals.length} day${mealTarget - selectedMeals.length === 1 ? "" : "s"}`}
                </button>
              )}
            </div>
            {autofillError && (
              <div style={{ fontSize:12, color:C.amber, marginBottom:10 }}>
                Couldn't auto-fill: {autofillError}
              </div>
            )}

            {/* Recommendations */}
            {selectedMeals.length >= 2 && recommendations.length > 0 && (
              <div style={{ background:C.greenPale, borderRadius:18, padding:"15px 16px", marginBottom:18, border:`1px solid ${C.greenMid}` }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:11 }}>
                  <span style={{ fontSize:15 }}>♻️</span>
                  <div>
                    <div style={{ fontFamily:"Lora,serif", fontWeight:600, color:C.green, fontSize:14 }}>Complete your week with less waste</div>
                    <div style={{ fontSize:12, color:C.grey }}>These meals share ingredients with your picks</div>
                  </div>
                </div>
                <div style={{ display:"flex", gap:9, overflowX:"auto", paddingBottom:2 }}>
                  {recommendations.map(meal => {
                    const sc = meal.sharedIngredientCount || 0;
                    const isAdded = selectedMeals.includes(meal.id);
                    return (
                      <div key={meal.id} onClick={() => toggleMeal(meal.id)} style={{
                        flexShrink:0, background:C.white,
                        border:`1.5px solid ${isAdded ? C.green : C.border}`,
                        borderRadius:14, padding:"10px 13px", cursor:"pointer", minWidth:150,
                        position:"relative", boxShadow:C.sh,
                      }}>
                        {meal.imageUrl ? (
                          <img
                            src={meal.imageUrl}
                            alt=""
                            loading="lazy"
                            style={{ width:44, height:44, borderRadius:12, objectFit:"cover", display:"block", marginBottom:6 }}
                          />
                        ) : (
                          <div style={{ fontSize:26, marginBottom:4 }}>{meal.emoji}</div>
                        )}
                        <div style={{ fontFamily:"Lora,serif", fontSize:13, fontWeight:600, color:C.charcoal, lineHeight:1.2, marginBottom:3 }}>{meal.name}</div>
                        <div style={{ fontSize:11, color:C.grey }}>{sc} shared{formatMealTime(meal.time) ? ` · ${formatMealTime(meal.time)}` : ""}</div>
                        {isAdded && <div style={{ marginTop:4, fontSize:11, fontWeight:700, color:C.green }}>✓ Added</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Meal grid */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:16, paddingBottom:20 }}>
              {filteredMeals.map((meal, idx) => {
                const isAdded = selectedMeals.includes(meal.id);
                const isJustFilled = justFilledIds.includes(meal.id);
                const sc = meal.sharedIngredientCount || 0;
                const isRec = !!recommendations.find(r=>r.id===meal.id);
                const disabled = !isAdded && selectedMeals.length >= mealTarget;
                const displayServings = plannedServingCount({ id: meal.id, servingMap, effectiveServings, people, fallback: meal.serves });
                return (
                  <div key={meal.id} style={{
                    background:C.white, borderRadius:18, overflow:"hidden",
                    border: isAdded ? `2px solid ${C.green}` : isRec ? `1.5px solid ${C.gold}` : "1px solid transparent",
                    boxShadow:isAdded ? C.shMd : C.sh,
                    cursor:disabled?"not-allowed":"pointer", opacity:disabled?.45:1,
                    transition:"all .2s", animation:`fadeUp .3s ease ${idx*20}ms both`,
                    display:"flex", flexDirection:"column",
                  }}
                    onMouseEnter={e => { if(!disabled) { e.currentTarget.style.transform="translateY(-3px)"; e.currentTarget.style.boxShadow=C.shMd; } }}
                    onMouseLeave={e => { e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow=isAdded?C.shMd:C.sh; }}
                  >
                    <div style={{ height:3, background:cuisineTint(meal.cuisine).accent, flexShrink:0 }} />
                    <div onClick={() => !disabled && toggleMeal(meal.id)} style={{
                      height:96, display:"flex", alignItems:"center", justifyContent:"center",
                      background:C.greenPale, position:"relative",
                    }}>
                      {meal.imageUrl ? (
                        <>
                          <img
                            src={meal.imageUrl}
                            alt=""
                            loading="lazy"
                            style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}
                          />
                          <div style={{ position:"absolute", inset:0, background:"linear-gradient(180deg,rgba(0,0,0,.02),rgba(0,0,0,.2))", pointerEvents:"none" }} />
                        </>
                      ) : (
                        <span style={{ fontSize:42 }}>{meal.emoji}</span>
                      )}
                      {isAdded && !isJustFilled && (
                        <div style={{ position:"absolute", top:7, left:7, width:20, height:20, background:C.green, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", color:C.white, fontSize:11, fontWeight:700, animation:"popIn .2s ease" }}>✓</div>
                      )}
                      {isJustFilled && (
                        <div style={{ position:"absolute", top:7, left:7, background:C.greenLight, color:C.green, borderRadius:99, padding:"2px 6px", fontSize:10, fontWeight:800, animation:"fillFadeOut 4s ease forwards" }}>♻️ Filled</div>
                      )}
                      {isRec && !isAdded && (
                        <div style={{ position:"absolute", top:7, left:7, background:C.gold, color:C.white, borderRadius:99, padding:"1px 7px", fontSize:10, fontWeight:800 }}>✨</div>
                      )}
                      {sc > 0 && !isAdded && (
                        <div title={`+${sc} shared ingredient${sc!==1?"s":""} with your current plan`} aria-label={`${sc} shared ingredients with your current plan`} style={{ position:"absolute", right:7, bottom:7, background:`${C.green}22`, color:C.green, borderRadius:99, padding:"1px 7px", fontSize:10, fontWeight:700, cursor:"help" }}>+{sc}</div>
                      )}
                    </div>
                    <div style={{ padding:"10px 12px 13px", display:"flex", flexDirection:"column", flex:1 }}>
                      <div onClick={() => !disabled && toggleMeal(meal.id)} style={{ fontFamily:"Lora,serif", fontSize:13, fontWeight:700, lineHeight:1.25, marginBottom:4, color:C.charcoal }}>{meal.name}</div>
                      {meal.chip && (
                        <div style={{ fontSize:12, color:C.green, background:C.greenPale, borderRadius:99, padding:"2px 8px", marginBottom:5, display:"inline-block", fontWeight:700 }}>{meal.chip.label}</div>
                      )}
                      <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center", marginBottom:6 }}>
                        <span style={{ fontSize:11.5, color:C.grey, fontWeight:600 }}>
                          {[
                            formatMealTime(meal.time) ? `⏱ ${formatMealTime(meal.time)}` : null,
                            displayServings ? `👤 ${displayServings}` : null,
                            (Number.isFinite(Number(meal.cals)) && Number(meal.cals) > 0) ? `🔥 ~${meal.cals}` : null,
                          ].filter(Boolean).join("  ·  ")}
                        </span>
                        {(() => { const b = dietBadge(meal.diets); return b ? <span style={{ padding:"2px 8px", borderRadius:99, background:C.tealPale, color:C.teal, fontWeight:600, fontSize:11 }}>{b.emoji} {b.label}</span> : null; })()}
                        {meal.allergens?.length > 0 && <span style={{ padding:"2px 8px", borderRadius:99, background:C.goldPale, color:C.amber, fontWeight:600, fontSize:11 }}>⚠️ {meal.allergens.slice(0,2).join(", ")}{meal.allergens.length>2?` +${meal.allergens.length-2}`:""}</span>}
                      </div>
                      <button onClick={() => setDetailId(meal.id)} style={{
                        width:"100%", padding:"7px", borderRadius:8,
                        border:"none", background:C.cream,
                        fontWeight:700, color:C.green, cursor:"pointer", fontSize:12,
                        marginTop:"auto",
                      }}>Details</button>
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredMeals.length === 0 && (
              <div style={{ textAlign:"center", padding:"40px 20px", color:C.grey }}>
                <div style={{ fontSize:48, marginBottom:12 }}>🍽️</div>
                {allMeals.length === 0 ? (
                  /* Server-zero — saved preferences exclude everything. */
                  <div style={{ textAlign:"left", maxWidth:440, margin:"0 auto", background:"#FFF8E1", border:"1.5px solid #F9A825", borderRadius:16, padding:"18px 20px", color:"#5D4037" }}>
                    <div style={{ fontFamily:"Lora,serif", fontSize:17, fontWeight:700, marginBottom:6, color:"#5D4037" }}>Your preferences are too restrictive</div>
                    <p style={{ fontSize:13, lineHeight:1.55, marginBottom:12 }}>
                      No meals match all your current choices. Active filters:
                    </p>
                    <ul style={{ fontSize:13, lineHeight:1.7, marginBottom:12, paddingLeft:18 }}>
                      {diet && diet !== "classic" && <li><strong>Diet:</strong> {diet}</li>}
                      {allergies.length > 0 && <li><strong>Allergies:</strong> {allergies.join(", ")}</li>}
                      {dislikes.length > 0 && <li><strong>Avoid:</strong> {dislikes.slice(0,3).join(", ")}{dislikes.length>3?` +${dislikes.length-3} more`:""}</li>}
                      {selectedCuisines.length > 0 && <li><strong>Cuisines:</strong> {selectedCuisines.map(c=>CUISINES.find(x=>x.id===c)?.label||c).join(" + ")}</li>}
                    </ul>
                    <p style={{ fontSize:13, lineHeight:1.55, marginBottom:12 }}>
                      Pick one to relax. Your saved preferences stay until you change them in Settings.
                    </p>
                    <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
                      {selectedCuisines.length > 0 && (
                        <button
                          onClick={() => setSelectedCuisines([])}
                          style={{ padding:"11px 16px", borderRadius:10, border:"1.5px solid #F9A825", background:C.white, fontWeight:700, fontSize:13, cursor:"pointer", textAlign:"left", color:"#5D4037" }}
                        >🌍 Show all cuisines (clears cuisine choice)</button>
                      )}
                      {allergies.length > 0 && (
                        <button
                          onClick={() => setSettingsOpen(true)}
                          style={{ padding:"11px 16px", borderRadius:10, border:"1.5px solid #F9A825", background:C.white, fontWeight:700, fontSize:13, cursor:"pointer", textAlign:"left", color:"#5D4037" }}
                        >🚫 Edit allergies in Settings ({allergies.length} active)</button>
                      )}
                      {dislikes.length > 0 && (
                        <button
                          onClick={() => setSettingsOpen(true)}
                          style={{ padding:"11px 16px", borderRadius:10, border:"1.5px solid #F9A825", background:C.white, fontWeight:700, fontSize:13, cursor:"pointer", textAlign:"left", color:"#5D4037" }}
                        >🥦 Edit foods to avoid ({dislikes.length})</button>
                      )}
                      <button
                        onClick={() => setSettingsOpen(true)}
                        style={{ padding:"11px 16px", borderRadius:10, border:"1.5px solid #F9A825", background:C.white, fontWeight:700, fontSize:13, cursor:"pointer", textAlign:"left", color:"#5D4037" }}
                      >🥗 Change diet ({diet}) in Settings</button>
                    </div>
                  </div>
                ) : (
                  /* Filter-zero — saved prefs are fine, temporary search/tag filter is the issue. */
                  <>
                    <div style={{ fontFamily:"Lora,serif", fontSize:18, fontWeight:600, marginBottom:8 }}>No meals match your search</div>
                    <div style={{ fontSize:14, marginBottom:16 }}>Your search or filter chips are hiding all meals. Saved preferences stay either way.</div>
                    <button onClick={() => { setTagFilters([]); setSearchQuery(""); }} style={{ padding:"10px 20px", borderRadius:99, border:`1.5px solid ${C.green}`, background:C.greenPale, color:C.green, fontWeight:700, cursor:"pointer" }}>Clear search and filters</button>
                  </>
                )}

                {/* Request a new cooking style */}
                <div style={{ marginTop:36, borderTop:`1px solid ${C.border}`, paddingTop:28 }}>
                  <div style={{ fontFamily:"Lora,serif", fontSize:15, fontWeight:600, color:C.charcoal, marginBottom:6 }}>
                    Don't see your favourite style?
                  </div>
                  <div style={{ fontSize:13, color:C.grey, marginBottom:16 }}>
                    Tell us what you'd love — we'll add it.
                  </div>
                  {styleReqSent ? (
                    <div style={{ fontSize:14, color:C.green, fontWeight:700 }}>
                      ✓ Requested! We'll be in touch.
                    </div>
                  ) : (
                    <form
                      onSubmit={async e => {
                        e.preventDefault();
                        setStyleReqLoading(true);
                        try {
                          await api.submitInbound({
                            channel: "style-request",
                            name:    styleReqEmail.split("@")[0],
                            email:   styleReqEmail,
                            message: styleReqStyle,
                          });
                          setStyleReqSent(true);
                        } catch { /* swallow — non-critical */ }
                        finally { setStyleReqLoading(false); }
                      }}
                      style={{ display:"flex", flexDirection:"column", gap:10, maxWidth:300, margin:"0 auto" }}
                    >
                      <input
                        type="text"
                        placeholder="e.g. Ethiopian, Peruvian…"
                        value={styleReqStyle}
                        onChange={e => setStyleReqStyle(e.target.value)}
                        required
                        style={{ padding:"10px 14px", borderRadius:10, border:`1.5px solid ${C.border}`, fontSize:14, fontFamily:"inherit", outline:"none" }}
                      />
                      <input
                        type="email"
                        placeholder="your@email.com"
                        value={styleReqEmail}
                        onChange={e => setStyleReqEmail(e.target.value)}
                        required
                        style={{ padding:"10px 14px", borderRadius:10, border:`1.5px solid ${C.border}`, fontSize:14, fontFamily:"inherit", outline:"none" }}
                      />
                      <button
                        type="submit"
                        disabled={styleReqLoading}
                        style={{ padding:"10px 20px", borderRadius:99, border:"none", background:C.green, color:C.white, fontWeight:700, fontSize:14, fontFamily:"inherit", cursor:styleReqLoading ? "default" : "pointer" }}
                      >
                        {styleReqLoading ? "Sending…" : "Request this style →"}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            )}

            {/* ── My Recipes section (import allowlist users only) ──────── */}
            {api.getJwt() && (
              <div style={{ marginTop:28, paddingTop:22, borderTop:`1.5px solid ${C.border}` }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
                  <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:16, color:C.charcoal }}>My Recipes</div>
                  <button
                    onClick={() => setImportSheetOpen(true)}
                    style={{
                      display:"flex", alignItems:"center", gap:6,
                      padding:"8px 14px", borderRadius:99,
                      border:"none", background:C.green, color:C.white,
                      fontWeight:700, fontSize:13, cursor:"pointer",
                    }}
                  >+ Add recipe</button>
                </div>

                {recipeLibrary.length === 0 ? (
                  <div style={{ textAlign:"center", padding:"28px 16px", color:C.grey, background:C.sand, borderRadius:14 }}>
                    <div style={{ fontSize:32, marginBottom:10 }}>📋</div>
                    <div style={{ fontWeight:700, fontSize:14, color:C.charcoal, marginBottom:6 }}>No recipes yet</div>
                    <div style={{ fontSize:13, marginBottom:14, lineHeight:1.5 }}>Paste a URL or type one in to start planning.</div>
                    <button
                      onClick={() => setImportSheetOpen(true)}
                      style={{ padding:"10px 20px", borderRadius:99, border:"none", background:C.green, color:C.white, fontWeight:700, fontSize:13, cursor:"pointer" }}
                    >+ Add recipe</button>
                  </div>
                ) : filteredRecipeLibrary.length === 0 ? (
                  <div style={{ textAlign:"center", padding:"24px 16px", color:C.grey, background:C.sand, borderRadius:14 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:C.charcoal, marginBottom:6 }}>No recipes match your search</div>
                    <div style={{ fontSize:13, lineHeight:1.5 }}>Try a different search term or clear the tag filters above.</div>
                  </div>
                ) : (
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:16 }}>
                    {filteredRecipeLibrary.map(recipe => {
                      const sharedCount = recipeSharedCounts.get(recipe.id) || 0;
                      const isAdded = selectedMeals.includes(recipe.id);
                      const isWasteRec = sharedCount > 0 && !isAdded;
                      const sourceDisplayName = recipeSourceDisplayName(recipe);
                      const recipePhoto = importedRecipeImageUrl(recipe);
                      const displayServings = plannedServingCount({ id: recipe.id, servingMap, effectiveServings, people, fallback: recipe.servings ?? recipe.serves ?? 2 });
                      return (
                      <div
                        key={recipe.id}
                        title={isWasteRec ? `${sharedCount} shared ingredient${sharedCount === 1 ? "" : "s"} with your current plan` : undefined}
                        style={{
                          background:C.white, borderRadius:18, overflow:"hidden",
                          border:`2px solid ${isAdded ? C.green : isWasteRec ? C.gold : C.border}`,
                          boxShadow:isAdded ? C.shMd : isWasteRec ? C.shMd : C.sh,
                          display:"flex", flexDirection:"column",
                        }}
                      >
                        {deleteConfirmId === recipe.id ? (
                          <div style={{ padding:"14px", display:"flex", flexDirection:"column", gap:8 }}>
                            <div style={{ fontSize:12, fontWeight:700, color:C.charcoal }}>Delete this recipe?</div>
                            <div style={{ display:"flex", gap:6 }}>
                              <button
                                onClick={async () => {
                                  try {
                                    await api.deleteRecipe(recipe.id);
                                    setRecipeLibrary(prev => prev.filter(r => r.id !== recipe.id));
                                    setRecipeLibraryIngredientCache(prev => { const n = new Map(prev); n.delete(recipe.id); return n; });
                                    setRecentlySavedRecipeIds(prev => { const n = new Set(prev); n.delete(recipe.id); return n; });
                                    if (selectedMeals.includes(recipe.id)) {
                                      clearAutofillUndoState();
                                      setSelectedMeals(s => s.filter(id => id !== recipe.id));
                                      setWeekPlan(p => { const n={...p}; Object.keys(n).forEach(d=>{ if(n[d]===recipe.id) delete n[d]; }); return n; });
                                    }
                                  } catch { show("Couldn't delete — try again", "warn"); }
                                  setDeleteConfirmId(null);
                                }}
                                style={{ flex:1, padding:"5px 0", borderRadius:8, border:"none", background:"#FEE2E2", color:"#C0392B", fontWeight:700, fontSize:12, cursor:"pointer" }}
                              >Delete</button>
                              <button
                                onClick={() => setDeleteConfirmId(null)}
                                style={{ flex:1, padding:"5px 0", borderRadius:8, border:`1px solid ${C.border}`, background:C.white, color:C.grey, fontWeight:700, fontSize:12, cursor:"pointer" }}
                              >Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div style={{ height:3, background:cuisineTint(recipe.cuisine).accent, flexShrink:0 }} />
                            {/* Hero image */}
                            <div
                              onClick={() => openRecipeDetail(recipe.id)}
                              style={{ height:96, display:"flex", alignItems:"center", justifyContent:"center", background:C.greenPale, position:"relative", cursor:"pointer" }}
                            >
                              {recipePhoto ? (
                                <>
                                  <img
                                    src={recipePhoto}
                                    alt=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer"
                                    onError={e => { e.currentTarget.style.display = "none"; }}
                                    style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}
                                  />
                                  <div style={{ position:"absolute", inset:0, background:"linear-gradient(180deg,rgba(0,0,0,.02),rgba(0,0,0,.2))", pointerEvents:"none" }} />
                                </>
                              ) : (
                                <span style={{ fontSize:42 }}>{recipeEmoji(recipe.title)}</span>
                              )}
                              <button
                                onClick={e => { e.stopPropagation(); setDeleteConfirmId(recipe.id); }}
                                aria-label={`Delete ${recipe.title}`}
                                style={{ position:"absolute", top:7, left:7, width:22, height:22, borderRadius:"50%", border:"none", background:"rgba(255,255,255,.85)", color:C.grey, cursor:"pointer", fontSize:11, display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}
                              >✕</button>
                              {isAdded && (
                                <div style={{ position:"absolute", top:7, left:36, width:20, height:20, background:C.green, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", color:C.white, fontSize:11, fontWeight:700 }}>✓</div>
                              )}
                              {isWasteRec && (
                                <div aria-label={`${sharedCount} shared ingredients with your current plan`} style={{ position:"absolute", bottom:7, right:7, background:`${C.green}22`, color:C.green, borderRadius:99, padding:"1px 7px", fontSize:10, fontWeight:700 }}>+{sharedCount}</div>
                              )}
                            </div>
                            {/* Info */}
                            <div style={{ padding:"10px 12px 13px", display:"flex", flexDirection:"column", flex:1 }}>
                              <div
                                onClick={() => openRecipeDetail(recipe.id)}
                                title={recipe.title}
                                style={{ fontFamily:"Lora,serif", fontSize:13, fontWeight:700, lineHeight:1.25, marginBottom:4, color:C.charcoal, cursor:"pointer", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}
                              >
                                {recipe.title}
                              </div>
                              {sourceDisplayName && (
                                <div style={{ fontSize:11, color:C.grey, marginBottom:4 }}>via {sourceDisplayName}</div>
                              )}
                              <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:6 }}>
                                {displayServings && (
                                  <span style={{ padding:"3px 8px", borderRadius:10, background:C.greenPale, color:C.charcoal, fontWeight:600, fontSize:11 }}>👤{displayServings}</span>
                                )}
                                {allergies.length > 0 && (
                                  <span style={{ padding:"3px 8px", borderRadius:99, background:C.goldPale, color:C.amber, fontWeight:600, fontSize:11 }}>⚠️ allergens unchecked</span>
                                )}
                              </div>
                              <button onClick={() => openRecipeDetail(recipe.id)} style={{
                                width:"100%", padding:"7px", borderRadius:8,
                                border:`1px solid ${C.border}`, background:C.white,
                                fontWeight:700, color:C.charcoal, cursor:"pointer", fontSize:12,
                                marginTop:"auto",
                              }}>Details</button>
                            </div>
                          </>
                        )}
                      </div>
                    );})}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ══ WEEK TAB ══ */}
        {activeTab === "week" && (() => {
          // Unassigned-meal stats — surfaces "X meals still need a day" so
          // users don't lose track of a partially-planned week. Beta users
          // missed this because the per-day chips only appeared on empty
          // days, never giving a global view.
          const assignedIds = new Set(Object.values(weekPlan));
          const unassigned = selectedMeals.filter((id) => !assignedIds.has(id));
          const scheduledCount = selectedMeals.filter((id) => assignedIds.has(id)).length;
          const weekStatus = planningStatus({
            targetMealsPerWeek: mealTarget,
            selectedCount: selectedMeals.length,
            scheduledCount,
            unscheduledCount: unassigned.length,
          });
          return (
          <div style={{ padding:"20px 0" }}>
            <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:24, color:C.green, marginBottom:6 }}>Weekly Planner</div>
            <div style={{ fontSize:14, color:C.grey, marginBottom:20 }}>
              {scheduledCount} of 7 days planned · {selectedMeals.length}/{mealTarget} meals selected
            </div>

            {selectedMeals.length === 0 ? (
              <div style={{ textAlign:"center", padding:"60px 20px", color:C.grey }}>
                <div style={{ fontSize:48, marginBottom:12 }}>📅</div>
                <div style={{ fontFamily:"Lora,serif", fontSize:18, fontWeight:600, marginBottom:8 }}>No meals planned yet.</div>
                <div style={{ fontSize:14 }}>Head to the Meals tab to start building your week.</div>
                <button onClick={() => setActiveTab("meals")} style={{ marginTop:16, padding:"12px 24px", borderRadius:99, border:"none", background:C.green, color:C.white, fontWeight:700, cursor:"pointer" }}>Browse meals</button>
              </div>
            ) : (
              <>
                {/* Summary */}
                <div style={{ display:"flex", gap:10, marginBottom:20, overflowX:"auto" }}>
                  {[
                    { label:"Selected", value:`${selectedMeals.length}/${mealTarget}`, icon:"🍽️" },
                    { label:"Cost", value:`£${displayCost}`, icon:isOverBudget?"⚠️":"💰" },
                    {
                      label:"Efficiency", value:`${displayEfficiency}%`, icon:"♻️",
                      hint:"Higher = less food waste. We use as much of every pack as we can.",
                    },
                  ].map(s => (
                    <div key={s.label} title={s.hint} style={{ flex:1, minWidth:90, background:C.white, borderRadius:14, padding:"12px 14px", boxShadow:C.sh, textAlign:"center", cursor: s.hint ? "help" : "default" }}>
                      <div style={{ fontSize:20, marginBottom:4 }}>{s.icon}</div>
                      <div style={{ fontWeight:800, fontSize:16, color:C.charcoal }}>{s.value}</div>
                      <div style={{ fontSize:12, color:C.grey }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Unassigned banner — only when at least one meal still needs a day. */}
                {unassigned.length > 0 && (
                  <div
                    role="status"
                    style={{
                      background:C.tealPale, color:C.teal, borderRadius:14,
                      padding:"12px 14px", marginBottom:14,
                      display:"flex", alignItems:"center", gap:10,
                      border:`1px solid ${C.teal}22`,
                    }}
                  >
                    <span style={{ fontSize:18 }}>📌</span>
                    <div style={{ flex:1, fontSize:13, fontWeight:600, lineHeight:1.4 }}>
                      {unassigned.length} meal{unassigned.length === 1 ? "" : "s"} still need{unassigned.length === 1 ? "s" : ""} a day. Tap a chip below to schedule
                      {unassigned.length <= 2 ? `: ${unassigned.map((id) => mealName(id, mealsById)).join(", ")}.` : "."}
                    </div>
                  </div>
                )}

                {/* Day grid */}
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {DAYS.map(day => {
                    const mealId = weekPlan[day];
                    const meal = mealId ? mealsById.get(mealId) : null;
                    return (
                      <div key={day} style={{ background:C.white, borderRadius:16, padding:16, boxShadow:C.sh, display:"flex", alignItems:"center", gap:12 }}>
                        <div style={{ width:40, fontWeight:800, fontSize:14, color:C.green, flexShrink:0 }}>{day}</div>
                        {meal ? (
                          <>
                            <span style={{ fontSize:24 }}>{meal.emoji}</span>
                            <div style={{ flex:1, minWidth:0 }}>
                              <FastTitleTooltip label={mealName(mealId, mealsById)}>
                                <div
                                  aria-label={mealName(mealId, mealsById)}
                                  tabIndex={0}
                                  style={{ fontWeight:700, fontSize:14, color:C.charcoal, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", cursor:"help" }}
                                >
                                  {mealName(mealId, mealsById)}
                                </div>
                              </FastTitleTooltip>
                              <div style={{ fontSize:12, color:C.grey }}>
                                {meal.isImported
                                  ? (recipeSourceDisplayName(meal) ? `via ${recipeSourceDisplayName(meal)}` : "Imported recipe")
                                  : `${formatMealTime(meal.time)} · ${effectiveServings[mealId]||meal.serves} serving${(effectiveServings[mealId]||meal.serves)!==1?"s":""}`}
                              </div>
                            </div>
                            {/* Move = open reassign modal for this day. Replaces
                                the previous remove-and-re-pick flow. */}
                            <button
                              onClick={() => setReassignDay(day)}
                              aria-label={`Move ${mealName(mealId, mealsById)} to another day`}
                              title="Move to another day"
                              style={{ padding:"6px 10px", borderRadius:8, border:`1px solid ${C.border}`, background:C.white, color:C.green, fontWeight:600, cursor:"pointer", fontSize:12, marginRight:6 }}
                            >↔ Move</button>
                            <button
                              onClick={() => removeMealWithUndo(mealId)}
                              aria-label={`Remove ${mealName(mealId, mealsById)} from ${day}`}
                              title={`Remove ${mealName(mealId, mealsById)} from ${day}`}
                              style={{ padding:"6px 12px", borderRadius:8, border:`1px solid ${C.border}`, background:C.white, color:C.grey, fontWeight:600, cursor:"pointer", fontSize:12 }}
                            >✕</button>
                          </>
                        ) : (
                          <>
                            <div style={{ flex:1, color:C.greyLight, fontSize:14, fontStyle:"italic" }}>No meal planned</div>
                            <div style={{ display:"flex", gap:6, overflowX:"auto" }}>
                              {unassigned.slice(0, 3).map(id => {
                                const m = mealsById.get(id);
                                // Show full meal name (no .split.slice). chip
                                // truncates with CSS ellipsis instead.
                                const fullName = mealName(id, mealsById);
                                return (
                                  <FastTitleTooltip key={id} label={fullName}>
                                    <button
                                      onClick={() => assignDay(day, id)}
                                      aria-label={`Assign ${fullName} to ${day}`}
                                      style={{
                                        flexShrink:0, padding:"6px 10px", borderRadius:8,
                                        border:`1px solid ${C.border}`, background:C.greenPale,
                                        color:C.green, fontWeight:600, cursor:"pointer", fontSize:12,
                                        display:"flex", alignItems:"center", gap:5,
                                        maxWidth:160, overflow:"hidden",
                                        textOverflow:"ellipsis", whiteSpace:"nowrap",
                                      }}
                                    >
                                      <span aria-hidden="true">{m?.emoji}</span>
                                      <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{fullName}</span>
                                    </button>
                                  </FastTitleTooltip>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Next-step CTA — once any meal is scheduled, point users at
                    the Shopping List. Beta users got stuck on Week tab not
                    knowing where to go next. Full prominence when nothing's
                    unassigned, softer-but-visible while there's still a meal
                    to schedule. */}
                {(selectedMeals.length > 0 || scheduledCount > 0) && (
                  <div style={{ marginTop:24, padding:"18px 20px", borderRadius:16, background:weekStatus.complete ? C.greenPale : C.cream, border:`1.5px solid ${weekStatus.complete ? C.green : C.border}` }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:14, flexWrap:"wrap" }}>
                      <div>
                        <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:16, color:C.charcoal, marginBottom:2 }}>
                          {weekStatus.complete ? "✅ " : ""}{weekStatus.title}
                        </div>
                        <div style={{ fontSize:13, color:C.grey }}>
                          {weekStatus.detail}
                        </div>
                      </div>
                      <button
                        onClick={() => setActiveTab("list")}
                        style={{ padding:"12px 22px", borderRadius:99, border:"none", background:C.green, color:C.white, fontWeight:800, fontSize:14, cursor:"pointer", whiteSpace:"nowrap" }}
                      >🛒 Go to Shopping List</button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Reassign modal — open when the user taps Move on a day's
                slotted meal. Picks a target day; assigning swaps the
                placement so we never end up with two meals on one day. */}
            {reassignDay && (() => {
              const movingId = weekPlan[reassignDay];
              if (!movingId) { setReassignDay(null); return null; }
              const movingName = mealName(movingId, mealsById);
              return (
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-label={`Move ${movingName}`}
                  onClick={() => setReassignDay(null)}
                  style={{ position:"fixed", inset:0, zIndex:550, display:"flex", alignItems:"flex-end", justifyContent:"center" }}
                >
                  <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,.45)", backdropFilter:"blur(4px)" }} />
                  <div onClick={(e) => e.stopPropagation()} style={{
                    position:"relative", background:C.white, borderRadius:"24px 24px 0 0",
                    width:"100%", maxWidth:480, padding:"24px 22px 32px",
                    boxShadow:"0 -16px 60px rgba(0,0,0,.2)", animation:"slideUp .3s ease",
                  }}>
                    <div style={{ width:36, height:4, background:C.border, borderRadius:99, margin:"0 auto 14px" }} />
                    <div style={{ fontFamily:"Lora,serif", fontSize:18, fontWeight:700, color:C.charcoal, marginBottom:6 }}>
                      Move "{movingName}"
                    </div>
                    <div style={{ fontSize:13, color:C.grey, marginBottom:14 }}>
                      Currently on {reassignDay}. Pick a new day:
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))", gap:8 }}>
                      {DAYS.filter((d) => d !== reassignDay).map((d) => {
                        const occupant = weekPlan[d];
                        const occupantName = occupant ? mealName(occupant, mealsById) : null;
                        return (
                          <button
                            key={d}
                            onClick={() => {
                              // Swap: if target day has a meal, push it to the
                              // origin day so neither ends up unassigned.
                              clearAutofillUndoState();
                              setWeekPlan((prev) => {
                                const next = { ...prev };
                                next[d] = movingId;
                                if (occupant) next[reassignDay] = occupant;
                                else delete next[reassignDay];
                                return next;
                              });
                              setReassignDay(null);
                              show(occupant ? `Swapped ${movingName} ↔ ${occupantName}` : `${movingName} → ${d}`);
                            }}
                            style={{
                              padding:"10px 8px", borderRadius:10,
                              border:`1px solid ${C.border}`,
                              background:occupant ? C.goldPale : C.greenPale,
                              color:occupant ? C.amber : C.green,
                              fontWeight:700, fontSize:12, cursor:"pointer",
                              textAlign:"left", lineHeight:1.3,
                            }}
                          >
                            <div>{d}</div>
                            {occupant && (
                              <div style={{ fontSize:10, fontWeight:600, marginTop:2, opacity:.85, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                ↔ {occupantName}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => setReassignDay(null)}
                      style={{
                        marginTop:14, width:"100%", padding:11, borderRadius:99,
                        border:`1.5px solid ${C.border}`, background:C.white,
                        color:C.charcoal, fontWeight:700, fontSize:13, cursor:"pointer",
                      }}
                    >Cancel</button>
                  </div>
                </div>
              );
            })()}
          </div>
          );
        })()}

        {/* ══ LIST TAB ══ */}
        {activeTab === "list" && (
          <div style={{ padding:"20px 0" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
              <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:24, color:C.green }}>Shopping List</div>
              {(Object.keys(shoppingList).length > 0 || buildManualItems.length > 0) && (
                <button onClick={copyList} style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 14px", borderRadius:10, border:`1.5px solid ${C.border}`, background:C.white, color:C.green, fontWeight:700, fontSize:13, cursor:"pointer" }}>
                  📋 {copied ? "Copied!" : "Copy"}
                </button>
              )}
            </div>
            <div style={{ fontSize:14, color:C.grey, marginBottom:20 }}>
              {/* Numeric summary lives in the "This week's shop" card below when the
                  plan has meals; here it only covers the empty state to avoid two
                  stacked identical count lines. Conflict flag stays in both. */}
              {planMealIds.length === 0 && (
                <>{selectedMealObjs.length} meal{selectedMealObjs.length!==1?"s":""} · {Object.keys(shoppingList).length + buildManualItems.length} items · est. £{displayCost}</>
              )}
              {planConflicts.length > 0 && (
                <span style={{ color:"#92400E", fontWeight:700 }}>{planMealIds.length === 0 ? " · " : ""}includes {planConflicts.length} conflicted meal{planConflicts.length === 1 ? "" : "s"}</span>
              )}
            </div>

            {/* Retailer selector — always rendered when the plan has meals.
                Uses the server's budget-gated list when available, falling back
                to FALLBACK_RETAILERS so the selector never disappears if the
                retailers API is slow or unavailable. Previously gated on
                availableRetailers.length > 0, which hid the selector entirely
                whenever the API call failed. */}
            {planMealIds.length > 0 && (() => {
              return (
                <div style={{ background:C.white, borderRadius:14, padding:"12px 14px", boxShadow:C.sh, marginBottom:16, display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:C.charcoal }}>Retailer</div>
                    <div style={{ fontSize:12, color:C.grey }}>
                      {retailerTier ? `${retailerTier} tier · ` : ""}Estimated cached prices
                    </div>
                  </div>
                  <select
                    value={retailerId}
                    onChange={e => setRetailerId(e.target.value)}
                    style={{ padding:"8px 10px", borderRadius:8, border:`1px solid ${C.border}`, background:C.white, fontWeight:600, fontSize:13, color:C.charcoal, cursor:"pointer" }}
                  >
                    {retailerOptions.map(r => (
                      <option key={r.id} value={r.id}>{r.label || r.name || r.id}</option>
                    ))}
                  </select>
                  <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:600, color:C.charcoal, cursor:"pointer" }}>
                    <input type="checkbox" checked={organicMode} onChange={e => setOrganicMode(e.target.checked)} />
                    Organic
                  </label>
                </div>
              );
            })()}

            {/* Empty state ONLY when the plan genuinely has no meals — not merely
                when the priced list hasn't finished loading. */}
            {planMealIds.length === 0 ? (
              <div style={{ textAlign:"center", padding:"60px 20px", color:C.grey }}>
                <div style={{ fontSize:48, marginBottom:12 }}>🛒</div>
                <div style={{ fontFamily:"Lora,serif", fontSize:18, fontWeight:600, marginBottom:8 }}>Your basket is empty</div>
                <div style={{ fontSize:14 }}>Select meals to automatically build your shopping list.</div>
                <button onClick={() => setActiveTab("meals")} style={{ marginTop:16, padding:"12px 24px", borderRadius:99, border:"none", background:C.green, color:C.white, fontWeight:700, cursor:"pointer" }}>Browse meals</button>
              </div>
            ) : Object.keys(shoppingList).length === 0 && buildManualItems.length === 0 ? (
              <div style={{ textAlign:"center", padding:"48px 20px", color:C.grey }}>
                <div style={{ fontSize:40, marginBottom:12 }}>🧺</div>
                <div style={{ fontFamily:"Lora,serif", fontSize:16, fontWeight:600, marginBottom:6 }}>Building your shopping list…</div>
                <div style={{ fontSize:13 }}>Pricing {selectedMealObjs.length || planMealIds.length} meal{(selectedMealObjs.length || planMealIds.length) === 1 ? "" : "s"} at {retailerLabel(retailerId, retailerOptions)}.</div>
              </div>
            ) : (
              <>
                {/* ── "This week's shop" value summary ─────────────────────────
                    Built entirely from existing reactive state — no new
                    calculations. Re-renders on meal add/remove, retailer change,
                    organic toggle, owned/pantry mark (displayCost via basketTotals),
                    and servings change (rebuilds shoppingList). Consolidates the
                    old standalone efficiency + shared-ingredient callouts so the
                    List tab leads with NomLoop's core value in one compact card. */}
                {(() => {
                  const itemCount   = Object.keys(shoppingList).length + buildManualItems.length;
                  const sharedNames = Object.values(shoppingList)
                    .filter(i => (i.sources || []).length >= 2)
                    .map(i => i.name);
                  const sharedText = sharedNames.length
                    ? sharedNames.slice(0, 4).join(", ") + (sharedNames.length > 4 ? ` +${sharedNames.length - 4} more` : "")
                    : "none yet — add more meals to share ingredients";
                  return (
                    <div style={{ background:C.white, borderRadius:16, padding:"16px 18px", boxShadow:C.sh, marginBottom:16 }}>
                      <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:17, color:C.green, marginBottom:8 }}>This week's shop</div>
                      <div style={{ fontSize:14, fontWeight:700, color:C.charcoal, marginBottom:10, lineHeight:1.4 }}>
                        {selectedMealObjs.length} meal{selectedMealObjs.length === 1 ? "" : "s"} · {itemCount} item{itemCount === 1 ? "" : "s"} · £{displayCost} at {retailerLabel(retailerId, retailerOptions)}
                        {basketTotals.ownedCount > 0 && <span style={{ color:C.green, fontWeight:600 }}> · {basketTotals.ownedCount} owned</span>}
                        {isOverBudget && <span style={{ color:"#C0392B", fontWeight:600 }}> · £{(displayCostValue-budget).toFixed(2)} over budget</span>}
                      </div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                        <span style={{ flex:"1 1 220px", fontSize:12.5, color:C.teal, background:C.tealPale, borderRadius:10, padding:"8px 10px", fontWeight:600 }}>
                          ♻️ Shared ingredients: {sharedText}
                        </span>
                        <span
                          title="Higher = less food waste. We pack-round so leftover ingredients are reused across meals."
                          style={{ flex:"0 0 auto", fontSize:12.5, fontWeight:700, borderRadius:10, padding:"8px 10px", cursor:"help", background:displayEfficiency>=85?C.greenPale:C.goldPale, color:displayEfficiency>=85?C.green:C.amber }}
                        >
                          {displayEfficiency>=85?"🌿":"💡"} Ingredient use: {displayEfficiency}%
                        </span>
                      </div>
                    </div>
                  );
                })()}

                {/* Swap suggestions (surfaces when efficiency < 70%) */}
                {swapSuggestions.length > 0 && (
                  <div style={{ background:C.goldPale, borderRadius:14, padding:"12px 14px", marginBottom:16, color:C.amber }}>
                    <div style={{ fontWeight:700, fontSize:13, marginBottom:6 }}>💡 Swap ideas to cut waste</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                      {swapSuggestions.map((s, i) => (
                        <div key={i} style={{ fontSize:13, lineHeight:1.4 }}>
                          {/* Was rendering raw mealId (e.g. "ita_venetian_duck_ragu")
                              when the server omitted the message field. The
                              swapSuggestionText helper resolves to a real meal
                              name via the mealsById cache. */}
                          {swapSuggestionText(s, mealsById)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Ingredient waste-swap suggestion cards */}
                {(() => {
                  // Fingerprints already accepted — used to hide suggestions instantly on accept
                  // and to avoid re-suggesting applied swaps before the next build completes.
                  const appliedFps = new Set(appliedSwaps.map(s => `${s.mealId}:${s.fromId}:${s.toId}`));
                  const visibleSuggestions = ingredientSwapSuggestions.filter(s =>
                    !dismissedSwapFps.includes(s.fingerprint) && !appliedFps.has(s.fingerprint),
                  );
                  // Show all accepted swaps that have display names (deduped by fingerprint).
                  // Intentionally does NOT require the swap to still be in ingredientSwapSuggestions
                  // so the confirmation row survives the rebuild that follows an accept.
                  const seenFps = new Set();
                  const appliedForDisplay = appliedSwaps.filter(s => {
                    const fp = `${s.mealId}:${s.fromId}:${s.toId}`;
                    if (seenFps.has(fp) || !s.fromName) return false;
                    seenFps.add(fp);
                    return true;
                  });
                  if (visibleSuggestions.length === 0 && appliedForDisplay.length === 0) return null;
                  return (
                    <div style={{ background:C.goldPale, borderRadius:14, padding:"12px 14px", marginBottom:16 }}>
                      <div style={{ fontWeight:700, fontSize:13, color:C.amber, marginBottom:8 }}>💡 Smart basket improvements</div>
                      {/* Applied swap confirmations with Undo */}
                      {appliedForDisplay.map(s => (
                        <div key={`${s.mealId}:${s.fromId}`} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:visibleSuggestions.length > 0 ? 8 : 0, fontSize:13 }}>
                          <span style={{ color:C.green, fontWeight:700 }}>✓</span>
                          <span style={{ flex:1, color:C.charcoal }}>Using {s.toName} instead of {s.fromName} in {s.mealName}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setAppliedSwaps(prev => prev.filter(x => !(x.mealId === s.mealId && x.fromId === s.fromId)));
                              api.swapEvent({ action:"undo", mealId:s.mealId, fromId:s.fromId, toId:s.toId }).catch(()=>{});
                            }}
                            style={{ fontSize:12, color:C.grey, background:"transparent", border:`1px solid ${C.border}`, borderRadius:8, padding:"2px 8px", cursor:"pointer", fontFamily:"inherit" }}
                          >Undo</button>
                        </div>
                      ))}
                      {/* Pending suggestion cards */}
                      {visibleSuggestions.map(s => (
                        <div key={s.fingerprint} style={{ background:C.white, borderRadius:10, padding:"10px 12px", marginBottom:6 }}>
                          <div style={{ fontSize:13, color:C.charcoal, marginBottom:8, lineHeight:1.45 }}>
                            <strong>{s.mealName}</strong> works with <strong>{s.toName}</strong> — you're already buying it for <strong>{s.toUsedInMealName}</strong>.
                            {s.costDelta > 0 && <span style={{ color:C.green }}> Saves £{s.costDelta.toFixed(2)}.</span>}
                            {s.costDelta <= 0 && s.itemsRemoved > 0 && <span style={{ color:C.green }}> One less item.</span>}
                          </div>
                          <div style={{ display:"flex", gap:8 }}>
                            <button
                              type="button"
                              onClick={() => {
                                const fp = `${s.mealId}:${s.fromId}:${s.toId}`;
                                setAppliedSwaps(prev => {
                                  if (prev.some(x => `${x.mealId}:${x.fromId}:${x.toId}` === fp)) return prev;
                                  return [...prev, { mealId:s.mealId, fromId:s.fromId, toId:s.toId, fromName:s.fromName, toName:s.toName, mealName:s.mealName }];
                                });
                                api.swapEvent({ action:"accept", mealId:s.mealId, fromId:s.fromId, toId:s.toId }).catch(()=>{});
                              }}
                              style={{ fontSize:12.5, fontWeight:700, background:C.green, color:"#fff", border:"none", borderRadius:8, padding:"5px 14px", cursor:"pointer", fontFamily:"inherit" }}
                            >Swap it</button>
                            <button
                              type="button"
                              onClick={() => {
                                setDismissedSwapFps(prev => [...prev, s.fingerprint]);
                                api.swapEvent({ action:"dismiss", mealId:s.mealId, fromId:s.fromId, toId:s.toId }).catch(()=>{});
                              }}
                              style={{ fontSize:12.5, color:C.grey, background:"transparent", border:`1px solid ${C.border}`, borderRadius:8, padding:"5px 12px", cursor:"pointer", fontFamily:"inherit" }}
                            >No thanks</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* Price-shock banner — flags any single item over £5 */}
                {(() => {
                  const pricey = Object.values(shoppingList).filter(i => i.cost > 5 && !pantryItems.has(`pkg:${i.id}`));
                  if (!pricey.length) return null;
                  return (
                    <div style={{ background:C.goldPale, border:`1.5px solid ${C.amber}`, borderRadius:14, padding:"12px 16px", marginBottom:16, display:"flex", gap:10, alignItems:"flex-start" }}>
                      <span style={{ fontSize:20, flexShrink:0 }}>⚠️</span>
                      <div>
                        <div style={{ fontWeight:700, fontSize:13, color:C.gold, marginBottom:2 }}>
                          {pricey.length} pricey item{pricey.length>1?"s":""} (over £5 each)
                        </div>
                        <div style={{ fontSize:12, color:C.gold }}>
                          {pricey.map(i => `${i.name} £${i.cost.toFixed(2)}`).join(" · ")}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* "Used in" toggle — lets user hide recipe provenance lines */}
                <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:8 }}>
                  <button
                    onClick={() => setShowUsedIn(v => !v)}
                    style={{
                      fontSize:11, fontWeight:600, padding:"3px 10px",
                      border:`1px solid ${C.border}`, borderRadius:99,
                      background: showUsedIn ? C.tealPale : C.white,
                      color: showUsedIn ? C.teal : C.grey,
                      cursor:"pointer",
                    }}
                  >
                    {showUsedIn ? "Hide recipe labels" : "Show recipe labels"}
                  </button>
                </div>

                {/* Items by category */}
                {["Proteins","Produce","Dairy","Pantry","Herbs"].map(cat => {
                  const items = Object.values(shoppingList).filter(i => i.cat === cat);
                  if (!items.length) return null;
                  return (
                    <div key={cat} style={{ marginBottom:20 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                        <span style={{ fontSize:18 }}>{CATEGORY_ICONS[cat]}</span>
                        <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:16, color:C.charcoal }}>{cat}</div>
                        <div style={{ fontSize:13, color:C.grey, marginLeft:"auto" }}>£{items.filter(i=>!pantryItems.has(`pkg:${i.id}`)).reduce((s,i)=>s+i.cost,0).toFixed(2)}</div>
                      </div>
                      <div style={{ background:C.white, borderRadius:16, overflow:"hidden", boxShadow:C.sh }}>
                        {items.map((item, idx) => {
                          // If this merged line came (partly) from an imported recipe,
                          // re-attach its swap chips + "for: <recipe ingredient>" note.
                          const imp = importedById.get(item.id);
                          const practical = imp ? practicalShoppingSubtitle(imp) : null;
                          // "Used in" — meal/recipe provenance from the server-built
                          // sources[] (catalog + imported). Rendered on its own row
                          // line via the usedIn prop so the needed/buy detail below
                          // stays visible instead of being overwritten.
                          const usedIn = (item.sources && item.sources.length)
                            ? item.sources.join(", ")
                            : undefined;
                          const scaledSubtitle = importedScalingSubtitle(item, null);
                          const subtitle = scaledSubtitle
                            || (imp ? (practical || `for: ${imp.origNames.join(", ")}`) : undefined);
                          return (
                            <ListItemRow
                              key={item.id}
                              item={item}
                              subtitle={subtitle}
                              usedIn={showUsedIn ? usedIn : undefined}
                              alternatives={imp?.alternatives}
                              onSwap={imp ? (pkgId) => handleSwap(item.id, imp.origNames[0], pkgId) : undefined}
                              pantried={pantryItems.has(`pkg:${item.id}`)}
                              onOwn={() => handleOwn(`pkg:${item.id}`)}
                              onRoundDownChoice={(scenario) => handleRoundDownChoice(item.id, scenario)}
                              borderBottom={idx < items.length - 1}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/* Imported ingredients with no catalogue match — add manually.
                    Strong matches are merged into the category sections above
                    (unified list), so only the unmatched fallbacks live here. */}
                {buildManualItems.length > 0 && (
                  <div style={{ marginBottom:20 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                      <span style={{ fontSize:18 }}>📋</span>
                      <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:16, color:C.charcoal }}>From your imported recipes</div>
                    </div>

                    {/* Manual items — no PKG match; add to trolley yourself */}
                    {buildManualItems.length > 0 && (
                      <div style={{ background:C.sand, borderRadius:12, padding:"10px 14px" }}>
                        <div style={{ fontWeight:700, fontSize:13, color:C.grey, marginBottom:4 }}>🛒 Add manually</div>
                        {buildManualItems.map((item, i) => {
                          const manualKey = `manual:${(item.origName ?? "").toLowerCase().replace(/[^a-z0-9_ -]/g, " ").trim().slice(0, 80)}`;
                          const owned = pantryItems.has(manualKey);
                          return (
                            <div key={i} style={{
                              fontSize:13, paddingTop:4,
                              borderTop: i > 0 ? `1px solid ${C.border}` : "none",
                              display:"flex", justifyContent:"space-between", alignItems:"center",
                              opacity: owned ? 0.45 : 1,
                            }}>
                              <span style={{ color:C.charcoal, textDecoration: owned ? "line-through" : "none" }}>
                                {item.displayText}
                              </span>
                              <button
                                onClick={() => handleOwn(manualKey)}
                                style={{
                                  fontSize:10, fontWeight:700, padding:"2px 7px",
                                  border:`1px solid ${owned ? C.green : C.border}`,
                                  borderRadius:6, marginLeft:8, flexShrink:0,
                                  background: owned ? C.greenPale : C.white,
                                  color: owned ? C.green : C.grey, cursor:"pointer",
                                }}
                              >
                                {owned ? "Owned ✓" : "Own"}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Total — shows payable cost large + crossed-out full cost when items are owned */}
                <div style={{ background:C.white, borderRadius:16, padding:"16px 18px", boxShadow:C.sh, display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
                  <div>
                    <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:18, color:C.charcoal }}>Estimated total</div>
                    <div style={{ fontSize:13, color:C.grey }}>
                      Budget: £{budget} · {isOverBudget ? `£${(displayCostValue-budget).toFixed(2)} over` : `£${(budget-displayCostValue).toFixed(2)} remaining`}
                      {basketTotals.ownedCount > 0 && <span style={{ color:C.green }}> · {basketTotals.ownedCount} owned</span>}
                    </div>
                    <div style={{ fontSize:12, color:C.grey, marginTop:4, lineHeight:1.35 }}>
                      Based on cached retailer prices. Live prices, discounts, and current offers may differ.
                    </div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontFamily:"Lora,serif", fontWeight:800, fontSize:28, color:isOverBudget?"#C0392B":C.green }}>
                      £{displayCost}
                    </div>
                    {basketTotals.ownedCount > 0 && (
                      <div style={{ fontSize:13, color:C.grey, textDecoration:"line-through" }}>£{basketTotals.fullCost}</div>
                    )}
                  </div>
                </div>

                {/* Checkout confirmation — flips the app into cooking mode */}
                {!checkedOut ? (
                  showPackDeficit ? (
                    /* Pack-deficit warning gate — shown when rounded-down items exist.
                       User must confirm they're OK with slight under-buying, or go back. */
                    <div style={{ background:"#FEF3C7", borderRadius:16, padding:"18px", border:"1.5px solid #F59E0B", marginBottom:20 }}>
                      <div style={{ display:"flex", gap:10, alignItems:"flex-start", marginBottom:12 }}>
                        <span style={{ fontSize:22, flexShrink:0 }}>⚠️</span>
                        <div>
                          <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:15, color:"#92400E", marginBottom:3 }}>
                            Some items are slightly under what you need
                          </div>
                          <div style={{ fontSize:12, color:"#B45309", lineHeight:1.5 }}>
                            NomLoop rounded these down within cooking tolerance. You could add an extra pack to be safe.
                          </div>
                        </div>
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:14 }}>
                        {roundedDownItems.map(item => (
                          <div key={item.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:13, padding:"6px 10px", background:"rgba(255,255,255,.6)", borderRadius:8 }}>
                            <span style={{ color:"#92400E", fontWeight:600 }}>{item.name}</span>
                            <span style={{ color:"#B45309", fontSize:12 }}>
                              Suggested: {item.packs}× {item.size}{item.unit === "count" ? "" : item.unit} pack · need ~{item.needed}{item.unit === "count" ? "" : item.unit} · {item.shortfall}{item.unit === "count" ? "" : item.unit} short
                              <span style={{ color:C.grey }}> (or add {item.suggestedPacks}× packs)</span>
                            </span>
                          </div>
                        ))}
                      </div>
                      <div style={{ display:"flex", gap:8 }}>
                        <button
                          onClick={() => {
                            setShowPackDeficit(false);
                            setCheckedOut(true);
                            setActiveTab("cook");
                            setCheckoutCount(c => {
                              const next = c + 1;
                              prefsStore.write("nomloop.checkoutCount", next);
                              if (c < VIDEO_UNLOCK_THRESHOLD && next >= VIDEO_UNLOCK_THRESHOLD) {
                                show("🎬 Cooking reels unlocked — check the Cook tab");
                              }
                              return next;
                            });
                            show("Cooking mode unlocked ✓");
                          }}
                          style={{ flex:1, padding:"10px 0", borderRadius:99, border:"none", background:"#92400E", color:C.white, fontWeight:700, fontSize:13, cursor:"pointer" }}
                        >
                          Continue anyway
                        </button>
                        <button
                          onClick={() => setShowPackDeficit(false)}
                          style={{ flex:1, padding:"10px 0", borderRadius:99, border:"1.5px solid #F59E0B", background:"transparent", color:"#92400E", fontWeight:700, fontSize:13, cursor:"pointer" }}
                        >
                          Review list
                        </button>
                      </div>
                    </div>
                  ) : (
                  <div style={{ background:C.white, borderRadius:16, padding:"18px", boxShadow:C.sh, marginBottom:20, display:"flex", gap:14, alignItems:"center" }}>
                    <div style={{ fontSize:28 }}>🛍️</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:15, color:C.charcoal, marginBottom:2 }}>Shopped at {retailerLabel(retailerId, retailerOptions)}?</div>
                      <div style={{ fontSize:12, color:C.grey, lineHeight:1.4 }}>
                        🍳 The <strong>Cook</strong> tab unlocks once you mark groceries as bought.
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const planned = Object.keys(weekPlan).length;
                        if (planned === 0) {
                          show("Assign meals to days in the Week tab first", "warn");
                          setActiveTab("week");
                          return;
                        }
                        if (roundedDownItems.length > 0) {
                          setShowPackDeficit(true);
                          return;
                        }
                        setCheckedOut(true);
                        setActiveTab("cook");
                        // Bump the lifetime checkout counter — 10 unlocks the
                        // cooking-reel video on the Cook tab.
                        setCheckoutCount(c => {
                          const next = c + 1;
                          prefsStore.write("nomloop.checkoutCount", next);
                          if (c < VIDEO_UNLOCK_THRESHOLD && next >= VIDEO_UNLOCK_THRESHOLD) {
                            show("🎬 Cooking reels unlocked — check the Cook tab");
                          }
                          return next;
                        });
                        show("Cooking mode unlocked ✓");
                      }}
                      style={{
                        padding:"10px 16px", borderRadius:99, border:"none",
                        background:C.green, color:C.white, fontWeight:700, fontSize:13,
                        cursor:"pointer", whiteSpace:"nowrap",
                      }}
                    >
                      ✓ I've bought these
                    </button>
                  </div>
                  )
                ) : (
                  <div style={{ background:C.greenPale, borderRadius:16, padding:"14px 16px", marginBottom:20, display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ fontSize:22 }}>🍳</div>
                    <div style={{ flex:1, color:C.green, fontWeight:700, fontSize:13 }}>
                      Cooking mode is on — head to the Cook tab for tonight's guide.
                    </div>
                    <button
                      onClick={() => setActiveTab("cook")}
                      style={{ padding:"8px 14px", borderRadius:99, border:`1.5px solid ${C.green}`, background:C.white, color:C.green, fontWeight:700, fontSize:12, cursor:"pointer" }}
                    >
                      Open Cook →
                    </button>
                  </div>
                )}

                {/* Perishable swap suggestions — buy a staple instead of a one-off fresh item */}
                {perishableSwaps.length > 0 && (
                  <div style={{ background:C.white, borderRadius:16, padding:"18px 18px 20px", boxShadow:C.sh }}>
                    <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:16, color:C.green, marginBottom:4 }}>♻️ Ingredient Swaps</div>
                    <div style={{ fontSize:13, color:C.grey, marginBottom:12 }}>Replace one-off items with staples already in your basket to cut waste.</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                      {perishableSwaps.map((sw, i) => (
                        <div key={i} style={{ display:"flex", alignItems:"center", gap:8, fontSize:13 }}>
                          <span style={{ color:C.grey, flex:"0 0 auto" }}>·</span>
                          <span style={{ color:C.charcoal }}>{sw.from}</span>
                          <span style={{ color:C.grey }}>→</span>
                          <span style={{ color:C.green, fontWeight:600 }}>{sw.to}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Waste tips — only shown when current plan has leftover ingredients */}
                {wasteTips.length > 0 ? (
                  <div style={{ background:C.white, borderRadius:16, padding:"18px 18px 20px", boxShadow:C.sh }}>
                    <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:16, color:C.green, marginBottom:12 }}>🌿 Waste Reduction Tips</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      {wasteTips.map((tip,i) => (
                        <div key={i} style={{ display:"flex", gap:10, fontSize:14, color:C.grey }}>
                          <span style={{ color:C.green, fontWeight:700, flexShrink:0 }}>·</span> {tip}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ background:C.white, borderRadius:16, padding:"18px 18px 20px", boxShadow:C.sh }}>
                    <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:16, color:C.green, marginBottom:8 }}>🌿 Waste Reduction</div>
                    <div style={{ fontSize:14, color:C.grey }}>Your plan is waste-efficient — no significant leftover ingredients this week.</div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ══ COOK TAB ══ (post-checkout only) */}
        {activeTab === "cook" && checkedOut && (() => {
          // Today's meal = weekPlan[DAYS[today]]. JS getDay() is Sun=0..Sat=6;
          // our DAYS array is Mon..Sun, so remap.
          const jsDay = new Date().getDay();
          const todayIdx = jsDay === 0 ? 6 : jsDay - 1;
          const todayName = DAYS[todayIdx];
          const todayMealId = weekPlan[todayName] || null;
          const todayMeal   = todayMealId ? mealsById.get(todayMealId) : null;

          return (
            <div style={{ padding:"20px 0" }}>
              <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:24, color:C.green, marginBottom:6 }}>Cook This Week</div>
              <div style={{ fontSize:14, color:C.grey, marginBottom:20 }}>
                Your basket is in — let's get dinner on the table.
              </div>

              {/* TONIGHT'S MEAL — big hero card */}
              {todayMeal ? (
                <div style={{
                  background:`linear-gradient(135deg, ${C.green}, ${C.greenLight})`,
                  borderRadius:20, padding:"22px 20px", marginBottom:20,
                  boxShadow:C.shLg, color:C.white,
                }}>
                  <div style={{ fontSize:12, fontWeight:700, letterSpacing:1.2, opacity:.8, marginBottom:6 }}>
                    TONIGHT · {todayName.toUpperCase()}
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:14 }}>
                    <span style={{ fontSize:52 }}>{todayMeal.emoji || "📋"}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:22, lineHeight:1.2, marginBottom:4 }}>
                        {todayMeal.name}
                      </div>
                      <div style={{ fontSize:13, opacity:.82 }}>
                        {todayMeal.isImported
                          ? (recipeSourceDisplayName(todayMeal) ? `via ${recipeSourceDisplayName(todayMeal)}` : "Imported recipe")
                          : `${formatMealTime(todayMeal.time)} · ${effectiveServings[todayMealId] || servingMap[todayMealId] || todayMeal.serves} serving${(effectiveServings[todayMealId] || servingMap[todayMealId] || todayMeal.serves) !== 1 ? "s" : ""}`}
                      </div>
                    </div>
                  </div>
                  {/* Imported recipes get the SAME cook guide (built from the
                      user's own saved steps). Source link, when present, is a
                      secondary option below. */}
                  <button
                    onClick={() => setCookGuideMealId(todayMealId)}
                    style={{
                      width:"100%", padding:"14px 18px", borderRadius:12,
                      border:"none", background:C.white, color:C.green,
                      fontWeight:800, fontSize:15, cursor:"pointer",
                      display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                      boxShadow:"0 2px 8px rgba(0,0,0,.1)",
                    }}
                  >
                    🍳 Start cook guide
                  </button>
                  {todayMeal.isImported && todayMeal.sourceUrl && (
                    <a
                      href={todayMeal.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                        marginTop:10, fontSize:12, fontWeight:700,
                        color:C.white, opacity:.85, textDecoration:"underline",
                      }}
                    >
                      🔗 View original via {recipeSourceDisplayName(todayMeal)} →
                    </a>
                  )}

                  {/* Cooking reel — hidden until the user has confirmed 10 shops.
                      Video file is served from /videos/<mealId>.mp4; if it's
                      not present the component quietly removes itself. */}
                  {videoUnlocked && (
                    <VideoReelPlayer mealId={todayMealId} accentColor={C.white} />
                  )}
                </div>
              ) : (
                <div style={{ background:C.goldPale, borderRadius:16, padding:"16px 18px", marginBottom:20, color:C.amber }}>
                  <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>No meal planned for {todayName}</div>
                  <div style={{ fontSize:13, lineHeight:1.4, marginBottom:10 }}>
                    Assign a meal to {todayName} in the Week tab to unlock tonight's cook guide.
                  </div>
                  <button
                    onClick={() => setActiveTab("week")}
                    style={{ padding:"8px 14px", borderRadius:99, border:`1.5px solid ${C.amber}`, background:C.white, color:C.amber, fontWeight:700, fontSize:12, cursor:"pointer" }}
                  >
                    Open Week planner →
                  </button>
                </div>
              )}

              {/* REST OF THE WEEK — smaller cards, cook buttons per meal */}
              <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:15, color:C.charcoal, marginBottom:10 }}>
                Rest of the week
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {DAYS.filter(d => d !== todayName).map(day => {
                  const mealId = weekPlan[day];
                  const meal = mealId ? mealsById.get(mealId) : null;
                  return (
                    <div key={day} style={{
                      background:C.white, borderRadius:14, padding:"12px 14px",
                      boxShadow:C.sh, display:"flex", alignItems:"center", gap:12,
                    }}>
                      <div style={{ width:36, fontWeight:800, fontSize:12, color:C.grey, flexShrink:0 }}>{day}</div>
                      {meal ? (
                        <>
                          <span style={{ fontSize:22 }}>{meal.emoji || "📋"}</span>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontWeight:700, fontSize:13, color:C.charcoal, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{meal.name}</div>
                            <div style={{ fontSize:11, color:C.grey }}>
                              {meal.isImported ? `via ${recipeSourceDisplayName(meal)}` : formatMealTime(meal.time)}
                            </div>
                          </div>
                          {/* Catalog AND imported meals get a Cook button — the
                              guide builds from saved steps for imported recipes. */}
                          <button
                            onClick={() => setCookGuideMealId(mealId)}
                            style={{
                              padding:"6px 12px", borderRadius:99,
                              border:`1.5px solid ${C.green}`, background:C.white,
                              color:C.green, fontWeight:700, fontSize:12, cursor:"pointer",
                              flexShrink:0,
                            }}
                          >
                            🍳 Cook
                          </button>
                        </>
                      ) : (
                        <div style={{ flex:1, color:C.greyLight, fontSize:12, fontStyle:"italic" }}>No meal planned</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Exit cooking mode (e.g. if they want to start a new week) */}
              <button
                onClick={() => {
                  setCheckedOut(false);
                  setActiveTab("meals");
                  show("Cooking mode off — plan your next week");
                }}
                style={{
                  marginTop:24, width:"100%", padding:"12px",
                  borderRadius:12, border:`1.5px solid ${C.border}`,
                  background:C.white, color:C.grey, fontWeight:600, fontSize:13,
                  cursor:"pointer",
                }}
              >
                Start a new week →
              </button>
            </div>
          );
        })()}
      </div>

      {/* Import sheet (Phase 2) */}
      {/* ── Recipe detail sheet ─────────────────────────────────────────── */}
      {recipeDetailId && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Recipe details"
          onClick={closeRecipeDetail}
          style={{ position:"fixed", inset:0, zIndex:550, display:"flex", alignItems:"flex-end", justifyContent:"center" }}
        >
          <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,.45)", backdropFilter:"blur(4px)" }} />
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position:"relative", background:C.white, borderRadius:"24px 24px 0 0",
              width:"100%", maxWidth:520, maxHeight:"min(760px, calc(100dvh - 32px))", display:"flex", flexDirection:"column",
              overflow:"hidden",
              boxShadow:"0 -16px 60px rgba(0,0,0,.2)", animation:"slideUp .3s ease",
            }}
          >
            <div style={{ width:36, height:4, background:C.border, borderRadius:99, margin:"16px auto 0", flexShrink:0 }} />
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 20px 0", flexShrink:0 }}>
              <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:18, color:C.charcoal, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"80%" }}>
                {recipeDetail?.title ?? recipeLibrary.find(r => r.id === recipeDetailId)?.title ?? "Recipe"}
              </div>
              <button onClick={closeRecipeDetail} aria-label="Close" style={{ background:"none", border:"none", cursor:"pointer", fontSize:18, color:C.grey, padding:4, lineHeight:1 }}>✕</button>
            </div>
            <div style={{ overflowY:"auto", flex:1, minHeight:0, overscrollBehavior:"contain", WebkitOverflowScrolling:"touch", padding:"12px 20px 0" }}>
              {recipeDetailLoading && <div style={{ textAlign:"center", color:C.grey, padding:"28px 0", fontSize:14 }}>Loading…</div>}
              {!recipeDetailLoading && recipeDetail && (() => {
                const sourceDisplayName = recipeSourceDisplayName(recipeDetail);
                return (
                <>
                  {/* Chef / publisher attribution: "By {chef}, From {publisher} via {sourceDisplayName}" */}
                  {(recipeDetail.chef || recipeDetail.publisher || sourceDisplayName) && (
                    <div style={{ fontSize:13, color:C.charcoal, marginBottom:10, lineHeight:1.5 }}>
                      {recipeDetail.chef && <span>By <b>{recipeDetail.chef}</b></span>}
                      {recipeDetail.chef && (recipeDetail.publisher || sourceDisplayName) && <span>, </span>}
                      {recipeDetail.publisher && <span>From <b>{recipeDetail.publisher}</b></span>}
                      {recipeDetail.publisher && sourceDisplayName && <span> </span>}
                      {sourceDisplayName && (
                        <span style={{ color:C.grey }}>via {sourceDisplayName}</span>
                      )}
                    </div>
                  )}

                  {recipeDetail.cuisine && (
                    <div style={{ fontSize:12, color:C.grey, fontWeight:600, marginBottom:12 }}>
                      {CUISINES.find(c => c.id === recipeDetail.cuisine)?.label || cuisineLabel(recipeDetail.cuisine)}
                    </div>
                  )}

                  {/* Prep / Cook / Serve grid */}
                  {(recipeDetail.prep_minutes || recipeDetail.cook_minutes || recipeDetail.servings) && (
                    <div style={{
                      display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:8,
                      padding:"10px", background:C.cream, borderRadius:10, marginBottom:14,
                    }}>
                      <div>
                        <div style={{ fontSize:11, fontWeight:800, color:C.grey, textTransform:"uppercase", letterSpacing:".05em" }}>Prepare</div>
                        <div style={{ fontSize:13, color:C.charcoal, fontWeight:600, marginTop:2 }}>
                          {recipeDetail.prep_minutes ? formatDuration(recipeDetail.prep_minutes) : "—"}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize:11, fontWeight:800, color:C.grey, textTransform:"uppercase", letterSpacing:".05em" }}>Cook</div>
                        <div style={{ fontSize:13, color:C.charcoal, fontWeight:600, marginTop:2 }}>
                          {recipeDetail.cook_minutes ? formatDuration(recipeDetail.cook_minutes) : "—"}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize:11, fontWeight:800, color:C.grey, textTransform:"uppercase", letterSpacing:".05em" }}>Serve</div>
                        <div style={{ fontSize:13, color:C.charcoal, fontWeight:600, marginTop:2 }}>
                          {(() => {
                            const base = Math.max(1, Number(recipeDetail.servings) || Number(recipeDetail.serves) || 2);
                            const currentServings = plannedServingCount({ id: recipeDetailId, servingMap, effectiveServings, people, fallback: base });
                            return currentServings ? `Serves ${currentServings}` : "—";
                          })()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Servings scaler — same control as catalog meals. Writes
                      servingMap[recipeDetailId], which flows into effectiveServings
                      and the unified /build, so shopping quantities scale too. */}
                  {(() => {
                    const base = Math.max(1, Number(recipeDetail.servings) || Number(recipeDetail.serves) || 2);
                    const currentServings = plannedServingCount({ id: recipeDetailId, servingMap, effectiveServings, people, fallback: base });
                    return (
                      <div style={{ background:C.greenPale, borderRadius:16, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                        <span style={{ fontWeight:700, fontSize:14 }}>Servings</span>
                        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                          {["-","+"].map((lbl,i) => (
                            <button key={lbl} onClick={() => setServingMap(p=>({...p, [recipeDetailId]: Math.max(1, Math.min(12, currentServings+(i===0?-1:1)))}))}
                              style={{ width:32, height:32, borderRadius:"50%", border:`2px solid ${C.green}`, background:C.white, color:C.green, fontWeight:700, fontSize:18, cursor:"pointer" }}>
                              {lbl}
                            </button>
                          ))}
                          <span style={{ fontWeight:800, fontSize:20, color:C.green, minWidth:24, textAlign:"center" }}>
                            {currentServings}
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Dietary chips */}
                  {Array.isArray(recipeDetail.dietary) && recipeDetail.dietary.length > 0 && (
                    <div style={{ marginBottom:14 }}>
                      <div style={{ fontSize:11, fontWeight:800, color:C.grey, textTransform:"uppercase", letterSpacing:".05em", marginBottom:6 }}>Dietary</div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                        {recipeDetail.dietary.map((tag, i) => (
                          <span key={i} style={{
                            fontSize:12, fontWeight:600, color:C.green,
                            padding:"4px 10px", borderRadius:99,
                            background:C.greenPale,
                          }}>{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Source link — links back to original recipe page */}
                  {recipeDetail.source_url && (
                    <a
                      href={recipeDetail.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display:"flex", alignItems:"center", gap:8,
                        padding:"10px 14px", borderRadius:10, marginBottom:14,
                        background:C.tealPale, color:C.teal,
                        fontSize:13, fontWeight:700, textDecoration:"none",
                        border:`1px solid ${C.teal}33`,
                      }}
                    >
                      <span style={{ fontSize:15 }}>🔗</span>
                      <span>View full cooking instructions via {sourceDisplayName} →</span>
                    </a>
                  )}

                  {(() => {
                    const base = Math.max(1, Number(recipeDetail.servings) || Number(recipeDetail.serves) || 2);
                    const currentServings = plannedServingCount({ id: recipeDetailId, servingMap, effectiveServings, people, fallback: base });
                    const factor = currentServings / base;
                    const scaled = Math.abs(factor - 1) > 0.001;
                    return (
                      <>
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, flexWrap:"wrap", marginBottom:8 }}>
                          <div style={{ fontWeight:700, fontSize:12, color:C.grey, textTransform:"uppercase", letterSpacing:.5 }}>Ingredients</div>
                          {/* Measurement display picker — converts qty for display only. */}
                          <MeasureModePicker mode={measureMode} onChange={setMeasureMode} />
                        </div>
                        {scaled && (
                          <div style={{ fontSize:11, color:C.teal, fontWeight:700, marginBottom:8 }}>
                            Scaled from {base} → {currentServings} serving{currentServings === 1 ? "" : "s"}
                          </div>
                        )}
                        <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:20 }}>
                          {(recipeDetail.ingredients || []).map((ing, i, arr) => {
                            const sec = (ing && ing.section) || null;
                            const showSec = sec && sec !== ((arr[i - 1] && arr[i - 1].section) || null);
                            return (
                              <Fragment key={i}>
                                {showSec && (
                                  <div style={{ fontSize:11, fontWeight:800, color:C.grey, textTransform:"uppercase", letterSpacing:".05em", marginTop: i ? 8 : 0, marginBottom:2 }}>{sec}</div>
                                )}
                                <div style={{ display:"flex", alignItems:"flex-start", gap:8, fontSize:14, color:C.charcoal, padding:"6px 0", borderBottom:`1px solid ${C.border}` }}>
                                  <span aria-hidden="true" style={{ fontSize:15, lineHeight:1.4, flexShrink:0 }}>{ingredientIcon(ing.name)}</span>
                                  <span style={{ color:C.grey, minWidth:60, flexShrink:0 }}>
                                    {displayScaledImportedQty(ing, factor, measureMode) || "—"}
                                  </span>
                                  <span style={{ flex:1 }}>
                                    {ing.name}{ing.notes ? <span style={{ color:C.grey }}>, {ing.notes}</span> : null}
                                  </span>
                                </div>
                              </Fragment>
                            );
                          })}
                        </div>
                      </>
                    );
                  })()}

                  {/* Cooking instructions — the user's own saved steps. */}
                  {Array.isArray(recipeDetail.instructions) && recipeDetail.instructions.length > 0 && (
                    <>
                      <div style={{ fontWeight:700, fontSize:12, color:C.grey, textTransform:"uppercase", letterSpacing:.5, marginBottom:8 }}>Instructions</div>
                      <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:20 }}>
                        {recipeDetail.instructions.map((step, i) => (
                          <div key={i} style={{ display:"flex", gap:10, fontSize:14, color:C.charcoal, lineHeight:1.5 }}>
                            <span style={{
                              width:22, height:22, borderRadius:"50%", background:C.green, color:C.white,
                              display:"flex", alignItems:"center", justifyContent:"center",
                              fontWeight:800, fontSize:12, flexShrink:0, marginTop:1,
                            }}>{i + 1}</span>
                            <span style={{ flex:1 }}>{step}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Add / remove from week plan */}
                  <div className="glass-control" style={{ position:"sticky", bottom:0, zIndex:2, margin:"4px -20px 0", padding:"12px 20px calc(16px + env(safe-area-inset-bottom, 0px))", borderTop:`1px solid ${C.border}` }}>
                    <button
                      onClick={() => {
                        const recipe = recipeDetail;
                        const mealEntry = {
                          id:           recipe.id,
                          name:         recipe.title,
                          emoji:        recipeEmoji(recipe.title),
                          time:         null,
                          cals:         null,
                          serves:       recipe.servings ?? 2,
                          cuisine:      recipe.cuisine ?? null,
                          isImported:   true,
                          sourceUrl:    recipe.source_url ?? null,
                          sourceDomain: recipe.source_domain ?? null,
                          sourceDisplayName: sourceDisplayName,
                        };
                        const alreadyIn = selectedMeals.includes(recipe.id);
                        setMealsCache(prev => { const next = new Map(prev); next.set(recipe.id, mealEntry); return next; });
                        if (alreadyIn) {
                          removeMealWithUndo(recipe.id);
                        } else if (selectedMeals.length < mealTarget) {
                          clearAutofillUndoState();
                          // Cache ingredients so match-imported endpoint can use them.
                          // The library list strips ingredients, so fall back to the
                          // library ingredient cache; if both are empty the prefetch
                          // effect re-fetches the full recipe by id.
                          setImportedIngredientCache(prev => {
                            const n = new Map(prev);
                            const ings = (recipe.ingredients && recipe.ingredients.length)
                              ? recipe.ingredients
                              : (recipeLibraryIngredientCache.get(recipe.id) || []);
                            n.set(recipe.id, ings);
                            return n;
                          });
                          setSelectedMeals(s => [...s, recipe.id]);
                          setCheckedOut(false);
                          show(`${recipe.title} added · Pick a day in the Week tab →`);
                        } else {
                          show("Week full — remove a meal first", "warn");
                        }
                        closeRecipeDetail();
                      }}
                      style={{
                        width:"100%", padding:14, borderRadius:99, border:"none",
                        background: selectedMeals.includes(recipeDetailId) ? "#FEE2E2" : C.green,
                        color: selectedMeals.includes(recipeDetailId) ? "#C0392B" : C.white,
                        fontWeight:800, fontSize:15, cursor:"pointer",
                      }}
                    >
                      {selectedMeals.includes(recipeDetailId) ? "✕ Remove from week" : "+ Add to this week"}
                    </button>
                  </div>
                </>
                );
              })()}
              {!recipeDetailLoading && !recipeDetail && (
                <div style={{ textAlign:"center", color:C.grey, padding:"28px 0", fontSize:14 }}>Couldn't load recipe. Please try again.</div>
              )}
            </div>
          </div>
        </div>
      )}

      <ImportSheet
        open={importSheetOpen}
        recipeLibrary={recipeLibrary}
        onOpenExisting={(id) => { setImportSheetOpen(false); openRecipeDetail(id); }}
        onClose={() => setImportSheetOpen(false)}
        onSaved={(saved) => {
          setImportSheetOpen(false);
          setRecentlySavedRecipeIds(prev => {
            const next = new Set(prev);
            next.add(saved.id);
            return next;
          });
          if (Array.isArray(saved.ingredients)) {
            setRecipeLibraryIngredientCache(prev => {
              const next = new Map(prev);
              next.set(saved.id, saved.ingredients);
              return next;
            });
          }
          setRecipeLibrary(prev => {
            const savedCard = {
              id:            saved.id,
              title:         saved.title,
              source_domain: saved.sourceDomain,
              source_url:    saved.sourceUrl ?? null,
              source_image_url: saved.source_image_url ?? null,
              sourceDisplayName: getRecipeSourceDisplayName(saved.sourceUrl ?? null, saved.sourceDomain ?? null),
              cuisine:       saved.cuisine ?? null,
              chef:          saved.chef        ?? null,
              publisher:     saved.publisher   ?? null,
              prep_minutes:  saved.prepMinutes ?? null,
              cook_minutes:  saved.cookMinutes ?? null,
              dietary:       Array.isArray(saved.dietary) ? saved.dietary : [],
              servings:      saved.servings ?? null,
            };
            const exists = prev.some(recipe => recipe.id === saved.id);
            const next = exists
              ? prev.map(recipe => recipe.id === saved.id ? { ...recipe, ...savedCard } : recipe)
              : [savedCard, ...prev];
            return dedupeRecipeLibraryById(next);
          });
          show("Saved to your recipes. We kept the ingredients; the rest stays where it belongs.");
        }}
      />
    </div>
  );
}
